import { useNavigate } from 'react-router-dom';
import React, {
	useState,
	useEffect,
	useCallback,
	useMemo,
	useRef,
} from 'react';
import { IoIosSearch } from 'react-icons/io';
import { MdArrowBackIosNew, MdCheck } from 'react-icons/md';
import { Link } from 'react-router-dom';
import ApiScaleGrid from './ApiScaleGrid';
import {
	requestBuscarGradeEscala,
	requestBuscarGradeEspecialidadeEscala,
	requestBuscarProfissionalEscala,
	requestCatalogosEscalaSelects,
	requestConsultarTrocaEscala,
	requestCriarSolicitacaoTroca,
	requestCriarItemEscala,
	requestCriarLoteItensEscala,
	requestEditarItemEscala,
	requestEditarLoteItensEscala,
	requestExcluirItemEscala,
	requestFormularioItemEscala,
	requestFormularioNovoItemEscala,
	requestRegistrarTrocaEscala,
	requestVinculosEscala,
} from '../../services/escalaRequests';
import API_BASE_URL from '../../services/config/api';

import Cookies from 'js-cookie';
import { IoSettingsOutline } from 'react-icons/io5';

import { BsClock } from 'react-icons/bs';
import moment from 'moment';
import 'moment/locale/pt-br';
import { sortSetoresPorAndar } from '../../utils/escalaSetorOrdering';
import SearchableSetorSelect from '../Escalas Utils/SearchableSetorSelect';

moment.locale('pt-br');

const ESCALA_FILTERS_CACHE_KEY = 'escala.filters.cache';
const MOSTRAR_SOLICITACAO_TROCA_UI = false;
const MODAL_LOADING_MIN_MS = 1000;
const MODAL_LOADING_MAX_MS = 10000;
const MODAL_LOADING_TIMEOUT_RESULT = { timeout: true };
const MODAL_VIEW_LOAD_ERROR_MESSAGE =
	'Não foi possível carregar essas informações.';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const getLocalDateKey = (date = new Date()) =>
	[
		date.getFullYear(),
		String(date.getMonth() + 1).padStart(2, '0'),
		String(date.getDate()).padStart(2, '0'),
	].join('-');

const getScaleItemDateKey = (item = {}) => {
	const value =
		item?.data ||
		item?.dados_edicao?.data ||
		item?.resumo?.data ||
		item?.origem?.data ||
		'';
	const match = String(value).match(/^(\d{4}-\d{2}-\d{2})/);
	return match?.[1] || '';
};

const isFutureScaleItem = (item = {}) => {
	const dateKey = getScaleItemDateKey(item);
	return Boolean(dateKey && dateKey > getLocalDateKey());
};

const runWithModalLoadingWindow = async (promise) => {
	const start = Date.now();
	let timeoutId;

	try {
		const result = await Promise.race([
			promise,
			new Promise((resolve) => {
				timeoutId = setTimeout(
					() => resolve(MODAL_LOADING_TIMEOUT_RESULT),
					MODAL_LOADING_MAX_MS,
				);
			}),
		]);
		const elapsed = Date.now() - start;

		if (elapsed < MODAL_LOADING_MIN_MS) {
			await wait(MODAL_LOADING_MIN_MS - elapsed);
		}

		return result;
	} finally {
		clearTimeout(timeoutId);
	}
};
const DEFAULT_MEDICO_CARGO = 'Médico';

const catalogosRequestCache = new Map();

const getCatalogosRequestKey = (token = '', estabelecimento = '') =>
	`${String(token || '').trim()}::${String(estabelecimento || '').trim()}`;

const fetchCatalogosOnce = async ({ token, estabelecimento } = {}) => {
	const key = getCatalogosRequestKey(token, estabelecimento);
	if (!token || !estabelecimento) return { error: true };
	if (catalogosRequestCache.has(key)) {
		return catalogosRequestCache.get(key);
	}

	const requestPromise = Promise.all([
		requestCatalogosEscalaSelects({ token, estabelecimento }),
		requestCatalogosEscalaSelectsComItens({ token, estabelecimento }),
	])
		.then(([catalogos, catalogosComItens]) => ({
			catalogos,
			catalogosComItens,
		}))
		.catch((_error) => ({ error: true }));

	catalogosRequestCache.set(key, requestPromise);

	const result = await requestPromise;
	if (result?.error || result?.catalogos?.error) {
		catalogosRequestCache.delete(key);
	}

	return result;
};

const buildEscalaFiltersUrl = ({
	estabelecimento,
	somenteComItens = false,
} = {}) => {
	const params = new URLSearchParams();
	if (estabelecimento) {
		params.set('estabelecimento', estabelecimento);
	}
	if (somenteComItens) {
		params.set('somente_com_itens', 'true');
	}
	const query = params.toString();
	return `${API_BASE_URL}/escala/escalas-mensais/filtros-busca/${
		query ? `?${query}` : ''
	}`;
};

const requestCatalogosEscalaSelectsComItens = async ({
	token,
	estabelecimento,
} = {}) => {
	if (!token) return {};
	try {
		const response = await fetch(
			buildEscalaFiltersUrl({
				estabelecimento,
				somenteComItens: true,
			}),
			{
				headers: {
					Authorization: `Token ${token}`,
					'Content-Type': 'application/json',
				},
			},
		);

		if (!response.ok) {
			return { error: true };
		}

		return await response.json();
	} catch (_error) {
		return { error: true };
	}
};

const getDefaultSearchFilters = () => ({
	unidade: '',
	tipoSetor: '',
	identificador: '',
	setor: '',
	especialidade: '',
	regime: 'rotina',
	servico: '',
	mesVigente: String(new Date().getMonth() + 1).padStart(2, '0'),
	anoVigente: String(new Date().getFullYear()),
});

const normalizarRegimeOptionValue = (value = '') =>
	String(value || '')
		.trim()
		.toLowerCase();

const getRegimeDefaultFromOptions = (options = []) => {
	const normalizedOptions = (Array.isArray(options) ? options : [])
		.map((option) => normalizarRegimeOptionValue(option?.value))
		.filter(Boolean);

	if (normalizedOptions.includes('todos')) return 'todos';
	if (normalizedOptions.includes('rotina')) return 'rotina';
	if (normalizedOptions.includes('plantonista')) return 'plantonista';
	return normalizedOptions[0] || 'rotina';
};

const regimeExistsInOptions = (value, options = []) => {
	const normalizedValue = normalizarRegimeOptionValue(value);
	if (!normalizedValue) return false;
	return (Array.isArray(options) ? options : []).some(
		(option) =>
			normalizarRegimeOptionValue(option?.value) === normalizedValue,
	);
};

const getCachedSearchFilters = () => {
	const defaults = getDefaultSearchFilters();

	try {
		const raw = localStorage.getItem(ESCALA_FILTERS_CACHE_KEY);
		if (!raw) return defaults;
		const parsed = JSON.parse(raw);

		return {
			...defaults,
			...parsed,
			unidade: String(parsed?.unidade || ''),
			tipoSetor: String(parsed?.tipoSetor || parsed?.servico || ''),
			identificador: String(parsed?.identificador || ''),
			setor: String(parsed?.setor || ''),
			especialidade: String(parsed?.especialidade || ''),
			regime: String(parsed?.regime || defaults.regime),
			servico: String(parsed?.servico || parsed?.tipoSetor || ''),
			mesVigente: defaults.mesVigente,
			anoVigente: defaults.anoVigente,
		};
	} catch (_error) {
		return defaults;
	}
};

const hasCachedSearchFilters = () => {
	try {
		return Boolean(localStorage.getItem(ESCALA_FILTERS_CACHE_KEY));
	} catch (_error) {
		return false;
	}
};

const getErrorMessage = (result, fallback) => {
	const issueMessages = [
		result?.issues,
		result?.error?.issues,
		result?.error?.data?.issues,
		result?.error?.detail?.issues,
	]
		.filter(Array.isArray)
		.flat()
		.map((issue = {}) => String(issue?.mensagem || '').trim())
		.filter(Boolean);
	if (issueMessages.length > 0) return Array.from(new Set(issueMessages)).join(' ');

	if (!result?.error) return '';
	const detail = result.error?.detail;
	if (typeof detail === 'string') return detail;
	if (Array.isArray(detail)) return detail.join(' ');
	if (typeof detail === 'object' && detail !== null) {
		return JSON.stringify(detail);
	}
	return fallback;
};

const resolveCargoValue = (...candidatos) => {
	const cargoValido = candidatos.find(
		(value) =>
			value !== null && value !== undefined && String(value).trim(),
	);

	return cargoValido ? String(cargoValido).trim() : DEFAULT_MEDICO_CARGO;
};

const normalizeItemType = (value = '') =>
	String(value)
		.toLowerCase()
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.trim();

const toDiaSemanaNumero = (dia) => {
	const diaMap = {
		Domingo: 7,
		'Segunda-feira': 1,
		'Terça-feira': 2,
		'Quarta-feira': 3,
		'Quinta-feira': 4,
		'Sexta-feira': 5,
		Sábado: 6,
	};
	return diaMap[dia];
};

const fromDiaSemanaNumero = (dia) => {
	const diaMap = {
		1: 'Segunda-feira',
		2: 'Terça-feira',
		3: 'Quarta-feira',
		4: 'Quinta-feira',
		5: 'Sexta-feira',
		6: 'Sábado',
		7: 'Domingo',
	};
	return diaMap[Number(dia)] || '';
};

const formatarHoraPayload = (valor = '') => {
	const texto = String(valor || '').trim();
	if (!texto) return '';
	if (/^\d{2}:\d{2}:\d{2}$/.test(texto)) return texto;
	if (/^\d{2}:\d{2}$/.test(texto)) return `${texto}:00`;
	if (/^\d{1,2}$/.test(texto)) {
		return `${texto.padStart(2, '0')}:00:00`;
	}
	return texto;
};

const calculateWeeklyHours = (dias = [], turnos = {}) => {
	let total = 0;
	dias.forEach((dia) => {
		const turno = turnos?.[dia];
		if (!turno?.inicio || !turno?.fim) return;
		const inicio = Number.parseInt(turno.inicio, 10);
		const fim = Number.parseInt(turno.fim, 10);
		if (Number.isNaN(inicio) || Number.isNaN(fim)) return;
		const horas = fim <= inicio ? 24 - inicio + fim : fim - inicio;
		total += horas;
	});
	return total;
};

const buildRecorrenciaOcorrencias = (dias = [], turnos = {}) =>
	dias
		.map((dia) => {
			const turno = turnos?.[dia] || {};
			const diaSemana = toDiaSemanaNumero(dia);
			const horaInicio = formatarHoraPayload(turno?.inicio);
			const horaFim = formatarHoraPayload(turno?.fim);

			if (!diaSemana || !horaInicio || !horaFim) {
				return null;
			}

			return {
				dia_semana: diaSemana,
				hora_inicio: horaInicio,
				hora_fim: horaFim,
			};
		})
		.filter(Boolean);

const buildTurnosFromOcorrencias = (ocorrencias = []) =>
	ocorrencias.reduce((acc, ocorrencia) => {
		const dia = fromDiaSemanaNumero(ocorrencia?.dia_semana);
		if (!dia) return acc;

		acc[dia] = {
			inicio: String(ocorrencia?.hora_inicio || '').slice(0, 2),
			fim: String(ocorrencia?.hora_fim || '').slice(0, 2),
		};

		return acc;
	}, {});

const normalizeIsoDate = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';

	const exactMatch = raw.match(/^(\d{4}-\d{2}-\d{2})/);
	if (exactMatch?.[1]) return exactMatch[1];

	const parsed = moment(raw, ['YYYY-MM-DD', moment.ISO_8601], true);
	return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
};

const isDateWithinPeriod = (
	dateValue = '',
	periodStart = '',
	periodEnd = '',
) => {
	const date = normalizeIsoDate(dateValue);
	const start = normalizeIsoDate(periodStart);
	const end = normalizeIsoDate(periodEnd);

	if (!date || !start || !end) return false;

	return date >= start && date <= end;
};

const getDiaSemanaNumeroFromDate = (value = '') => {
	const isoDate = normalizeIsoDate(value);
	if (!isoDate) return null;

	const parsed = moment(isoDate, 'YYYY-MM-DD', true);
	return parsed.isValid() ? parsed.isoWeekday() : null;
};

const buildOcorrenciaFromEscalaItem = (item = {}) => {
	const horarioItem = item?.horario || item?.edicao?.horario || {};
	const data = normalizeIsoDate(
		horarioItem?.data ||
			item?.dados_edicao?.data ||
			item?.data ||
			item?.celula?.data ||
			'',
	);
	const horaInicio = formatarHoraPayload(
		horarioItem?.hora_inicio ||
			item?.dados_edicao?.hora_inicio ||
			item?.hora_inicio ||
			item?.resumo?.hora_inicio ||
			'',
	);
	const horaFim = formatarHoraPayload(
		horarioItem?.hora_fim ||
			item?.dados_edicao?.hora_fim ||
			item?.hora_fim ||
			item?.resumo?.hora_fim ||
			'',
	);
	const diaSemana = Number(
		horarioItem?.dia_semana ||
			item?.celula?.dia_semana ||
			item?.dia_semana ||
			item?.dados_edicao?.dia_semana ||
			getDiaSemanaNumeroFromDate(data) ||
			0,
	);

	if (!data || !diaSemana || !horaInicio || !horaFim) {
		return null;
	}

	return {
		data,
		dia_semana: diaSemana,
		hora_inicio: horaInicio,
		hora_fim: horaFim,
	};
};

const SPECIAL_INTERVAL_TYPES = new Set([
	'ferias',
	'atestado',
	'folga',
	'licenca',
]);

const addDaysToIsoDate = (value = '', days = 0) => {
	const isoDate = normalizeIsoDate(value);
	if (!isoDate) return '';

	const parsed = moment(isoDate, 'YYYY-MM-DD', true);
	return parsed.isValid()
		? parsed.add(days, 'days').format('YYYY-MM-DD')
		: '';
};

const getEscalaItemTipo = (item = {}) =>
	normalizeItemType(
		item?.tipo_item || item?.dados_edicao?.tipo_item || item?.status || '',
	);

const isSpecialIntervalTipo = (tipo = '') =>
	SPECIAL_INTERVAL_TYPES.has(normalizeItemType(tipo));

const getEscalaItemObservation = (...sources) => {
	for (const source of sources) {
		if (!source) continue;

		if (typeof source === 'string') {
			const texto = String(source).trim();
			if (texto) return texto;
			continue;
		}

		const observation = [
			source?.dados_edicao?.observacao,
			source?.edicao?.observacao,
			source?.observacao,
			source?.resumo?.observacao,
			source?.resumo?.descricao,
			source?.descricao,
			source?.motivo,
		].find((value) => String(value || '').trim());

		if (observation) {
			return String(observation).trim();
		}
	}

	return '';
};

const getEscalaTrocaPartnerName = (...sources) => {
	for (const source of sources) {
		if (!source || typeof source !== 'object') continue;

		const candidate = [
			source?.troca_com_nome,
			source?.dados_edicao?.troca_com_nome,
			source?.edicao?.troca_com_nome,
			source?.nome_profissional_destino,
			source?.nome_profissional_origem,
			source?.resumo?.troca_com,
			source?.resumo?.nome_profissional_destino,
			source?.resumo?.nome_profissional_origem,
			source?.troca?.profissional_original?.nome,
			source?.troca?.profissional_original?.nome_completo,
			source?.troca?.profissionalOriginal?.nome,
			source?.troca?.profissionalOriginal?.nome_completo,
			source?.troca?.profissional_destino?.nome,
			source?.troca?.profissional_origem?.nome,
			source?.troca?.destino?.profissional?.nome,
			source?.troca?.origem?.profissional?.nome,
			source?.troca?.item_destino?.profissional?.nome,
			source?.troca?.item_origem?.profissional?.nome,
			source?.troca?.destino?.nome_profissional,
			source?.troca?.origem?.nome_profissional,
		].find((value) => String(value || '').trim());

		if (candidate) {
			return String(candidate).trim();
		}
	}

	return '';
};

const formatEscalaShiftLabel = (value = '') => {
	const raw = String(value || '').trim();
	if (!raw) return '';

	const normalized = normalizeItemType(raw);
	if (normalized === 'diurno') return 'Diurno';
	if (normalized === 'noturno') return 'Noturno';

	return raw;
};

const resolveEscalaItemShiftLabel = (...sources) => {
	for (const source of sources) {
		if (!source || typeof source !== 'object') continue;

		const candidate = [
			source?.nome_faixa_horaria,
			source?.faixa_horaria_texto,
			source?.nome_turno,
			source?.turno,
			source?.faixa_horaria,
			source?.horario?.nome_faixa_horaria,
			source?.horario?.faixa_horaria_texto,
			source?.horario?.nome_turno,
			source?.horario?.turno,
			source?.horario?.faixa_horaria,
			source?.dados_edicao?.nome_faixa_horaria,
			source?.dados_edicao?.faixa_horaria_texto,
			source?.dados_edicao?.nome_turno,
			source?.dados_edicao?.turno,
			source?.dados_edicao?.faixa_horaria,
			source?.resumo?.faixa_horaria_texto,
			source?.resumo?.nome_faixa_horaria,
			source?.resumo?.turno,
			source?.resumo?.faixa_horaria,
		].find((value) => String(value || '').trim());

		if (candidate) {
			return formatEscalaShiftLabel(candidate);
		}
	}

	return '';
};

const getContinuousIntervalFromDates = (dates = [], targetDate = '') => {
	const normalizedDates = [
		...new Set(
			(dates || []).map((date) => normalizeIsoDate(date)).filter(Boolean),
		),
	].sort();
	const normalizedTargetDate = normalizeIsoDate(targetDate);

	if (!normalizedDates.length || !normalizedTargetDate) {
		return null;
	}

	if (!normalizedDates.includes(normalizedTargetDate)) {
		return {
			dataInicio: normalizedTargetDate,
			dataFim: normalizedTargetDate,
		};
	}

	let dataInicio = normalizedTargetDate;
	let dataFim = normalizedTargetDate;

	while (normalizedDates.includes(addDaysToIsoDate(dataInicio, -1))) {
		dataInicio = addDaysToIsoDate(dataInicio, -1);
	}

	while (normalizedDates.includes(addDaysToIsoDate(dataFim, 1))) {
		dataFim = addDaysToIsoDate(dataFim, 1);
	}

	return { dataInicio, dataFim };
};

const getSpecialContinuousPeriod = ({
	items = [],
	currentItem = null,
	fallbackDate = '',
} = {}) => {
	const tipoItem = getEscalaItemTipo(currentItem);
	if (!isSpecialIntervalTipo(tipoItem)) return null;

	const targetDate =
		buildOcorrenciaFromEscalaItem(currentItem)?.data ||
		normalizeIsoDate(fallbackDate);
	if (!targetDate) return null;

	const relevantDates = (items || [])
		.filter((item) => getEscalaItemTipo(item) === tipoItem)
		.map((item) => buildOcorrenciaFromEscalaItem(item)?.data)
		.filter(Boolean);

	if (!relevantDates.includes(targetDate)) {
		relevantDates.push(targetDate);
	}

	return getContinuousIntervalFromDates(relevantDates, targetDate);
};

const extractEscalaItemDates = (items = []) =>
	[
		...new Set(
			items
				.map((item) => buildOcorrenciaFromEscalaItem(item)?.data)
				.filter(Boolean),
		),
	].sort();

const normalizeComparableString = (value = '') => String(value || '').trim();

const normalizeComparableDigits = (value = '') =>
	String(value || '').replace(/\D/g, '');

const normalizeComparableList = (items = []) =>
	(items || []).map((item) => String(item || '')).sort();

const normalizeComparableTurnos = (turnos = {}) =>
	Object.entries(turnos || {})
		.map(([dia, turno]) => [
			String(dia || ''),
			String(turno?.inicio || ''),
			String(turno?.fim || ''),
		])
		.sort(([diaA], [diaB]) => diaA.localeCompare(diaB));

const semana = [
	'Segunda-feira',
	'Terça-feira',
	'Quarta-feira',
	'Quinta-feira',
	'Sexta-feira',
	'Sábado',
	'Domingo',
];

const UF_OPTIONS = [
	'AC',
	'AL',
	'AP',
	'AM',
	'BA',
	'CE',
	'DF',
	'ES',
	'GO',
	'MA',
	'MT',
	'MS',
	'MG',
	'PA',
	'PB',
	'PR',
	'PE',
	'PI',
	'RJ',
	'RN',
	'RS',
	'RO',
	'RR',
	'SC',
	'SP',
	'SE',
	'TO',
];

const normalizeUfSigla = (value = '') => {
	const normalized = String(value || '')
		.trim()
		.toUpperCase();
	return UF_OPTIONS.includes(normalized) ? normalized : '';
};

const getNumeroConselhoFromProfissional = (profissional = {}) => {
	const profissionalSeguro = profissional || {};
	if (profissionalSeguro.numero_registro) {
		return String(profissionalSeguro.numero_registro)
			.replace(/\D/g, '')
			.slice(0, 20);
	}
	return '';
};

/* String(
		profissional?.conselho ||
			profissional?.numero_registro ||
			profissional?.registro ||
			profissional?.crm ||
			profissional?.numeroConselho ||
			'',
	)
		.replace(/\D/g, '')
		.slice(0, 20); */

const getUfConselhoFromProfissional = (profissional = {}) =>
	normalizeUfSigla(
		profissional?.uf_conselho ||
			profissional?.ufConselho ||
			profissional?.conselho_uf ||
			profissional?.uf ||
			'',
	);

const hasOwnField = (obj, fieldName) =>
	Boolean(
		obj &&
		typeof obj === 'object' &&
		Object.prototype.hasOwnProperty.call(obj, fieldName),
	);

const hasTelefoneField = (value, visited = new WeakSet()) => {
	if (!value || typeof value !== 'object') return false;
	if (visited.has(value)) return false;
	visited.add(value);

	if (hasOwnField(value, 'telefone') || hasOwnField(value, 'celular')) {
		return true;
	}

	if (Array.isArray(value)) {
		return value.some((item) => hasTelefoneField(item, visited));
	}

	return Object.values(value).some((item) => hasTelefoneField(item, visited));
};

const hasNamedPermission = (permissions = [], permissionName = '') => {
	const target = String(permissionName || '')
		.trim()
		.toLowerCase();
	if (!target) return false;

	const normalizedPermissions = Array.isArray(permissions)
		? permissions
		: [permissions];

	return normalizedPermissions.some(
		(permission) =>
			String(permission || '')
				.trim()
				.toLowerCase() === target,
	);
};

const normalizeLookupText = (value = '') =>
	String(value || '')
		.normalize('NFD')
		.replace(/[\u0300-\u036f]/g, '')
		.toLowerCase()
		.trim();

const getVinculoOptionLabel = (options = [], value = '') => {
	const rawValue = String(value || '').trim();
	if (!rawValue) return '';

	const normalizedValue = normalizeLookupText(rawValue);
	const optionMatch = (options || []).find((option) =>
		[
			option?.id,
			option?.codigo,
			option?.value,
			option?.nome,
			option?.descricao,
			option?.label,
		].some((candidate) => {
			const candidateValue = String(candidate || '').trim();
			if (!candidateValue) return false;

			return (
				candidateValue === rawValue ||
				normalizeLookupText(candidateValue) === normalizedValue
			);
		}),
	);

	return String(
		optionMatch?.nome || optionMatch?.descricao || optionMatch?.label || '',
	).trim();
};
const getVinculoTipoIdFromProfissionalPorCpf = (...candidatos) => {
	for (const candidato of candidatos) {
		if (
			!candidato ||
			typeof candidato !== 'object' ||
			Array.isArray(candidato)
		) {
			continue;
		}

		if (
			Object.prototype.hasOwnProperty.call(candidato, 'vinculo_tipo_id')
		) {
			return candidato.vinculo_tipo_id;
		}
	}

	return undefined;
};

const getVinculoDisplayName = (...candidatos) => {
	for (const candidato of candidatos) {
		if (!candidato || typeof candidato !== 'object') continue;

		const nome =
			candidato?.nome_vinculo ||
			candidato?.vinculo_tipo_nome ||
			candidato?.vinculo_nome ||
			candidato?.descricao_vinculo ||
			candidato?.vinculo?.nome ||
			candidato?.vinculo?.descricao ||
			candidato?.vinculo?.label ||
			candidato?.vinculo_tipo?.nome ||
			candidato?.tipo_vinculo?.nome ||
			'';

		if (String(nome).trim()) return String(nome).trim();
	}

	return '';
};

const FormScale = () => {
	const navigate = useNavigate();
	const [ultimoDiaTipo, setUltimoDiaTipo] = useState({});
	const [hasInitialCachedFilters] = useState(hasCachedSearchFilters);
	const [authStatus, setAuthStatus] = useState('loading');
	const [isPrivilegedEscalaUser, setIsPrivilegedEscalaUser] = useState(false);
	const [
		canAccessProfessionalManagement,
		setCanAccessProfessionalManagement,
	] = useState(false);
	const [canViewProfessionalPhone, setCanViewProfessionalPhone] =
		useState(true);
	const [carregando, setCarregando] = useState(false);
	const [cpf, setCpf] = useState('');
	const [vacanciaOn, setVacanciaOn] = useState(false);
	const [showAddScaleModal, setShowAddScaleModal] = useState(false);
	const [showSolicitacaoTrocaModal, setShowSolicitacaoTrocaModal] =
		useState(false);
	const [solicitacaoTrocaForm, setSolicitacaoTrocaForm] = useState({
		origemId: '',
		trocaCom: '',
		data: '',
		turnoId: '',
		justificativa: '',
	});
	const [solicitacaoTrocaCandidatos, setSolicitacaoTrocaCandidatos] =
		useState([]);
	const [solicitacaoTrocaProfissionalId, setSolicitacaoTrocaProfissionalId] =
		useState('');
	const [solicitacaoTrocaLoading, setSolicitacaoTrocaLoading] =
		useState(false);
	const [solicitacaoTrocaFeedback, setSolicitacaoTrocaFeedback] = useState({
		type: '',
		message: '',
	});
	const [solicitacaoTrocaEnviada, setSolicitacaoTrocaEnviada] =
		useState(false);
	const [popupRegime, setPopupRegime] = useState('rotina');
	const [popupStatus, setPopupStatus] = useState('');
	const [popupExtraFields, setPopupExtraFields] = useState({
		dataInicio: '',
		dataFim: '',
		dataInicioVacancia: '',
		observacao: '',
	});
	const [searchFilters, setSearchFilters] = useState(getCachedSearchFilters);
	const [appliedFilters, setAppliedFilters] = useState(
		getCachedSearchFilters,
	);
	const [viewMode, setViewMode] = useState('setor');
	const [gradePayload, setGradePayload] = useState(null);
	const [gradeMessage, setGradeMessage] = useState('');
	const [gradeEspecialidadePayloads, setGradeEspecialidadePayloads] =
		useState([]);
	const [gradeEspecialidadeMessage, setGradeEspecialidadeMessage] =
		useState('');
	const [, setOperationError] = useState('');

	const [, setCabeçalho] = useState({
		unidades: '',
		setores: '',
		regimes: '',
		turnos: '',
	});

	const [currentPro, setCurrentPro] = useState({
		nome: '',
		cargo: DEFAULT_MEDICO_CARGO,
		admissao: '',
		numeroConselho: '',
		ufConselho: '',
		regime: 'rotina',
		cargaHorariaSemanal: '',
		turnos: {},
		turnoLabel: '',
		especialidade: '',
		observacao: '',
		vinculo: '',
		setor: '',
		sala: '',
		telefone: '',
		dias: [],
		trocaCom: '',
		trocaComNome: '',
		trocaData: '',
		trocaHora: '',
		trocaItemDestinoId: '',
	});

	const [currentSearch, setCurrentSearch] = useState([]);
	const [token, setToken] = useState('');
	const [, setServiceOptions] = useState([]);
	const [identificadorCatalogOptions, setIdentificadorCatalogOptions] =
		useState([]);
	const [regimeOptions, setRegimeOptions] = useState([]);
	const [vinculoOptions, setVinculoOptions] = useState([]);
	const [catalogoSetores, setCatalogoSetores] = useState([]);
	const [setorOptions, setSetorOptions] = useState([]);
	const [, setSalaOptions] = useState([]);
	const [especialidadeOptions, setEspecialidadeOptions] = useState([]);
	const [modalEspecialidadeOptions, setModalEspecialidadeOptions] = useState(
		[],
	);
	const [modalSetorOptions, setModalSetorOptions] = useState([]);
	const [cargoOptions, setCargoOptions] = useState([]);
	const [editingItemId, setEditingItemId] = useState(null);
	const [editingItemSnapshot, setEditingItemSnapshot] = useState(null);
	const [isPopupEditing, setIsPopupEditing] = useState(false);
	const [isModalLoading, setIsModalLoading] = useState(false);
	const [modalViewLoadError, setModalViewLoadError] = useState('');
	const [forceViewOnlyModal, setForceViewOnlyModal] = useState(false);
	const [cpfLockedFields, setCpfLockedFields] = useState({});
	const [popupFieldErrors, setPopupFieldErrors] = useState({});
	const [popupSubmitError, setPopupSubmitError] = useState('');
	const [cpfSearchWarning, setCpfSearchWarning] = useState('');
	const [popupFormularioAssistivo, setPopupFormularioAssistivo] =
		useState(null);
	const [showUnidadeFilter, setShowUnidadeFilter] = useState(true);
	const [userCnes, setUserCnes] = useState('');
	const [unidadeOptions, setUnidadeOptions] = useState([]);
	const [unidadeSearchTerm, setUnidadeSearchTerm] = useState('');
	const [showUnidadeSuggestions, setShowUnidadeSuggestions] = useState(false);
	const filtroNomeProfissional = ''; // filtro de nome removido da UI
	const [initialGradeChecked, setInitialGradeChecked] = useState(false);
	const [trocaCandidatos, setTrocaCandidatos] = useState([]);
	const [carregandoTroca, setCarregandoTroca] = useState(false);
	const [editingProfessionalGradeItems, setEditingProfessionalGradeItems] =
		useState([]);
	const [, setEditingProfessionalGradeDays] = useState([]);
	const handleEditItemRef = useRef(null);

	useEffect(() => {
		const hasTelefoneDisponivel = [
			gradePayload,
			...gradeEspecialidadePayloads,
		]
			.filter(Boolean)
			.some((payload) => hasTelefoneField(payload));

		if (gradePayload !== null || gradeEspecialidadePayloads.length > 0) {
			setCanViewProfessionalPhone(hasTelefoneDisponivel);
		}
	}, [gradePayload, gradeEspecialidadePayloads]);

	const getFieldBorderClass = (fieldName) =>
		popupFieldErrors[fieldName] ? 'border-[#dc2626]' : 'border-[#E3E3E3]';

	const getFieldToneClass = (value) =>
		String(value ?? '').trim() ? 'text-[#6B7280]' : 'text-[#CBD3DD]';

	const SELECT_OPTION_PROPS = {
		className: 'text-[#4F5257] bg-white',
		style: { color: '#4F5257', backgroundColor: '#FFFFFF' },
	};

	const getLockedFieldStateClass = (isLocked) =>
		isLocked
			? '!bg-[#E5E7EB] text-[#7C8796] border-[#D1D5DB] cursor-not-allowed opacity-100'
			: '';

	const getProfessionalModalFieldClass = (
		fieldName,
		value,
		{ locked = false, extraClass = '' } = {},
	) =>
		`border-[1px] w-full outline-none ${getFieldBorderClass(fieldName)} rounded-lg text-[14px] font-medium px-2.5 py-1.5 text-start bg-white transition-colors placeholder:text-[#D8DFE8] ${getFieldToneClass(value)} ${getLockedFieldStateClass(locked)} ${extraClass}`.trim();

	const getProfessionalModalDateFieldClass = (
		fieldName,
		value,
		{ locked = false, extraClass = '' } = {},
	) =>
		getProfessionalModalFieldClass(fieldName, value, {
			locked,
			extraClass:
				`text-[#6B7280] [color-scheme:light] ${extraClass}`.trim(),
		});

	const getPopupDateFieldClass = (fieldName) =>
		`border-[1px] w-full outline-none ${getFieldBorderClass(fieldName)} rounded-md text-[14px] font-medium px-2 py-2 text-start text-[#6B7280] bg-white transition-colors [color-scheme:light]`;

	const clearFieldError = (fieldName) => {
		setPopupFieldErrors((prevState) => {
			if (!prevState[fieldName]) return prevState;
			const nextState = { ...prevState };
			delete nextState[fieldName];
			return nextState;
		});
	};

	const getEmptySelectMessage = (fieldName) => {
		if (fieldName === 'troca') {
			return 'Nenhum candidato disponível para troca';
		}
		if (fieldName === 'especialidade') {
			return 'Nenhuma área de atuação adicionada para esta unidade';
		}
		if (fieldName === 'setor') {
			return 'Nenhum setor adicionado para esta unidade';
		}
		if (fieldName === 'sala') {
			return 'Nenhuma sala adicionada para esta unidade';
		}

		return 'Nenhum item cadastrado';
	};

	const cargaHorariaSemanalCalculada = calculateWeeklyHours(
		currentPro.dias,
		currentPro.turnos,
	);

	const normalizeUnits = (unitsPayload) => {
		const unitsList = Array.isArray(unitsPayload)
			? unitsPayload
			: Array.isArray(unitsPayload?.results)
				? unitsPayload.results
				: Array.isArray(unitsPayload?.unidades)
					? unitsPayload.unidades
					: Array.isArray(unitsPayload?.estabelecimentos)
						? unitsPayload.estabelecimentos
						: [];

		const dedupByCnes = new Map();
		const normalizeUnitName = (value = '') =>
			String(value)
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim();
		const shouldHideMedicalScaleUnit = (nome = '') =>
			normalizeUnitName(nome) === 'cer andarai';

		unitsList.forEach((item) => {
			const cnes = String(
				item?.cnes ||
					item?.cnes_estabelecimento ||
					item?.codigo_cnes ||
					item?.id ||
					'',
			).trim();
			const nome = String(
				item?.nome || item?.unidade || item?.nome_fantasia || '',
			).trim();

			if (!cnes || !nome) return;
			if (shouldHideMedicalScaleUnit(nome)) return;
			if (!dedupByCnes.has(cnes)) {
				dedupByCnes.set(cnes, { cnes, nome });
			}
		});

		return Array.from(dedupByCnes.values());
	};

	const normalizeSelectOptions = (
		items = [],
		{ preferLabelAsValue = false } = {},
	) =>
		(items || [])
			.map((item) => {
				const label = String(
					item?.nome ||
						item?.nome_exibicao ||
						item?.label ||
						item?.descricao ||
						item?.identificador ||
						item?.value ||
						item ||
						'',
				).trim();
				const rawValue = preferLabelAsValue
					? label
					: item?.id || item?.codigo || item?.value || label;

				return {
					value: String(rawValue || '').trim(),
					label,
				};
			})
			.filter((item) => item.value && item.label);

	const resolveEspecialidadeSelectValue = useCallback(
		(...candidatos) => {
			const sourceOptions = [
				...modalEspecialidadeOptions,
				...especialidadeOptions,
			];
			const valorEncontrado = candidatos.find(
				(value) =>
					value !== null &&
					value !== undefined &&
					String(value).trim() !== '',
			);

			if (!valorEncontrado) return '';

			const texto = String(valorEncontrado).trim();
			const porId = sourceOptions.find(
				(option) => String(option.id) === texto,
			);
			if (porId) return String(porId.id);

			const normalizar = (value = '') =>
				String(value)
					.normalize('NFD')
					.replace(/[\u0300-\u036f]/g, '')
					.toLowerCase()
					.trim();

			const textoNormalizado = normalizar(texto);
			const porNome = sourceOptions.find(
				(option) => normalizar(option.nome) === textoNormalizado,
			);
			if (porNome) return String(porNome.id);

			return texto;
		},
		[especialidadeOptions, modalEspecialidadeOptions],
	);

	const buildPopupValidationErrors = () => {
		const errors = {};
		const tipo = (popupStatus || 'alocacao').toLowerCase();
		const isVacancia = tipo === 'vacancia';
		const isTroca = tipo === 'troca';
		const isCreating = !isPopupEditing;
		const diasComTurno = (currentPro.dias || []).filter((dia) => {
			const turno = currentPro.turnos?.[dia];
			return turno?.inicio && turno?.fim;
		});

		if (isTroca) {
			if (!currentPro.trocaCom) {
				errors.trocaCom = 'Selecione o profissional da troca.';
			}

			if (!currentPro.trocaData) {
				errors.trocaData = 'Selecione a data da troca.';
			}

			if (!currentPro.trocaItemDestinoId && !currentPro.trocaHora) {
				errors.trocaHora = 'Selecione o horário da troca.';
			}

			return errors;
		}

		if (!isVacancia && !String(cpf || '').trim()) {
			errors.cpf = 'CPF é obrigatório para este tipo de item.';
		}

		if (!isVacancia) {
			if (!currentPro.setor) {
				errors.setor = 'Setor é obrigatório.';
			}
		}

		if (!isVacancia) {
			const nomeAtual = currentSearch[0]?.nome || currentPro.nome || '';
			if (!String(nomeAtual).trim()) {
				errors.nome = 'Nome é obrigatório.';
			}

			if (isCreating) {
				const especialidadeSelecionada =
					resolveEspecialidadeSelectValue(
						currentSearch[0]?.especialidade_id,
						currentSearch[0]?.especialidade,
						currentPro.especialidade,
					);

				if (!String(especialidadeSelecionada || '').trim()) {
					errors.especialidade =
						'Área de atuação é obrigatória para criar item da escala.';
				}
			}
		}

		if (isCreating && diasComTurno.length === 0) {
			errors.turnos =
				'Informe ao menos um dia e uma faixa horária para adicionar à escala.';
		}

		return errors;
	};

	const mapApiErrorToFields = (message) => {
		const normalizedMessage = String(message || '').toLowerCase();
		const mapped = {};

		if (normalizedMessage.includes('cpf')) mapped.cpf = true;
		if (normalizedMessage.includes('nome')) mapped.nome = true;
		if (normalizedMessage.includes('setor')) mapped.setor = true;
		if (normalizedMessage.includes('sala')) mapped.sala = true;
		if (normalizedMessage.includes('troca')) mapped.trocaHora = true;

		return mapped;
	};

	const canSwapScaleItem = (item) => {
		if (!item) return false;

		const tipoAtual = normalizeItemType(
			item?.tipo_item || item?.dados_edicao?.tipo_item || '',
		);

		return tipoAtual !== 'vacancia';
	};

	const formatarHoraCurta = useCallback(
		(valor = '') => String(valor || '').slice(0, 5) || '--:--',
		[],
	);

	const formatarFaixaTroca = useCallback(
		(item = {}) => {
			const inicioValor =
				item?.horario?.hora_inicio ||
				item?.dados_edicao?.horario?.hora_inicio ||
				item?.edicao?.horario?.hora_inicio ||
				item?.hora_inicio ||
				item?.dados_edicao?.hora_inicio ||
				item?.edicao?.hora_inicio ||
				item?.resumo?.hora_inicio ||
				'';
			const fimValor =
				item?.horario?.hora_fim ||
				item?.dados_edicao?.horario?.hora_fim ||
				item?.edicao?.horario?.hora_fim ||
				item?.hora_fim ||
				item?.dados_edicao?.hora_fim ||
				item?.edicao?.hora_fim ||
				item?.resumo?.hora_fim ||
				'';

			if (inicioValor && fimValor) {
				return `${formatarHoraCurta(inicioValor)} às ${formatarHoraCurta(
					fimValor,
				)}`;
			}

			return (
				item?.faixa_horaria_texto ||
				item?.horario?.faixa_horaria_texto ||
				item?.dados_edicao?.faixa_horaria_texto ||
				item?.resumo?.faixa_horaria_texto ||
				'Horário não informado'
			);
		},
		[formatarHoraCurta],
	);

	const formatarDataCurta = useCallback((value = '') => {
		const match = String(value || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
		if (!match) return String(value || '');
		return `${match[3]}/${match[2]}/${match[1]}`;
	}, []);

	const getTrocaProfessionalKey = useCallback(
		(item = {}) =>
			String(
				item?.profissional ||
					item?.dados_edicao?.profissional ||
					item?.nome_profissional ||
					'',
			),
		[],
	);

	const profissionaisTrocaDisponiveis = useMemo(() => {
		const mapa = new Map();

		trocaCandidatos.forEach((item) => {
			const key = getTrocaProfessionalKey(item);
			const nome = String(item?.nome_profissional || '').trim();
			if (!key || !nome || mapa.has(key)) return;
			mapa.set(key, { value: key, label: nome, nome });
		});

		return Array.from(mapa.values());
	}, [trocaCandidatos, getTrocaProfessionalKey]);

	const datasTrocaDisponiveis = useMemo(() => {
		if (!currentPro.trocaCom) return [];

		const mapa = new Map();

		trocaCandidatos.forEach((item) => {
			if (getTrocaProfessionalKey(item) !== String(currentPro.trocaCom)) {
				return;
			}

			const data = String(item?.data || item?.dados_edicao?.data || '');
			if (!data || mapa.has(data)) return;
			mapa.set(data, {
				value: data,
				label: formatarDataCurta(data),
			});
		});

		return Array.from(mapa.values());
	}, [
		trocaCandidatos,
		currentPro.trocaCom,
		getTrocaProfessionalKey,
		formatarDataCurta,
	]);

	const horasTrocaDisponiveis = useMemo(() => {
		if (!currentPro.trocaCom || !currentPro.trocaData) return [];

		return trocaCandidatos
			.filter((item) => {
				const sameProfessional =
					getTrocaProfessionalKey(item) ===
					String(currentPro.trocaCom);
				const sameDate =
					String(item?.data || item?.dados_edicao?.data || '') ===
					String(currentPro.trocaData);
				return sameProfessional && sameDate;
			})
			.map((item) => {
				const dados = item?.dados_edicao || {};
				const itemId =
					item?.id ||
					dados?.id ||
					item?.item_id ||
					dados?.item_id ||
					item?.id_item ||
					dados?.id_item ||
					item?.escala_item_id ||
					dados?.escala_item_id ||
					(Array.isArray(item?.celula?.itens_ids)
						? item.celula.itens_ids[0]
						: '');

				return {
					value: String(itemId || ''),
					label: formatarFaixaTroca(item),
					itemId: String(itemId || ''),
				};
			});
	}, [
		trocaCandidatos,
		currentPro.trocaCom,
		currentPro.trocaData,
		getTrocaProfessionalKey,
		formatarFaixaTroca,
	]);

	const normalizedCargoOptions = useMemo(
		() =>
			normalizeSelectOptions(cargoOptions, { preferLabelAsValue: true }),
		[cargoOptions],
	);

	const cargoSelectOptions = useMemo(() => {
		const optionsMap = new Map();

		[DEFAULT_MEDICO_CARGO, currentPro.cargo]
			.filter((value) => String(value || '').trim())
			.forEach((value) => {
				const normalizedValue = String(value).trim();
				optionsMap.set(normalizedValue, {
					value: normalizedValue,
					label: normalizedValue,
				});
			});

		normalizedCargoOptions.forEach((option) => {
			if (!optionsMap.has(option.value)) {
				optionsMap.set(option.value, option);
			}
		});

		return Array.from(optionsMap.values());
	}, [currentPro.cargo, normalizedCargoOptions]);

	const getMesReferenciaAtual = useCallback(() => {
		const mesNumero = String(searchFilters.mesVigente || '').padStart(
			2,
			'0',
		);
		const anoNumero = String(searchFilters.anoVigente || '');
		return `${anoNumero}-${mesNumero}-01`;
	}, [searchFilters.anoVigente, searchFilters.mesVigente]);

	const getPeriodoPadraoMes = useCallback(
		(mesReferencia) => {
			const mesMatch = String(mesReferencia || '').match(
				/^(\d{4})-(\d{2})/,
			);
			const anoNumero = mesMatch?.[1] || String(searchFilters.anoVigente);
			const mesNumero = mesMatch?.[2] || String(searchFilters.mesVigente);
			const ultimoDiaDoMes = new Date(
				Number(anoNumero),
				Number(mesNumero),
				0,
			).getDate();

			return {
				dataInicio: `${anoNumero}-${mesNumero}-01`,
				dataFim: `${anoNumero}-${mesNumero}-${String(ultimoDiaDoMes).padStart(2, '0')}`,
			};
		},
		[searchFilters.anoVigente, searchFilters.mesVigente],
	);

	const applyFormularioAssistivo = useCallback(
		(formulario, options = {}) => {
			if (!formulario || formulario?.error) return;

			setPopupFormularioAssistivo(formulario);

			const catalogos = formulario?.catalogos || {};
			const setoresRelacionados = Array.isArray(
				catalogos?.setores_relacionados,
			)
				? catalogos.setores_relacionados
				: [];
			const especialidadesFormulario = Array.isArray(
				catalogos?.especialidades,
			)
				? catalogos.especialidades
				: [];
			const cargosFormulario = Array.isArray(catalogos?.cargos)
				? catalogos.cargos
				: [];

			setModalEspecialidadeOptions(especialidadesFormulario);
			setCargoOptions(cargosFormulario);

			if (Array.isArray(catalogos?.vinculos)) {
				setVinculoOptions(catalogos.vinculos);
			}

			setModalSetorOptions(sortSetoresPorAndar(setoresRelacionados));

			const setorDefault =
				formulario?.defaults?.contexto_competencia?.setor_id ||
				formulario?.contexto_competencia?.setor_id ||
				'';
			const regimeDefault =
				String(
					formulario?.defaults?.contexto_competencia?.regime ||
						formulario?.contexto_competencia?.regime ||
						popupRegime ||
						'',
				).toLowerCase() || 'rotina';

			if (options.applyDefaultsToForm !== false) {
				setPopupRegime(regimeDefault);
				setCurrentPro((prevState) => ({
					...prevState,
					setor:
						prevState.setor ||
						(setorDefault ? String(setorDefault) : ''),
				}));

				if (!isPopupEditing) {
					const periodoPadrao = formulario?.defaults?.periodo || {};
					setPopupExtraFields((prevState) => ({
						...prevState,
						dataInicio:
							prevState.dataInicio ||
							periodoPadrao.data_inicio ||
							'',
						dataFim: prevState.dataFim || '',
						dataInicioVacancia:
							prevState.dataInicioVacancia ||
							formulario?.defaults?.data_inicio_vacancia ||
							'',
						observacao:
							prevState.observacao ||
							formulario?.defaults?.observacao ||
							'',
					}));
				}
			}
		},
		[isPopupEditing, popupRegime],
	);

	const resolveEscalaItemId = (item = {}) => {
		const dados = item?.dados_edicao || {};
		const candidatos = [
			item?.id,
			dados?.id,
			item?.item_id,
			dados?.item_id,
			item?.id_item,
			dados?.id_item,
			item?.escala_item_id,
			dados?.escala_item_id,
			Array.isArray(item?.celula?.itens_ids)
				? item.celula.itens_ids[0]
				: undefined,
		];

		const encontrado = candidatos.find(
			(value) =>
				value !== null &&
				value !== undefined &&
				String(value).trim() !== '',
		);

		return encontrado ?? null;
	};

	const solicitacaoTrocaOrigens = useMemo(() => {
		const professionalId = String(
			solicitacaoTrocaProfissionalId || '',
		).trim();
		if (!professionalId) return [];

		const payloads = [
			gradePayload,
			...(Array.isArray(gradeEspecialidadePayloads)
				? gradeEspecialidadePayloads
				: []),
		].filter(Boolean);
		const uniqueItems = new Map();

		payloads.forEach((payload) => {
			const visualizacao = payload?.visualizacao || payload?.grade || {};
			const setorContexto = payload?.contexto?.setor || {};
			const flatItems = Array.isArray(payload?.itens)
				? payload.itens
				: [];

			flatItems.forEach((item) => {
				const itemId = resolveEscalaItemId(item);
				if (itemId === null) return;
				uniqueItems.set(String(itemId), {
					...item,
					setorSolicitacao: item?.setorSolicitacao || setorContexto,
				});
			});

			(visualizacao?.setores || []).forEach((setor = {}) => {
				(setor?.linhas || []).forEach((linha = {}) => {
					(linha?.celulas || []).forEach((celula = {}) => {
						(celula?.cards || []).forEach((item = {}) => {
							const itemId = resolveEscalaItemId(item);
							if (itemId === null) return;
							uniqueItems.set(String(itemId), {
								...item,
								data:
									item?.data ||
									item?.dados_edicao?.data ||
									celula?.data ||
									'',
								hora_inicio:
									item?.horario?.hora_inicio ||
									item?.dados_edicao?.horario?.hora_inicio ||
									item?.edicao?.horario?.hora_inicio ||
									item?.hora_inicio ||
									item?.dados_edicao?.hora_inicio ||
									item?.resumo?.hora_inicio ||
									celula?.hora_inicio ||
									linha?.faixa_horaria?.hora_inicio ||
									'',
								hora_fim:
									item?.horario?.hora_fim ||
									item?.dados_edicao?.horario?.hora_fim ||
									item?.edicao?.horario?.hora_fim ||
									item?.hora_fim ||
									item?.dados_edicao?.hora_fim ||
									item?.resumo?.hora_fim ||
									celula?.hora_fim ||
									linha?.faixa_horaria?.hora_fim ||
									'',
								profissionalSolicitacaoId:
									item?.profissional?.id ||
									item?.profissional_id ||
									linha?.profissional?.id ||
									linha?.vinculo_profissional
										?.profissional_id ||
									linha?.vinculo_profissional?.profissional
										?.id ||
									'',
								setorSolicitacao: setor,
							});
						});
					});
				});
			});
		});

		return Array.from(uniqueItems.values())
			.filter((item) => {
				const tipo = normalizeItemType(
					item?.tipo_item || item?.dados_edicao?.tipo_item || '',
				);
				if (tipo === 'vacancia' || !isFutureScaleItem(item)) {
					return false;
				}

				const itemProfessionalIds = [
					item?.profissional?.id,
					typeof item?.profissional !== 'object'
						? item?.profissional
						: '',
					item?.profissional_id,
					item?.dados_edicao?.profissional?.id,
					typeof item?.dados_edicao?.profissional !== 'object'
						? item?.dados_edicao?.profissional
						: '',
					item?.dados_edicao?.profissional_id,
					item?.edicao?.profissional?.id,
					item?.edicao?.profissional_id,
					item?.vinculo_profissional?.profissional_id,
					item?.vinculo_profissional?.profissional?.id,
					item?.profissionalSolicitacaoId,
				]
					.map((value) => String(value || '').trim())
					.filter(Boolean);

				return itemProfessionalIds.includes(professionalId);
			})
			.map((item) => {
				const itemId = String(resolveEscalaItemId(item) || '');
				const data = item?.data || item?.dados_edicao?.data || '';
				const turno = formatarFaixaTroca(item);

				return {
					value: itemId,
					label: [formatarDataCurta(data), turno]
						.filter(Boolean)
						.join(' - '),
					item,
				};
			})
			.sort((a, b) => a.label.localeCompare(b.label, 'pt-BR'));
	}, [
		gradeEspecialidadePayloads,
		gradePayload,
		formatarDataCurta,
		formatarFaixaTroca,
		solicitacaoTrocaProfissionalId,
	]);

	const getSolicitacaoTrocaProfessionalKey = useCallback(
		(item = {}) =>
			String(
				item?.profissional?.id ||
					item?.profissional ||
					item?.dados_edicao?.profissional?.id ||
					item?.dados_edicao?.profissional ||
					item?.profissional_id ||
					item?.nome_profissional ||
					item?.resumo?.titulo ||
					'',
			).trim(),
		[],
	);

	const solicitacaoTrocaProfissionais = useMemo(() => {
		const options = new Map();

		solicitacaoTrocaCandidatos.forEach((item) => {
			if (!isFutureScaleItem(item)) return;
			const value = getSolicitacaoTrocaProfessionalKey(item);
			const label = String(
				item?.nome_profissional ||
					item?.dados_edicao?.nome ||
					item?.dados_edicao?.nome_profissional ||
					item?.profissional?.nome ||
					item?.resumo?.titulo ||
					'',
			).trim();
			if (!value || !label || options.has(value)) return;
			options.set(value, { value, label });
		});

		return Array.from(options.values());
	}, [getSolicitacaoTrocaProfessionalKey, solicitacaoTrocaCandidatos]);

	useEffect(() => {
		if (
			!showSolicitacaoTrocaModal ||
			solicitacaoTrocaLoading ||
			!solicitacaoTrocaProfissionalId ||
			solicitacaoTrocaOrigens.length > 0
		) {
			return;
		}

		setSolicitacaoTrocaFeedback({
			type: 'error',
			message: 'Não foram encontradas escalas com o seu perfil',
		});
	}, [
		showSolicitacaoTrocaModal,
		solicitacaoTrocaLoading,
		solicitacaoTrocaOrigens.length,
		solicitacaoTrocaProfissionalId,
	]);

	const solicitacaoTrocaItensProfissional = useMemo(() => {
		if (!solicitacaoTrocaForm.trocaCom) return [];

		return solicitacaoTrocaCandidatos.filter(
			(item) =>
				isFutureScaleItem(item) &&
				getSolicitacaoTrocaProfessionalKey(item) ===
					String(solicitacaoTrocaForm.trocaCom),
		);
	}, [
		getSolicitacaoTrocaProfessionalKey,
		solicitacaoTrocaCandidatos,
		solicitacaoTrocaForm.trocaCom,
	]);

	const solicitacaoTrocaDatas = useMemo(() => {
		if (!solicitacaoTrocaForm.trocaCom) return [];
		const options = new Map();

		solicitacaoTrocaItensProfissional.forEach((item) => {
			const data = String(item?.data || item?.dados_edicao?.data || '');
			if (!data) return;

			const turno = formatarFaixaTroca(item);
			const option = options.get(data) || {
				value: data,
				turnos: new Set(),
			};
			if (turno && turno !== 'Horário não informado') {
				option.turnos.add(turno);
			}
			options.set(data, option);
		});

		return Array.from(options.values()).map(({ turnos, ...option }) => ({
			...option,
			label: [
				formatarDataCurta(option.value),
				Array.from(turnos).join(' / '),
			]
				.filter(Boolean)
				.join(' - '),
		}));
	}, [
		formatarDataCurta,
		formatarFaixaTroca,
		solicitacaoTrocaItensProfissional,
		solicitacaoTrocaForm.trocaCom,
	]);

	const solicitacaoTrocaTurnos = useMemo(() => {
		if (!solicitacaoTrocaForm.trocaCom || !solicitacaoTrocaForm.data) {
			return [];
		}

		const options = new Map();

		solicitacaoTrocaItensProfissional
			.filter(
				(item) =>
					String(item?.data || item?.dados_edicao?.data || '') ===
					String(solicitacaoTrocaForm.data),
			)
			.forEach((item) => {
				const value = String(resolveEscalaItemId(item) || '');
				if (!value || options.has(value)) return;

				options.set(value, {
					value,
					label: formatarFaixaTroca(item),
				});
			});

		return Array.from(options.values()).sort((a, b) =>
			a.label.localeCompare(b.label, 'pt-BR'),
		);
	}, [
		formatarFaixaTroca,
		solicitacaoTrocaItensProfissional,
		solicitacaoTrocaForm.data,
		solicitacaoTrocaForm.trocaCom,
	]);

	const resolveProfissionalIdParaEdicao = useCallback(
		(formularioAtual = null, itemSnapshot = editingItemSnapshot) => {
			const itemFormulario = formularioAtual?.item || {};
			const profissionalFormulario = itemFormulario?.profissional || {};
			const profissionalPorCpfFormulario =
				itemFormulario?.profissional_por_cpf || {};
			const dadosSnapshot = itemSnapshot?.dados_edicao || {};
			const candidatos = [
				currentSearch?.[0]?.id,
				currentSearch?.[0]?.profissional_id,
				currentSearch?.[0]?.profissional,
				itemFormulario?.profissional_id,
				itemFormulario?.profissional,
				profissionalFormulario?.id,
				profissionalFormulario?.profissional_id,
				profissionalFormulario?.profissional,
				profissionalPorCpfFormulario?.id,
				profissionalPorCpfFormulario?.profissional_id,
				profissionalPorCpfFormulario?.profissional,
				itemSnapshot?.profissional_id,
				itemSnapshot?.profissional,
				dadosSnapshot?.profissional_id,
				dadosSnapshot?.profissional,
			];

			const encontrado = candidatos.find((value) => {
				const numero = Number(value);
				return Number.isFinite(numero) && numero > 0;
			});

			return encontrado ? Number(encontrado) : null;
		},
		[currentSearch, editingItemSnapshot],
	);

	const carregarGradeProfissionalParaEdicao = useCallback(
		async ({
			formularioAtual,
			itemSnapshot = editingItemSnapshot,
			targetEstabelecimento,
			targetRegime,
			targetMesReferencia,
		}) => {
			const profissionalId = resolveProfissionalIdParaEdicao(
				formularioAtual,
				itemSnapshot,
			);

			/* if (!profissionalId) {
				setEditingProfessionalGradeItems([]);
				setEditingProfessionalGradeDays([]);
				return {
					error: 'Não foi possível identificar o profissional para consultar a grade da edição.',
				};
			} */

			const mesMatch = String(targetMesReferencia || '').match(
				/^(\d{4})-(\d{2})/,
			);
			const anoBusca =
				Number(mesMatch?.[1] || searchFilters.anoVigente || 0) ||
				undefined;
			const mesBusca =
				Number(mesMatch?.[2] || searchFilters.mesVigente || 0) ||
				undefined;
			const setorBusca =
				Number(
					formularioAtual?.contexto_competencia?.setor_id ||
						formularioAtual?.defaults?.contexto_competencia
							?.setor_id ||
						itemSnapshot?.dados_edicao?.setor ||
						currentPro.setor ||
						searchFilters.setor ||
						gradePayload?.contexto?.setor?.id ||
						0,
				) || undefined;

			const response = await requestBuscarGradeEscala({
				token,
				estabelecimento: targetEstabelecimento,
				setor: setorBusca,
				regime: targetRegime,
				mes: mesBusca,
				ano: anoBusca,
				profissionalId,
			});

			if (response?.error) {
				setEditingProfessionalGradeItems([]);
				setEditingProfessionalGradeDays([]);
				return {
					error: getErrorMessage(
						response,
						'Não foi possível consultar os cards do profissional na escala.',
					),
				};
			}

			const itens = Array.isArray(response?.itens) ? response.itens : [];
			setEditingProfessionalGradeItems(itens);
			setEditingProfessionalGradeDays(extractEscalaItemDates(itens));

			return { itens };
		},
		[
			currentPro.setor,
			editingItemSnapshot,
			gradePayload?.contexto?.setor?.id,
			resolveProfissionalIdParaEdicao,
			searchFilters.anoVigente,
			searchFilters.mesVigente,
			searchFilters.setor,
			token,
		],
	);

	const carregarOcorrenciasPeriodoProfissional = useCallback(
		async ({ dataInicio, dataFim }) => {
			if (!isPopupEditing) {
				return { ocorrencias: null };
			}

			const itensOrigem =
				Array.isArray(editingProfessionalGradeItems) &&
				editingProfessionalGradeItems.length > 0
					? editingProfessionalGradeItems
					: editingItemSnapshot
						? [editingItemSnapshot]
						: [];

			const itensComHorario = itensOrigem
				.map((item) => ({
					item,
					horario: buildOcorrenciaFromEscalaItem(item),
				}))
				.filter(
					({ horario }) =>
						horario &&
						isDateWithinPeriod(horario.data, dataInicio, dataFim),
				)
				.reduce((acc, entry) => {
					const itemId = resolveEscalaItemId(entry.item);

					if (
						itemId !== null &&
						itemId !== undefined &&
						acc.some(
							(existingEntry) =>
								String(
									resolveEscalaItemId(existingEntry.item),
								) === String(itemId),
						)
					) {
						return acc;
					}

					return [...acc, entry];
				}, []);

			const ocorrencias = itensComHorario
				.map(({ horario }) => horario)
				.filter(Boolean);

			if (!ocorrencias.length) {
				return { ocorrencias: null };
			}

			const ocorrenciasUnicas = ocorrencias.reduce((acc, ocorrencia) => {
				const chave = `${ocorrencia.data}-${ocorrencia.dia_semana}-${ocorrencia.hora_inicio}-${ocorrencia.hora_fim}`;

				if (acc.some((item) => item.chave === chave)) {
					return acc;
				}

				return [
					...acc,
					{
						chave,
						data: ocorrencia.data,
						dia_semana: ocorrencia.dia_semana,
						hora_inicio: ocorrencia.hora_inicio,
						hora_fim: ocorrencia.hora_fim,
					},
				];
			}, []);

			return {
				cards: itensComHorario,
				dias: extractEscalaItemDates(
					itensComHorario.map(({ item }) => item),
				),
				ocorrencias: ocorrenciasUnicas.map(
					({ chave, ...ocorrencia }) => ({
						dia_semana: ocorrencia.dia_semana,
						hora_inicio: ocorrencia.hora_inicio,
						hora_fim: ocorrencia.hora_fim,
					}),
				),
			};
		},
		[editingItemSnapshot, editingProfessionalGradeItems, isPopupEditing],
	);

	const resetTrocaState = useCallback(() => {
		setTrocaCandidatos([]);
		setCarregandoTroca(false);
		setCurrentPro((prevState) => ({
			...prevState,
			trocaCom: '',
			trocaComNome: '',
			trocaData: '',
			trocaHora: '',
			trocaItemDestinoId: '',
		}));
	}, []);

	const carregarCandidatosTroca = useCallback(
		async (item) => {
			const itemId = resolveEscalaItemId(item);

			if (itemId === null) {
				setPopupSubmitError(
					'Não foi possível identificar o card de origem para troca.',
				);
				return false;
			}

			setCarregandoTroca(true);
			setPopupSubmitError('');

			const response = await requestConsultarTrocaEscala({
				token,
				id: itemId,
			});

			if (response?.error) {
				setCarregandoTroca(false);
				setTrocaCandidatos([]);
				setPopupSubmitError(
					getErrorMessage(
						response,
						'Não foi possível carregar os candidatos para troca.',
					),
				);
				return false;
			}

			const candidatos = Array.isArray(response?.candidatos)
				? response.candidatos.filter((candidato) => {
						const candidatoId = resolveEscalaItemId(candidato);
						const tipoCandidato = normalizeItemType(
							candidato?.tipo_item ||
								candidato?.dados_edicao?.tipo_item ||
								'',
						);
						const nomeProfissional = String(
							candidato?.nome_profissional ||
								candidato?.dados_edicao?.nome ||
								candidato?.dados_edicao?.nome_profissional ||
								candidato?.resumo?.titulo ||
								'',
						).trim();

						return (
							candidatoId !== null &&
							String(candidatoId) !== String(itemId) &&
							tipoCandidato !== 'vacancia' &&
							nomeProfissional !== ''
						);
					})
				: [];

			setCarregandoTroca(false);
			setTrocaCandidatos(candidatos);

			if (!Array.isArray(candidatos) || !candidatos.length) {
				setPopupSubmitError(
					'Nenhum candidato foi encontrado para esta troca.',
				);
			}

			return true;
		},
		[token],
	);

	const handleTrocaInputChange = (fieldName, value) => {
		clearFieldError(fieldName);
		setPopupSubmitError('');

		setCurrentPro((prevState) => {
			if (fieldName === 'trocaCom') {
				const profissionalSelecionado =
					profissionaisTrocaDisponiveis.find(
						(item) => String(item.value) === String(value),
					);

				return {
					...prevState,
					trocaCom: value,
					trocaComNome: profissionalSelecionado?.nome || '',
					trocaData: '',
					trocaHora: '',
					trocaItemDestinoId: '',
				};
			}

			if (fieldName === 'trocaData') {
				return {
					...prevState,
					trocaData: value,
					trocaHora: '',
					trocaItemDestinoId: '',
				};
			}

			if (fieldName === 'trocaHora') {
				return {
					...prevState,
					trocaHora: value,
					trocaItemDestinoId: value,
				};
			}

			return {
				...prevState,
				[fieldName]: value,
			};
		});
	};

	const resetSolicitacaoTrocaModal = () => {
		setSolicitacaoTrocaForm({
			origemId: '',
			trocaCom: '',
			data: '',
			turnoId: '',
			justificativa: '',
		});
		setSolicitacaoTrocaCandidatos([]);
		setSolicitacaoTrocaProfissionalId('');
		setSolicitacaoTrocaLoading(false);
		setSolicitacaoTrocaFeedback({ type: '', message: '' });
		setSolicitacaoTrocaEnviada(false);
	};

	const abrirModalSolicitacaoTroca = async () => {
		resetSolicitacaoTrocaModal();
		setShowSolicitacaoTrocaModal(true);
		setSolicitacaoTrocaLoading(true);

		const userData = (() => {
			try {
				return JSON.parse(Cookies.get('user') || '{}');
			} catch (_error) {
				return {};
			}
		})();
		const normalizeCpfValue = (value = '') =>
			String(value || '').replace(/\D/g, '');
		const userCpf = normalizeCpfValue(
			userData?.cpf || userData?.userName || userData?.username || '',
		);
		const estabelecimento = String(
			userCnes ||
				userData?.cnes ||
				userData?.cnes_estabelecimento ||
				userData?.cnesEstabelecimento ||
				searchFilters.unidade ||
				appliedFilters.unidade ||
				'',
		).trim();

		if (!token || !userCpf || !estabelecimento) {
			setSolicitacaoTrocaLoading(false);
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message: 'Não foram encontradas escalas com o seu perfil',
			});
			return;
		}

		const profissionais = await requestBuscarProfissionalEscala({
			token,
			cpf: userCpf,
			estabelecimento,
			scope: 'enfermagem',
		});

		const profissional = Array.isArray(profissionais)
			? profissionais.find((item) => {
					const itemCpf = normalizeCpfValue(
						item?.cpf ||
							item?.username ||
							item?.userName ||
							item?.profissional?.cpf ||
							'',
					);
					return item?.id && (!itemCpf || itemCpf === userCpf);
				}) ||
				profissionais.find((item) => item?.id) ||
				null
			: null;

		if (profissionais?.error || !profissional?.id) {
			setSolicitacaoTrocaLoading(false);
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message: 'Não foram encontradas escalas com o seu perfil',
			});
			return;
		}

		setSolicitacaoTrocaProfissionalId(String(profissional.id));
		setSolicitacaoTrocaLoading(false);
	};

	const fecharModalSolicitacaoTroca = () => {
		setShowSolicitacaoTrocaModal(false);
		resetSolicitacaoTrocaModal();
	};

	useEffect(() => {
		if (!showSolicitacaoTrocaModal || !solicitacaoTrocaEnviada) return;

		const timeoutId = setTimeout(() => {
			setShowSolicitacaoTrocaModal(false);
			resetSolicitacaoTrocaModal();
		}, 10000);

		return () => clearTimeout(timeoutId);
	}, [showSolicitacaoTrocaModal, solicitacaoTrocaEnviada]);

	const handleSolicitacaoTrocaOrigemChange = async (origemId) => {
		setSolicitacaoTrocaForm((prevState) => ({
			...prevState,
			origemId,
			trocaCom: '',
			data: '',
			turnoId: '',
		}));
		setSolicitacaoTrocaCandidatos([]);
		setSolicitacaoTrocaFeedback({ type: '', message: '' });

		if (!origemId) return;

		setSolicitacaoTrocaLoading(true);
		const response = await requestConsultarTrocaEscala({
			token,
			id: origemId,
			scope: 'medica',
		});

		if (response?.error) {
			setSolicitacaoTrocaLoading(false);
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message: getErrorMessage(
					response,
					'Não foi possível carregar os candidatos para troca.',
				),
			});
			return;
		}

		const candidatos = Array.isArray(response?.candidatos)
			? response.candidatos.filter((candidato) => {
					const candidatoId = resolveEscalaItemId(candidato);
					const tipoCandidato = normalizeItemType(
						candidato?.tipo_item ||
							candidato?.dados_edicao?.tipo_item ||
							'',
					);
					const nomeProfissional = String(
						candidato?.nome_profissional ||
							candidato?.dados_edicao?.nome ||
							candidato?.dados_edicao?.nome_profissional ||
							candidato?.profissional?.nome ||
							candidato?.resumo?.titulo ||
							'',
					).trim();

					return (
						candidatoId !== null &&
						String(candidatoId) !== String(origemId) &&
						tipoCandidato !== 'vacancia' &&
						nomeProfissional !== ''
					);
				})
			: [];

		setSolicitacaoTrocaLoading(false);
		setSolicitacaoTrocaCandidatos(candidatos);

		if (!candidatos.length) {
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message: 'Nenhum candidato foi encontrado para esta troca.',
			});
		}
	};

	const handleSolicitacaoTrocaFieldChange = (fieldName, value) => {
		setSolicitacaoTrocaFeedback({ type: '', message: '' });
		setSolicitacaoTrocaForm((prevState) => {
			if (fieldName === 'trocaCom') {
				return {
					...prevState,
					trocaCom: value,
					data: '',
					turnoId: '',
				};
			}

			if (fieldName === 'data') {
				return {
					...prevState,
					data: value,
					turnoId: '',
				};
			}

			return {
				...prevState,
				[fieldName]: value,
			};
		});
	};

	const handleEnviarSolicitacaoTroca = async () => {
		const camposObrigatoriosPreenchidos =
			solicitacaoTrocaForm.origemId &&
			solicitacaoTrocaForm.trocaCom &&
			solicitacaoTrocaForm.data &&
			solicitacaoTrocaForm.turnoId &&
			solicitacaoTrocaForm.justificativa.trim();

		if (!camposObrigatoriosPreenchidos) {
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message: 'Preencha todos os campos para enviar a solicitação.',
			});
			return;
		}

		setSolicitacaoTrocaLoading(true);
		const response = await requestCriarSolicitacaoTroca({
			token,
			body: {
				modalidade: 'MEDICA',
				item_origem_id: Number(solicitacaoTrocaForm.origemId),
				item_destino_id: Number(solicitacaoTrocaForm.turnoId),
				justificativa: solicitacaoTrocaForm.justificativa.trim(),
			},
		});

		setSolicitacaoTrocaLoading(false);
		if (response?.error) {
			setSolicitacaoTrocaFeedback({
				type: 'error',
				message:
					response.error.status === 429
						? 'Limite de solicitações atingido. Aguarde antes de tentar novamente.'
						: getErrorMessage(
								response,
								'Não foi possível enviar a solicitação.',
							),
			});
			return;
		}

		setSolicitacaoTrocaFeedback({
			type: 'success',
			message: 'Solicitação enviada com sucesso.',
		});
		setSolicitacaoTrocaEnviada(true);
	};

	const refreshGrade = useCallback(
		async (filters) => {
			if (
				!filters ||
				(showUnidadeFilter && !filters.unidade) ||
				!filters.setor ||
				!filters.regime ||
				!filters.mesVigente ||
				!filters.anoVigente ||
				!token
			) {
				if (showUnidadeFilter && !filters?.unidade) {
					setGradePayload(null);
					setGradeMessage(
						'Selecione uma unidade para carregar a escala.',
					);
				}
				return;
			}

			setCarregando(true);
			setOperationError('');
			const estabelecimentoConsulta = showUnidadeFilter
				? filters.unidade || undefined
				: userCnes || filters.unidade || undefined;
			const identificadorSelecionado = String(
				filters.identificador || '',
			).trim();
			const setorSelecionado = Number(filters.setor);
			const response = await requestBuscarGradeEscala({
				token,
				estabelecimento: estabelecimentoConsulta,
				tipoSecao:
					Number(filters.tipoSetor || filters.servico || 0) ||
					undefined,
				identificador: identificadorSelecionado || undefined,
				setor:
					!identificadorSelecionado &&
					Number.isFinite(setorSelecionado)
						? setorSelecionado
						: undefined,
				nomeProfissional:
					String(filtroNomeProfissional || '').trim() || undefined,
				regime: String(filters.regime).toUpperCase(),
				mes: Number(filters.mesVigente),
				ano: Number(filters.anoVigente),
			});

			if (response?.error) {
				const errorMessage = getErrorMessage(
					response,
					'Não foi possível carregar a grade para os filtros selecionados.',
				);

				const status = response?.error?.status;
				const code = String(response?.error?.code || '').toLowerCase();
				const normalizedDetail = String(errorMessage).toLowerCase();
				const noScaleFound =
					status === 404 ||
					code.includes('nao_encontr') ||
					code.includes('not_found') ||
					normalizedDetail.includes('nao encontrada') ||
					normalizedDetail.includes('não encontrada') ||
					normalizedDetail.includes('nao encontrado') ||
					normalizedDetail.includes('não encontrado') ||
					normalizedDetail.includes('nao existe') ||
					normalizedDetail.includes('não existe') ||
					normalizedDetail.includes('inexistente');

				if (noScaleFound) {
					setGradePayload(null);
					setGradeMessage(
						'Ainda não existe uma escala com estes filtros.',
					);
					setCarregando(false);
					return;
				}

				setGradePayload(null);
				setGradeMessage(
					`Houve um problema ao carregar a escala existente: ${errorMessage}`,
				);
				setCarregando(false);
				return;
			}

			if (
				!Array.isArray(response?.visualizacao?.setores) &&
				!Array.isArray(response?.grade?.setores)
			) {
				setGradePayload(null);
				setGradeMessage(
					'Houve um problema ao carregar a escala existente: resposta da API sem visualização válida.',
				);
				setCarregando(false);
				return;
			}

			setGradePayload(response);
			setGradeMessage('');
			const setorCanonico =
				response?.contexto?.setor?.id ||
				response?.filtros_aplicados?.setor ||
				response?.visualizacao?.setor_inicial_id ||
				response?.grade?.setor_inicial_id ||
				'';
			const identificadorCanonico =
				response?.contexto?.identificador ||
				response?.filtros_aplicados?.identificador ||
				filters.identificador ||
				'';
			if (setorCanonico || identificadorCanonico) {
				setSearchFilters((prevState) => ({
					...prevState,
					setor: setorCanonico
						? String(setorCanonico)
						: prevState.setor,
					identificador: identificadorCanonico
						? String(identificadorCanonico)
						: prevState.identificador,
				}));
			}
			setCarregando(false);
		},
		[token, userCnes, showUnidadeFilter, filtroNomeProfissional],
	);

	const refreshGradeEspecialidade = useCallback(
		async (filters) => {
			if (
				!filters ||
				(showUnidadeFilter && !filters.unidade) ||
				!filters.especialidade ||
				!filters.regime ||
				!filters.mesVigente ||
				!filters.anoVigente ||
				!token
			) {
				if (showUnidadeFilter && !filters?.unidade) {
					setGradeEspecialidadePayloads([]);
					setGradeEspecialidadeMessage(
						'Selecione uma unidade para carregar a escala.',
					);
				}
				return;
			}

			setCarregando(true);
			setOperationError('');
			const estabelecimentoConsulta = showUnidadeFilter
				? filters.unidade || undefined
				: userCnes || filters.unidade || undefined;

			const regimeConsulta = String(filters.regime || '').toUpperCase();
			const regimesConsulta =
				regimeConsulta === 'TODOS'
					? ['PLANTONISTA', 'ROTINA']
					: [regimeConsulta];
			const responses = await Promise.all(
				regimesConsulta.map((regimeItem) =>
					requestBuscarGradeEspecialidadeEscala({
						token,
						estabelecimento: estabelecimentoConsulta,
						especialidade: filters.especialidade
							? Number(filters.especialidade)
							: undefined,
						regime: regimeItem,
						mes: Number(filters.mesVigente),
						ano: Number(filters.anoVigente),
					}),
				),
			);
			const response = responses.find((item) => item?.error) || responses[0];

			if (response?.error) {
				const errorMessage = getErrorMessage(
					response,
					'Não foi possível carregar as grades para a área de atuação selecionada.',
				);
				setGradeEspecialidadePayloads([]);
				setGradeEspecialidadeMessage(
					`Houve um problema ao carregar as escalas: ${errorMessage}`,
				);
				setCarregando(false);
				return;
			}

			const calendarios = responses.flatMap((item) =>
				Array.isArray(item?.calendarios) ? item.calendarios : [],
			);
			if (calendarios.length === 0) {
				setGradeEspecialidadePayloads([]);
				setGradeEspecialidadeMessage(
					'Nenhuma escala encontrada para a área de atuação selecionada.',
				);
				setCarregando(false);
				return;
			}

			setGradeEspecialidadePayloads(calendarios);
			setGradeEspecialidadeMessage('');
			setCarregando(false);
		},
		[token, userCnes, showUnidadeFilter],
	);

	const buildItemPayload = ({
		targetScaleId = null,
		targetEstabelecimento,
		targetRegime,
		targetMesReferencia,
		formularioAssistivo,
		forceBatchCreate = false,
		forcedRecorrenciaPayload = null,
	}) => {
		const isEditing = isPopupEditing;
		const tipo = (popupStatus || 'alocacao').toUpperCase();
		const formularioAtual = formularioAssistivo || popupFormularioAssistivo;
		const itemFormulario = formularioAtual?.item || {};
		const defaults = formularioAtual?.defaults || {};
		const profissionalFormulario =
			itemFormulario?.profissional_por_cpf ||
			itemFormulario?.profissional ||
			defaults?.profissional_por_cpf ||
			defaults?.profissional ||
			{};
		const contextoFormulario =
			formularioAtual?.contexto_competencia ||
			defaults?.contexto_competencia ||
			{};

		// Para criação, sempre usa o mês/ano real (Brasil) — nunca o filtro de busca
		const _hoje = new Date();
		const _mesHoje = String(_hoje.getMonth() + 1).padStart(2, '0');
		const _anoHoje = String(_hoje.getFullYear());
		const mesReferenciaHoje = `${_anoHoje}-${_mesHoje}-01`;

		const mesReferencia = isEditing
			? String(
					targetMesReferencia ||
						contextoFormulario?.mes_referencia ||
						getMesReferenciaAtual(),
				) || getMesReferenciaAtual()
			: mesReferenciaHoje;

		const diasComTurno = (currentPro.dias || []).filter((dia) => {
			const turno = currentPro.turnos?.[dia];
			return turno?.inicio && turno?.fim;
		});
		const primeiroDiaComTurno =
			diasComTurno[0] || currentPro.dias?.[0] || '';
		const turnoReferencia = currentPro.turnos?.[primeiroDiaComTurno] || {};
		const horarioReferencia = {
			hora_inicio: formatarHoraPayload(turnoReferencia.inicio),
			hora_fim: formatarHoraPayload(turnoReferencia.fim),
		};
		const periodoPadraoMes = getPeriodoPadraoMes(mesReferencia);
		const dataInicio =
			popupExtraFields.dataInicio ||
			(tipo === 'VACANCIA'
				? popupExtraFields.dataInicioVacancia || ''
				: '') ||
			defaults?.periodo?.data_inicio ||
			defaults?.horario?.data ||
			periodoPadraoMes.dataInicio;
		const dataFim =
			popupExtraFields.dataFim ||
			(tipo === 'VACANCIA'
				? popupExtraFields.dataInicioVacancia || ''
				: '') ||
			defaults?.periodo?.data_fim ||
			defaults?.horario?.data ||
			periodoPadraoMes.dataFim;
		const dataInicioVacancia =
			popupExtraFields.dataInicioVacancia ||
			defaults?.data_inicio_vacancia ||
			defaults?.periodo?.data_inicio ||
			defaults?.horario?.data ||
			periodoPadraoMes.dataInicio;
		const diasSemana = diasComTurno
			.map((dia) => toDiaSemanaNumero(dia))
			.filter(Boolean);
		const ocorrenciasRecorrencia = buildRecorrenciaOcorrencias(
			diasComTurno,
			currentPro.turnos,
		);
		const possuiHorariosDiferentes =
			new Set(
				ocorrenciasRecorrencia.map(
					(ocorrencia) =>
						`${ocorrencia.hora_inicio}-${ocorrencia.hora_fim}`,
				),
			).size > 1;
		const regimeNormalizado = String(
			targetRegime || contextoFormulario?.regime || popupRegime || '',
		).toUpperCase();
		const profissionalSelecionado = currentSearch[0] || {};
		const profissionalId = Number(
			profissionalSelecionado?.id ||
				profissionalSelecionado?.profissional_id ||
				profissionalSelecionado?.profissional ||
				itemFormulario?.profissional_id ||
				itemFormulario?.profissional ||
				profissionalFormulario?.id ||
				profissionalFormulario?.profissional_id ||
				profissionalFormulario?.profissional ||
				0,
		);
		const vinculoProfissionalId = Number(
			profissionalSelecionado?.vinculo_id ||
				profissionalSelecionado?.vinculoId ||
				profissionalSelecionado?.vinculo?.id ||
				profissionalSelecionado?.vinculos?.[0]?.id ||
				profissionalFormulario?.vinculo_id ||
				profissionalFormulario?.vinculoId ||
				profissionalFormulario?.vinculo?.id ||
				0,
		);
		const vinculoTipoId = Number(
			currentPro.vinculo ||
				profissionalSelecionado?.vinculo_tipo ||
				profissionalSelecionado?.vinculoTipo ||
				profissionalFormulario?.vinculo_tipo_id ||
				profissionalFormulario?.vinculo_tipo ||
				defaults?.profissional?.vinculo_tipo ||
				defaults?.profissional_por_cpf?.vinculo_tipo_id ||
				0,
		);
		const setorId = Number(
			currentPro.setor ||
				contextoFormulario?.setor_id ||
				searchFilters.setor ||
				0,
		);
		const numeroConselhoPayload =
			String(
				currentPro.numeroConselho ||
					getNumeroConselhoFromProfissional(
						profissionalSelecionado,
					) ||
					getNumeroConselhoFromProfissional(profissionalFormulario) ||
					getNumeroConselhoFromProfissional(defaults?.profissional) ||
					getNumeroConselhoFromProfissional(
						defaults?.profissional_por_cpf,
					) ||
					'',
			)
				.replace(/\D/g, '')
				.slice(0, 20) || null;
		const ufConselhoPayload =
			normalizeUfSigla(
				currentPro.ufConselho ||
					getUfConselhoFromProfissional(profissionalSelecionado) ||
					getUfConselhoFromProfissional(profissionalFormulario) ||
					getUfConselhoFromProfissional(defaults?.profissional) ||
					getUfConselhoFromProfissional(
						defaults?.profissional_por_cpf,
					) ||
					'',
			) || null;
		const basePayload = {
			tipo_item: tipo,
			observacao:
				popupExtraFields.observacao || defaults?.observacao || '',
		};

		if (tipo === 'VACANCIA') {
			basePayload.data_inicio_vacancia = dataInicioVacancia || undefined;
		}

		if (!isEditing) {
			basePayload.contexto_competencia = {
				setor_id: setorId || undefined,
				regime: regimeNormalizado,
				mes_referencia: mesReferencia,
			};
		}

		if (tipo !== 'VACANCIA') {
			const cargoPayload = resolveCargoValue(
				currentPro.cargo,
				profissionalFormulario?.cargo,
				profissionalSelecionado?.cargo,
				defaults?.profissional?.cargo,
				defaults?.profissional_por_cpf?.cargo,
			);
			const especialidadeId = resolveEspecialidadeSelectValue(
				currentSearch[0]?.especialidade_id,
				currentSearch[0]?.especialidade,
				currentPro.especialidade,
				profissionalFormulario?.especialidade_id,
				defaults?.profissional_por_cpf?.especialidade_id,
			);
			const vinculoNome =
				vinculoOptions.find(
					(option) => String(option?.id) === String(vinculoTipoId),
				)?.nome ||
				profissionalFormulario?.nome_vinculo ||
				profissionalFormulario?.vinculo_nome ||
				profissionalSelecionado?.nome_vinculo ||
				profissionalSelecionado?.descricao_vinculo ||
				profissionalSelecionado?.vinculo_nome ||
				'';
			const nomeEspecialidade =
				especialidadeOptions.find(
					(option) => String(option?.id) === String(especialidadeId),
				)?.nome ||
				profissionalFormulario?.nome_especialidade ||
				profissionalFormulario?.especialidade_nome ||
				profissionalFormulario?.especialidade ||
				profissionalSelecionado?.nome_especialidade ||
				profissionalSelecionado?.especialidade_nome ||
				profissionalSelecionado?.especialidade ||
				'';
			const profissionalPayload = {
				id: profissionalId || null,
				origem:
					profissionalSelecionado?.origem ||
					profissionalSelecionado?.origem_dados ||
					'CNES',
				data_admissao:
					currentPro.admissao ||
					profissionalFormulario?.admissao ||
					profissionalFormulario?.data_admissao ||
					profissionalSelecionado?.admissao ||
					profissionalSelecionado?.data_admissao ||
					null,
				cpf:
					String(
						cpf ||
							profissionalSelecionado?.cpf ||
							profissionalFormulario?.cpf ||
							'',
					).replace(/\D/g, '') || null,
				nome:
					currentSearch[0]?.nome ||
					currentPro.nome ||
					profissionalFormulario?.nome ||
					defaults?.nome ||
					null,

				conselho: numeroConselhoPayload,
				numero_registro: numeroConselhoPayload,
				uf_conselho: ufConselhoPayload,
				cargo: cargoPayload,
				telefone:
					String(
						currentPro.telefone ||
							profissionalFormulario?.telefone ||
							profissionalSelecionado?.telefone ||
							'',
					).replace(/\D/g, '') || null,
				carga_horaria_semanal: currentPro.cargaHorariaSemanal
					? Number(currentPro.cargaHorariaSemanal)
					: profissionalFormulario?.carga_horaria_semanal ||
						profissionalSelecionado?.carga_horaria_semanal ||
						null,
				estabelecimento:
					String(
						targetEstabelecimento ||
							profissionalFormulario?.estabelecimento ||
							profissionalSelecionado?.estabelecimento ||
							gradePayload?.contexto?.estabelecimento?.id ||
							'',
					) || null,
				vinculo_estabelecimento_id:
					profissionalFormulario?.vinculo_estabelecimento_id ||
					profissionalFormulario?.vinculoEstabelecimentoId ||
					profissionalSelecionado?.vinculo_estabelecimento_id ||
					profissionalSelecionado?.vinculoEstabelecimentoId ||
					null,
				vinculo_tipo: vinculoTipoId || null,
				nome_vinculo: vinculoNome || null,
				especialidade: especialidadeId ? Number(especialidadeId) : null,
				nome_especialidade: nomeEspecialidade || null,
				ativo:
					typeof profissionalSelecionado?.ativo === 'boolean'
						? profissionalSelecionado.ativo
						: true,
			};
			const cpfInformado = String(cpf || '').replace(/\D/g, '');
			const cpfEncontrado = String(
				profissionalSelecionado?.cpf ||
					profissionalFormulario?.cpf ||
					'',
			).replace(/\D/g, '');
			const profissionalEncontradoViaCpf =
				!isEditing &&
				Array.isArray(currentSearch) &&
				currentSearch.length > 0 &&
				Boolean(cpfInformado) &&
				Boolean(cpfEncontrado) &&
				cpfInformado === cpfEncontrado;
			const profissionalPorCpfPayload = {
				data_admissao:
					currentPro.admissao ||
					profissionalFormulario?.admissao ||
					profissionalFormulario?.data_admissao ||
					profissionalSelecionado?.admissao ||
					profissionalSelecionado?.data_admissao ||
					null,
				especialidade_id: especialidadeId
					? Number(especialidadeId)
					: null,
				telefone:
					String(
						currentPro.telefone ||
							profissionalFormulario?.telefone ||
							profissionalSelecionado?.telefone ||
							'',
					).replace(/\D/g, '') || null,
				uf_conselho: ufConselhoPayload,

				numero_registro: numeroConselhoPayload,
				conselho: numeroConselhoPayload,
				vinculo_tipo_id: vinculoTipoId || null,
				carga_horaria_semanal: currentPro.cargaHorariaSemanal
					? Number(currentPro.cargaHorariaSemanal)
					: profissionalSelecionado?.carga_horaria_semanal || null,
				cpf: cpfInformado || null,
				nome:
					currentSearch[0]?.nome ||
					currentPro.nome ||
					profissionalFormulario?.nome ||
					defaults?.nome ||
					null,
				cargo: cargoPayload,
			};

			if (!isEditing) {
				if (profissionalEncontradoViaCpf) {
					basePayload.profissional_por_cpf =
						profissionalPorCpfPayload;
				} else {
					basePayload.profissional = profissionalPayload;
				}
			} else if (profissionalId) {
				basePayload.profissional = {
					id: profissionalId,
					data_admissao:
						currentPro.admissao ||
						profissionalFormulario?.admissao ||
						profissionalFormulario?.data_admissao ||
						profissionalSelecionado?.admissao ||
						profissionalSelecionado?.data_admissao ||
						null,
					especialidade_id: especialidadeId
						? Number(especialidadeId)
						: null,
					telefone:
						String(
							currentPro.telefone ||
								profissionalFormulario?.telefone ||
								profissionalSelecionado?.telefone ||
								'',
						).replace(/\D/g, '') || null,
					vinculo_id: vinculoProfissionalId || null,
					uf_conselho: ufConselhoPayload,
					cbo:
						profissionalSelecionado?.cbo ||
						profissionalFormulario?.cbo ||
						null,
					numero_registro: numeroConselhoPayload,
					conselho: numeroConselhoPayload,
					vinculo_tipo_id: vinculoTipoId || null,
					carga_horaria_semanal: currentPro.cargaHorariaSemanal
						? Number(currentPro.cargaHorariaSemanal)
						: profissionalSelecionado?.carga_horaria_semanal ||
							null,
					cpf:
						String(
							cpf ||
								profissionalSelecionado?.cpf ||
								profissionalFormulario?.cpf ||
								'',
						).replace(/\D/g, '') || null,
					nome:
						currentSearch[0]?.nome ||
						currentPro.nome ||
						profissionalFormulario?.nome ||
						defaults?.nome ||
						null,
					cargo: cargoPayload,
				};
			} else {
				basePayload.profissional_por_cpf = {
					cpf: String(cpf || '').replace(/\D/g, '') || undefined,
					nome:
						currentSearch[0]?.nome ||
						currentPro.nome ||
						profissionalFormulario?.nome ||
						defaults?.nome ||
						undefined,
					data_admissao:
						currentPro.admissao ||
						profissionalFormulario?.admissao ||
						profissionalFormulario?.data_admissao ||
						profissionalSelecionado?.admissao ||
						undefined,
					especialidade_id: especialidadeId
						? Number(especialidadeId)
						: undefined,
					telefone:
						String(
							currentPro.telefone ||
								profissionalFormulario?.telefone ||
								profissionalSelecionado?.telefone ||
								'',
						).replace(/\D/g, '') || undefined,
					uf_conselho: ufConselhoPayload || undefined,
					cbo:
						profissionalSelecionado?.cbo ||
						profissionalFormulario?.cbo ||
						undefined,
					numero_registro: numeroConselhoPayload || undefined,
					conselho: numeroConselhoPayload || undefined,
					vinculo_tipo_id:
						vinculoTipoId ||
						defaults?.profissional_por_cpf?.vinculo_tipo_id ||
						undefined,
					carga_horaria_semanal: currentPro.cargaHorariaSemanal
						? Number(currentPro.cargaHorariaSemanal)
						: profissionalSelecionado?.carga_horaria_semanal ||
							undefined,
					cargo: cargoPayload,
				};
			}
		}

		const shouldEditBatch = isEditing;
		const shouldCreateBatch =
			!isEditing && (Boolean(targetScaleId) || Boolean(forceBatchCreate));
		const recorrenciaPayload =
			forcedRecorrenciaPayload ||
			(possuiHorariosDiferentes
				? {
						ocorrencias:
							ocorrenciasRecorrencia.length > 0
								? ocorrenciasRecorrencia
								: defaults?.recorrencia?.ocorrencias ||
									undefined,
					}
				: {
						dias_semana:
							diasSemana.length > 0
								? diasSemana
								: defaults?.recorrencia?.dias_semana ||
									undefined,
						horario: horarioReferencia,
						ocorrencias:
							defaults?.recorrencia?.ocorrencias || undefined,
					});

		if (shouldEditBatch) {
			return {
				mode: 'edit-batch',
				payload: {
					...basePayload,
					item_referencia_id: editingItemId,
					periodo: {
						data_inicio: dataInicio,
						data_fim: dataFim,
					},
					recorrencia: recorrenciaPayload,
				},
			};
		}

		if (shouldCreateBatch) {
			const batchCreatePayload = {
				...basePayload,
				...(basePayload?.profissional
					? {
							profissional: {
								...basePayload.profissional,
								id: '',
								cbo: '',
								vinculo_estabelecimento_id: '',
							},
						}
					: {}),
			};

			return {
				mode: 'append-batch',
				payload: {
					...batchCreatePayload,
					periodo: {
						data_inicio: dataInicio,
						data_fim: dataFim,
					},
					recorrencia: recorrenciaPayload,
				},
			};
		}

		return {
			mode: 'single',
			payload: {
				...basePayload,
				horario: {
					data: dataInicio,
					...horarioReferencia,
				},
			},
		};
	};

	const buildBatchSelectedItemsPatchPayload = useCallback(
		(payloadAtual = {}, cardsSelecionados = []) => {
			const itemIds = [
				...new Set(
					(cardsSelecionados || [])
						.map((entry) =>
							resolveEscalaItemId(entry?.item || entry),
						)
						.filter(
							(value) =>
								value !== null &&
								value !== undefined &&
								String(value).trim() !== '',
						),
				),
			];

			const horarioReferencia = payloadAtual?.horario || {};
			const ocorrenciaReferencia = Array.isArray(
				payloadAtual?.recorrencia?.ocorrencias,
			)
				? payloadAtual.recorrencia.ocorrencias.find(
						(ocorrencia) =>
							ocorrencia?.hora_inicio && ocorrencia?.hora_fim,
					)
				: null;
			const horarioPatch = {
				...(horarioReferencia?.hora_inicio ||
				ocorrenciaReferencia?.hora_inicio
					? {
							hora_inicio:
								horarioReferencia?.hora_inicio ||
								ocorrenciaReferencia?.hora_inicio,
						}
					: {}),
				...(horarioReferencia?.hora_fim ||
				ocorrenciaReferencia?.hora_fim
					? {
							hora_fim:
								horarioReferencia?.hora_fim ||
								ocorrenciaReferencia?.hora_fim,
						}
					: {}),
			};

			return {
				item_ids: itemIds,
				...(payloadAtual?.tipo_item
					? { tipo_item: payloadAtual.tipo_item }
					: {}),
				...(payloadAtual?.observacao !== undefined
					? { observacao: payloadAtual.observacao }
					: {}),
				...(payloadAtual?.data_inicio_vacancia
					? {
							data_inicio_vacancia:
								payloadAtual.data_inicio_vacancia,
						}
					: {}),
				...(Object.keys(horarioPatch).length > 0
					? { horario: horarioPatch }
					: {}),
				...(payloadAtual?.profissional
					? { profissional: payloadAtual.profissional }
					: {}),
				...(!payloadAtual?.profissional &&
				payloadAtual?.profissional_por_cpf
					? {
							profissional_por_cpf:
								payloadAtual.profissional_por_cpf,
						}
					: {}),
			};
		},
		[],
	);

	const shouldUseSingleProfessionalPatch = useCallback(
		(formularioAtual = null, payloadAtual = {}) => {
			if (!isPopupEditing || popupStatus === 'vacancia') {
				return false;
			}

			const itemFormulario = formularioAtual?.item || {};
			const horarioFormulario = itemFormulario?.horario || {};
			const recorrenciaFormulario =
				formularioAtual?.defaults?.recorrencia || {};
			const profissionalFormulario =
				itemFormulario?.profissional_por_cpf ||
				itemFormulario?.profissional ||
				{};
			const diasRecorrentes = Array.isArray(
				recorrenciaFormulario?.dias_semana,
			)
				? recorrenciaFormulario.dias_semana
						.map((dia) => fromDiaSemanaNumero(dia))
						.filter(Boolean)
				: [];
			const ocorrenciasRecorrentes = Array.isArray(
				recorrenciaFormulario?.ocorrencias,
			)
				? recorrenciaFormulario.ocorrencias.filter(
						(ocorrencia) =>
							ocorrencia?.dia_semana &&
							ocorrencia?.hora_inicio &&
							ocorrencia?.hora_fim,
					)
				: [];
			const horarioRecorrente = recorrenciaFormulario?.horario || {};
			const diasRecorrentesPorOcorrencia = ocorrenciasRecorrentes
				.map((ocorrencia) =>
					fromDiaSemanaNumero(ocorrencia?.dia_semana),
				)
				.filter(Boolean);
			const turnosRecorrentesPorOcorrencia = buildTurnosFromOcorrencias(
				ocorrenciasRecorrentes,
			);
			const tipoOriginal = normalizeItemType(
				itemFormulario?.tipo_item ||
					editingItemSnapshot?.tipo_item ||
					popupStatus ||
					'',
			);
			const tipoAtual = normalizeItemType(popupStatus || '');
			const observacaoOriginal = normalizeComparableString(
				formularioAtual?.defaults?.observacao ||
					itemFormulario?.observacao ||
					editingItemSnapshot?.observacao ||
					editingItemSnapshot?.resumo?.observacao ||
					'',
			);
			const dataOriginal = normalizeIsoDate(
				itemFormulario?.data_inicio_vacancia ||
					formularioAtual?.defaults?.data_inicio_vacancia ||
					formularioAtual?.defaults?.periodo?.data_inicio ||
					horarioFormulario?.data ||
					formularioAtual?.defaults?.horario?.data ||
					'',
			);
			const dataFimOriginal = normalizeIsoDate(
				formularioAtual?.defaults?.periodo?.data_fim ||
					horarioFormulario?.data ||
					formularioAtual?.defaults?.horario?.data ||
					'',
			);
			const diasOriginais =
				diasRecorrentesPorOcorrencia.length > 0
					? diasRecorrentesPorOcorrencia
					: diasRecorrentes;
			const turnosOriginais =
				Object.keys(turnosRecorrentesPorOcorrencia).length > 0
					? turnosRecorrentesPorOcorrencia
					: diasRecorrentes.length > 0
						? diasRecorrentes.reduce((acc, dia) => {
								acc[dia] = {
									inicio: String(
										horarioRecorrente?.hora_inicio || '',
									).slice(0, 2),
									fim: String(
										horarioRecorrente?.hora_fim || '',
									).slice(0, 2),
								};
								return acc;
							}, {})
						: {};
			const setorOriginal = normalizeComparableString(
				formularioAtual?.contexto_competencia?.setor_id ||
					formularioAtual?.defaults?.contexto_competencia?.setor_id ||
					itemFormulario?.setor ||
					editingItemSnapshot?.dados_edicao?.setor ||
					gradePayload?.contexto?.setor?.id ||
					searchFilters.setor ||
					'',
			);

			const structuralChanged =
				tipoOriginal !== tipoAtual ||
				observacaoOriginal !==
					normalizeComparableString(payloadAtual?.observacao || '') ||
				dataOriginal !==
					normalizeIsoDate(
						popupExtraFields.dataInicioVacancia ||
							popupExtraFields.dataInicio ||
							'',
					) ||
				dataFimOriginal !==
					normalizeIsoDate(
						popupExtraFields.dataFim ||
							popupExtraFields.dataInicioVacancia ||
							'',
					) ||
				JSON.stringify(normalizeComparableList(diasOriginais)) !==
					JSON.stringify(
						normalizeComparableList(currentPro.dias || []),
					) ||
				JSON.stringify(normalizeComparableTurnos(turnosOriginais)) !==
					JSON.stringify(
						normalizeComparableTurnos(currentPro.turnos || {}),
					) ||
				setorOriginal !==
					normalizeComparableString(currentPro.setor || '');

			const profissionalOriginal = {
				nome: normalizeComparableString(
					profissionalFormulario?.nome || itemFormulario?.nome || '',
				),
				cargo: normalizeComparableString(
					profissionalFormulario?.cargo ||
						itemFormulario?.cargo ||
						'',
				),
				admissao: normalizeIsoDate(
					profissionalFormulario?.data_admissao ||
						itemFormulario?.data_admissao ||
						'',
				),
				telefone: normalizeComparableDigits(
					profissionalFormulario?.telefone ||
						itemFormulario?.telefone ||
						itemFormulario?.celular ||
						'',
				),
				cpf: normalizeComparableDigits(
					profissionalFormulario?.cpf || itemFormulario?.cpf || cpf,
				),
				numeroConselho: normalizeComparableDigits(
					getNumeroConselhoFromProfissional(profissionalFormulario) ||
						getNumeroConselhoFromProfissional(itemFormulario),
				),
				ufConselho: normalizeComparableString(
					getUfConselhoFromProfissional(profissionalFormulario) ||
						getUfConselhoFromProfissional(itemFormulario),
				),
				especialidade: normalizeComparableString(
					resolveEspecialidadeSelectValue(
						profissionalFormulario?.especialidade_id,
						itemFormulario?.especialidade_id,
						itemFormulario?.especialidade,
					),
				),
				vinculo: normalizeComparableString(
					profissionalFormulario?.vinculo_tipo_id ||
						profissionalFormulario?.vinculo_tipo ||
						itemFormulario?.vinculo_tipo_id ||
						itemFormulario?.vinculo_tipo ||
						itemFormulario?.vinculo ||
						'',
				),
				cargaHorariaSemanal: normalizeComparableString(
					profissionalFormulario?.carga_horaria_semanal ||
						itemFormulario?.carga_horaria_semanal ||
						'',
				),
			};

			const profissionalAtual = {
				nome: normalizeComparableString(currentPro.nome || ''),
				cargo: normalizeComparableString(currentPro.cargo || ''),
				admissao: normalizeIsoDate(currentPro.admissao || ''),
				telefone: normalizeComparableDigits(currentPro.telefone || ''),
				cpf: normalizeComparableDigits(cpf || ''),
				numeroConselho: normalizeComparableDigits(
					currentPro.numeroConselho || '',
				),
				ufConselho: normalizeComparableString(
					currentPro.ufConselho || '',
				),
				especialidade: normalizeComparableString(
					currentPro.especialidade || '',
				),
				vinculo: normalizeComparableString(currentPro.vinculo || ''),
				cargaHorariaSemanal: normalizeComparableString(
					currentPro.cargaHorariaSemanal || '',
				),
			};

			const profissionalChanged = Object.keys(profissionalAtual).some(
				(chave) =>
					profissionalAtual[chave] !== profissionalOriginal[chave],
			);

			return !structuralChanged && profissionalChanged;
		},
		[
			cpf,
			currentPro.admissao,
			currentPro.cargaHorariaSemanal,
			currentPro.cargo,
			currentPro.dias,
			currentPro.especialidade,
			currentPro.nome,
			currentPro.numeroConselho,
			currentPro.setor,
			currentPro.telefone,
			currentPro.turnos,
			currentPro.ufConselho,
			currentPro.vinculo,
			editingItemSnapshot,
			gradePayload?.contexto?.setor?.id,
			isPopupEditing,
			popupExtraFields.dataFim,
			popupExtraFields.dataInicio,
			popupExtraFields.dataInicioVacancia,
			popupStatus,
			resolveEspecialidadeSelectValue,
			searchFilters.setor,
		],
	);

	const buildSingleProfessionalPatchPayload = useCallback(
		(formularioAtual = null, payloadAtual = {}) => {
			const escalaMensalId =
				Number(
					gradePayload?.escala?.id ||
						formularioAtual?.contexto_competencia
							?.escala_mensal_id ||
						formularioAtual?.defaults?.contexto_competencia
							?.escala_mensal_id ||
						0,
				) || undefined;

			return {
				escala_mensal_id: escalaMensalId,
				tipo_item: payloadAtual?.tipo_item,
				observacao: payloadAtual?.observacao || '',
				...(payloadAtual?.profissional
					? { profissional: payloadAtual.profissional }
					: {}),
				...(!payloadAtual?.profissional &&
				payloadAtual?.profissional_por_cpf
					? {
							profissional_por_cpf:
								payloadAtual.profissional_por_cpf,
						}
					: {}),
			};
		},
		[gradePayload?.escala?.id],
	);

	// Corrige handleDia para não adicionar profissional automaticamente
	const handleDia = (e) => {
		if (
			popupStatus === 'vacancia' ||
			(isPopupEditing &&
				editingItemId !== null &&
				editingItemId !== undefined)
		) {
			return;
		}

		const { checked, id } = e.target;
		let result = { ...currentPro };
		if (checked) {
			// Adiciona o dia apenas se não existe
			if (!result.dias.includes(id)) {
				result.dias = [...result.dias, id];
			}
		} else {
			result.dias = result.dias.filter((dia) => dia !== id);
			if (result.turnos && result.turnos[id]) {
				const updatedTurnos = { ...result.turnos };
				delete updatedTurnos[id];
				result.turnos = updatedTurnos;
			}
		}
		setCurrentPro(result);
	};

	const handleTurnos = (e) => {
		if (
			popupStatus === 'vacancia' ||
			(isPopupEditing &&
				editingItemId !== null &&
				editingItemId !== undefined)
		) {
			return;
		}

		const { id, value } = e.target;
		const [dia, tipo] = id.split('/');

		let sanitizedValue = value.replace(/\D/g, '').slice(0, 2);
		if (sanitizedValue !== '' && parseInt(sanitizedValue, 10) > 23) {
			sanitizedValue = '23';
		}

		setCurrentPro((prevState) => {
			const updatedTurnos = { ...prevState.turnos };
			const turnoDia = { ...(updatedTurnos[dia] || {}) };

			if (sanitizedValue === '') {
				delete turnoDia[tipo];
			} else {
				turnoDia[tipo] = sanitizedValue;
			}

			if (Object.keys(turnoDia).length === 0) {
				delete updatedTurnos[dia];
			} else {
				updatedTurnos[dia] = turnoDia;
			}

			return {
				...prevState,
				turnos: updatedTurnos,
			};
		});
	};

	const handleTurnoBlur = (dia, tipo) => {
		if (
			popupStatus === 'vacancia' ||
			(isPopupEditing &&
				editingItemId !== null &&
				editingItemId !== undefined)
		) {
			return;
		}

		setCurrentPro((prevState) => {
			const valorAtual = prevState.turnos?.[dia]?.[tipo];
			if (!valorAtual || valorAtual.length !== 1) {
				return prevState;
			}

			return {
				...prevState,
				turnos: {
					...prevState.turnos,
					[dia]: {
						...prevState.turnos[dia],
						[tipo]: `0${valorAtual}`,
					},
				},
			};
		});
	};

	useEffect(() => {
		const cargaCalculada = String(cargaHorariaSemanalCalculada);

		setCurrentPro((prevState) => {
			if (prevState.cargaHorariaSemanal === cargaCalculada) {
				return prevState;
			}

			return {
				...prevState,
				cargaHorariaSemanal: cargaCalculada,
			};
		});
	}, [currentPro.dias, currentPro.turnos, cargaHorariaSemanalCalculada]);

	const handleCpf = (e) => {
		setCpf(e.target.value.slice(0, 11));
		setCurrentSearch([]);
		setCpfLockedFields({});
		setCurrentPro((prevState) => ({
			...prevState,
			cargo: DEFAULT_MEDICO_CARGO,
		}));
		clearFieldError('cpf');
		setPopupSubmitError('');
		setCpfSearchWarning('');
	};

	const handleSearch = async () => {
		if (!token) return;
		const cpfNotFoundMessage =
			'profissional não cadastrado na base de dados, prossiga com o preenchimento do formulário para cadastrá-lo';
		setPopupSubmitError('');
		if (showUnidadeFilter && !searchFilters.unidade) {
			setPopupSubmitError(
				'Selecione uma unidade antes de pesquisar profissional.',
			);
			setOperationError(
				'Selecione uma unidade antes de pesquisar profissional.',
			);
			return;
		}
		if (!String(cpf || '').trim()) {
			setCurrentSearch([]);
			setCpfLockedFields({});
			setOperationError('');
			setCpfSearchWarning('');
			setCurrentPro((prevState) => ({
				...prevState,
				nome: '',
				cargo: DEFAULT_MEDICO_CARGO,
				admissao: '',
				numeroConselho: '',
				ufConselho: '',
				cargaHorariaSemanal: '',
				observacao: '',
				especialidade: '',
				vinculo: '',
				setor: '',
				sala: '',
				telefone: '',
			}));
			return;
		}

		const result = await requestBuscarProfissionalEscala({
			token,
			cpf,
			estabelecimento: showUnidadeFilter
				? searchFilters.unidade || undefined
				: userCnes || undefined,
		});
		if (result?.error) {
			setCurrentSearch([]);
			setCpfLockedFields({});
			setCpfSearchWarning('');
			const searchErrorMessage = getErrorMessage(
				result,
				'Não foi possível buscar profissional.',
			);
			const normalizedErrorMessage = String(searchErrorMessage || '')
				.toLowerCase()
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '');
			const isNotFoundMessage =
				normalizedErrorMessage.includes('nao encontrado') ||
				normalizedErrorMessage.includes('nao encontrada') ||
				normalizedErrorMessage.includes('nao cadastrado') ||
				normalizedErrorMessage.includes('not found');

			if (isNotFoundMessage) {
				setCurrentPro((prevState) => ({
					...prevState,
					cargo: DEFAULT_MEDICO_CARGO,
				}));
				setCpfSearchWarning(cpfNotFoundMessage);
				setOperationError('');
				return;
			}

			setPopupSubmitError(searchErrorMessage);
			setOperationError(searchErrorMessage);
			return;
		}

		if (!Array.isArray(result) || result.length === 0) {
			setCurrentSearch([]);
			setCpfLockedFields({});
			setCurrentPro((prevState) => ({
				...prevState,
				cargo: DEFAULT_MEDICO_CARGO,
			}));
			setCpfSearchWarning(cpfNotFoundMessage);
			setPopupSubmitError('');
			setOperationError('');
			return;
		}

		setCpfSearchWarning('');
		setPopupSubmitError('');

		const profissionalEncontrado = result[0] || {};
		setCanViewProfessionalPhone(hasTelefoneField(profissionalEncontrado));
		setCpfLockedFields({
			nome: Boolean(String(profissionalEncontrado.nome || '').trim()),
			cargo: Boolean(String(profissionalEncontrado.cargo || '').trim()),
			admissao: Boolean(
				String(
					profissionalEncontrado.admissao ||
						profissionalEncontrado.data_admissao ||
						'',
				).trim(),
			),
			numeroConselho: Boolean(
				String(
					getNumeroConselhoFromProfissional(profissionalEncontrado),
				).trim(),
			),
			ufConselho: Boolean(
				String(
					getUfConselhoFromProfissional(profissionalEncontrado),
				).trim(),
			),
			especialidade: false,
			vinculo: Boolean(
				String(
					profissionalEncontrado.vinculo_tipo ||
						profissionalEncontrado.vinculo ||
						'',
				).trim(),
			),
			telefone: Boolean(
				String(profissionalEncontrado.telefone || '').trim(),
			),
			setor: false,
			sala: false,
		});
		setOperationError('');

		setCurrentSearch(result);

		setCurrentPro({
			nome: profissionalEncontrado.nome || '',
			cargo: resolveCargoValue(profissionalEncontrado.cargo),
			admissao:
				profissionalEncontrado.admissao ||
				profissionalEncontrado.data_admissao ||
				'',
			numeroConselho: getNumeroConselhoFromProfissional(
				profissionalEncontrado,
			),
			ufConselho: getUfConselhoFromProfissional(profissionalEncontrado),
			regime: popupRegime,
			cargaHorariaSemanal: '',
			observacao: '',
			turnos: {},
			especialidade: resolveEspecialidadeSelectValue(
				profissionalEncontrado.especialidade_id,
				profissionalEncontrado.especialidade,
			),
			vinculo: String(
				profissionalEncontrado.vinculo_tipo ||
					profissionalEncontrado.vinculo ||
					'',
			),
			setor: profissionalEncontrado.setor
				? String(profissionalEncontrado.setor)
				: '',
			sala: profissionalEncontrado.sala
				? String(profissionalEncontrado.sala)
				: '',
			telefone: profissionalEncontrado.telefone || '',
			dias: [],
			trocaCom: '',
			trocaComNome: '',
			trocaData: '',
			trocaHora: '',
			trocaItemDestinoId: '',
		});
	};

	// Função para formatar telefone celular
	const formatPhoneNumber = (value) => {
		if (!value) return '';
		// Remove tudo que não for número
		value = value.replace(/\D/g, '');
		// Aplica a máscara (00) 0 0000-0000
		if (value.length > 11) value = value.slice(0, 11);
		if (value.length > 0) value = '(' + value;
		if (value.length > 3) value = value.slice(0, 3) + ') ' + value.slice(3);
		if (value.length > 10)
			value = value.slice(0, 10) + '-' + value.slice(10);
		return value;
	};

	const handleFilterInputChange = (fieldName, value) => {
		if (fieldName === 'unidade' && !showUnidadeFilter) {
			return;
		}
		setSearchFilters((prevState) => {
			if (fieldName === 'unidade' && !String(value || '').trim()) {
				return {
					...prevState,
					unidade: '',
					tipoSetor: '',
					identificador: '',
					servico: '',
					setor: '',
					especialidade: '',
				};
			}

			if (fieldName === 'tipoSetor' || fieldName === 'servico') {
				return {
					...prevState,
					tipoSetor: value,
					servico: value,
					identificador: '',
					setor: '',
				};
			}

			if (fieldName === 'identificador') {
				const identificadorSelecionado =
					identificadorCatalogOptions.find(
						(item) => String(item.value) === String(value),
					);

				return {
					...prevState,
					identificador: value,
					setor: identificadorSelecionado?.setor_id
						? String(identificadorSelecionado.setor_id)
						: '',
				};
			}

			return {
				...prevState,
				[fieldName]: value,
			};
		});
	};

	const handleFilterSearch = async () => {
		if (viewMode === 'especialidade') {
			if (
				(showUnidadeFilter && !searchFilters.unidade) ||
				!searchFilters.especialidade ||
				!searchFilters.regime ||
				!searchFilters.mesVigente ||
				!searchFilters.anoVigente
			) {
				setGradeEspecialidadePayloads([]);
				setGradeEspecialidadeMessage(
					showUnidadeFilter && !searchFilters.unidade
						? 'Selecione a unidade para buscar as escalas.'
						: !searchFilters.especialidade
							? 'Selecione a área de atuação antes de buscar a escala.'
							: 'Preencha Mês e Ano para buscar as escalas.',
				);
				return;
			}
			const nextAppliedFilters = {
				...searchFilters,
				regime: searchFilters.regime,
			};
			setAppliedFilters(nextAppliedFilters);
			setGradePayload(null);
			await refreshGradeEspecialidade(nextAppliedFilters);
			return;
		}

		if (
			(showUnidadeFilter && !searchFilters.unidade) ||
			!searchFilters.setor ||
			!searchFilters.regime ||
			!searchFilters.mesVigente ||
			!searchFilters.anoVigente
		) {
			setGradePayload(null);
			setGradeMessage(
				showUnidadeFilter
					? 'Selecione Unidade, Setor, Mês e Ano para buscar a grade.'
					: 'Preencha Setor, Mês e Ano para buscar a grade.',
			);
			return;
		}

		const nextAppliedFilters = {
			...searchFilters,
			regime: searchFilters.regime,
		};
		setAppliedFilters(nextAppliedFilters);
		setGradeEspecialidadePayloads([]);
		await refreshGrade(nextAppliedFilters);
	};

	const handleProInputs = (e) => {
		const { name } = e.target;
		let { value } = e.target;
		if (name === 'numeroConselho') {
			value = String(value || '')
				.replace(/\D/g, '')
				.slice(0, 20);
		}
		if (name === 'ufConselho') {
			value = normalizeUfSigla(value);
		}
		clearFieldError(name);
		if (name === 'setor') {
			clearFieldError('sala');
		}
		setPopupSubmitError('');

		setCurrentPro((prevState) => {
			if (name === 'setor') {
				return {
					...prevState,
					setor: value,
					sala: '',
				};
			}

			return { ...prevState, [name]: value };
		});
	};

	const handlePopupStatusSelect = async (status) => {
		const nextStatus = popupStatus === status ? '' : status;

		setPopupStatus(nextStatus);
		setPopupFieldErrors({});
		setPopupSubmitError('');
		const isVacancia = nextStatus === 'vacancia';
		setVacanciaOn(isVacancia);

		if (nextStatus !== 'troca') {
			resetTrocaState();
		}

		if (isVacancia) {
			setCpf('');
		}

		if (nextStatus === 'troca' && editingItemSnapshot) {
			await carregarCandidatosTroca(editingItemSnapshot);
		}
	};

	const handlePopupExtraFieldChange = (e) => {
		const { name, value } = e.target;
		setPopupSubmitError('');
		setPopupExtraFields((prevState) => ({
			...prevState,
			[name]: value,
		}));
	};

	const resolveFiltersAfterScaleSave = (
		apiResponse,
		submittedPayload,
		previousFilters,
	) => {
		const escala = apiResponse?.escala || {};
		const contextoCompetencia =
			submittedPayload?.contexto_competencia || {};

		const mesReferencia =
			apiResponse?.mes_referencia ||
			escala?.mes_referencia ||
			contextoCompetencia?.mes_referencia ||
			submittedPayload?.mes_referencia ||
			'';

		const mesMatch = String(mesReferencia).match(/^(\d{4})-(\d{2})/);
		const anoVigente =
			mesMatch?.[1] || String(previousFilters?.anoVigente || '');
		const mesVigente =
			mesMatch?.[2] || String(previousFilters?.mesVigente || '');

		const servico =
			submittedPayload?.servico ||
			submittedPayload?.dados_edicao?.servico ||
			submittedPayload?.resultado?.item?.servico ||
			escala?.servico_id ||
			escala?.servico ||
			previousFilters?.servico ||
			'';

		const regimeRaw =
			contextoCompetencia?.regime ||
			submittedPayload?.regime ||
			escala?.regime ||
			previousFilters?.regime ||
			'';
		const regime = String(regimeRaw || '').toLowerCase();

		const estabelecimento =
			submittedPayload?.estabelecimento ||
			escala?.cnes_estabelecimento ||
			escala?.estabelecimento ||
			apiResponse?.estabelecimento ||
			(showUnidadeFilter
				? previousFilters?.unidade
				: userCnes || previousFilters?.unidade) ||
			'';

		return {
			...previousFilters,
			unidade: String(estabelecimento || ''),
			servico: String(servico || ''),
			regime: regime || previousFilters?.regime || 'plantonista',
			mesVigente: String(mesVigente || previousFilters?.mesVigente || ''),
			anoVigente: String(anoVigente || previousFilters?.anoVigente || ''),
		};
	};

	const fecharModalEscala = () => {
		setShowAddScaleModal(false);
		setIsModalLoading(false);
		setModalViewLoadError('');
		setForceViewOnlyModal(false);
		setPopupFormularioAssistivo(null);
		setModalEspecialidadeOptions([]);
		setModalSetorOptions([]);
		setCargoOptions([]);
		setEditingProfessionalGradeItems([]);
		setEditingProfessionalGradeDays([]);
		setPopupSubmitError('');
		setPopupFieldErrors({});
		setCpfSearchWarning('');
		resetTrocaState();
	};

	const abrirModalAdicionarEscala = async () => {
		if (showUnidadeFilter && !searchFilters.unidade) {
			setGradeMessage(
				'Selecione uma unidade antes de adicionar uma escala.',
			);
			return;
		}

		setEditingItemId(null);
		setEditingItemSnapshot(null);
		setIsPopupEditing(false);
		setForceViewOnlyModal(false);
		setModalViewLoadError('');
		setPopupFormularioAssistivo(null);
		setModalEspecialidadeOptions([]);
		setModalSetorOptions([]);
		setCargoOptions([]);
		setEditingProfessionalGradeItems([]);
		setEditingProfessionalGradeDays([]);
		setCpfLockedFields({});
		setPopupFieldErrors({});
		setPopupSubmitError('');
		setCpfSearchWarning('');
		setPopupStatus('');
		setPopupRegime('rotina');
		setCpf('');
		setCurrentSearch([]);
		resetTrocaState();
		setPopupExtraFields({
			dataInicio: '',
			dataFim: '',
			dataInicioVacancia: '',
			observacao: '',
		});
		setCurrentPro({
			nome: '',
			cargo: DEFAULT_MEDICO_CARGO,
			admissao: '',
			numeroConselho: '',
			ufConselho: '',
			regime: 'rotina',
			cargaHorariaSemanal: '',
			turnos: {},
			especialidade: '',
			observacao: '',
			vinculo: '',
			setor: searchFilters.setor || '',
			sala: '',
			telefone: '',
			dias: [],
			trocaCom: '',
			trocaComNome: '',
			trocaData: '',
			trocaHora: '',
			trocaItemDestinoId: '',
		});
		setVacanciaOn(false);
		setShowAddScaleModal(true);

		const responseFormulario = await requestFormularioNovoItemEscala({
			token,
			estabelecimento: showUnidadeFilter
				? searchFilters.unidade || undefined
				: userCnes || searchFilters.unidade || undefined,
			setor: searchFilters.setor || undefined,
			identificador: searchFilters.identificador || undefined,
			regime: String(popupRegime || 'rotina').toUpperCase(),
			mes: new Date().getMonth() + 1,
			ano: new Date().getFullYear(),
		});

		if (!responseFormulario?.error) {
			applyFormularioAssistivo(responseFormulario);
		}
	};

	const handlePopupSubmit = async () => {
		const isEditing = isPopupEditing;
		setOperationError('');
		setPopupSubmitError('');

		if (isEditing && (!isPrivilegedEscalaUser || forceViewOnlyModal)) {
			fecharModalEscala();
			return;
		}

		if (
			isEditing &&
			(editingItemId === null || editingItemId === undefined)
		) {
			setPopupSubmitError(
				'Não foi possível identificar o card para atualização. Feche o modal, abra a edição do card novamente e tente salvar.',
			);
			return;
		}

		const validationErrors = buildPopupValidationErrors();
		if (Object.keys(validationErrors).length > 0) {
			setPopupFieldErrors(validationErrors);
			setPopupSubmitError(Object.values(validationErrors)[0]);
			return;
		}

		setPopupFieldErrors({});

		const targetEstabelecimento = showUnidadeFilter
			? searchFilters.unidade || undefined
			: userCnes || searchFilters.unidade || undefined;

		if (popupStatus === 'troca') {
			const itemDestino = Number(
				currentPro.trocaItemDestinoId || currentPro.trocaHora || 0,
			);

			if (!itemDestino) {
				setPopupSubmitError(
					'Selecione um candidato válido para efetivar a troca.',
				);
				return;
			}

			const responseTroca = await requestRegistrarTrocaEscala({
				token,
				id: editingItemId,
				body: {
					item_destino_id: itemDestino,
				},
			});

			if (responseTroca?.error) {
				const apiErrorMessage = getErrorMessage(
					responseTroca,
					'Não foi possível registrar a troca da escala.',
				);
				setOperationError(apiErrorMessage);
				setPopupSubmitError(apiErrorMessage);
				const mappedFieldErrors = mapApiErrorToFields(apiErrorMessage);
				if (Object.keys(mappedFieldErrors).length > 0) {
					setPopupFieldErrors((prevState) => ({
						...prevState,
						...mappedFieldErrors,
					}));
				}
				return;
			}

			setEditingItemId(null);
			setEditingItemSnapshot(null);
			setIsPopupEditing(false);
			setPopupFieldErrors({});
			setPopupSubmitError('');
			resetTrocaState();
			fecharModalEscala();
			await refreshGrade({
				...searchFilters,
				unidade: targetEstabelecimento || searchFilters.unidade,
				regime: searchFilters.regime,
			});
			return;
		}

		const selectedRegime = String(popupRegime || 'rotina').toUpperCase();
		const targetMesReferencia = getMesReferenciaAtual();
		let targetScaleId = gradePayload?.escala?.id || null;
		let shouldCreateInBatchForExistingScale = !isEditing;

		let formularioAssistivoAtual = popupFormularioAssistivo;
		if (!formularioAssistivoAtual && !isEditing) {
			formularioAssistivoAtual = await requestFormularioNovoItemEscala({
				token,
				estabelecimento: targetEstabelecimento,
				setor: searchFilters.setor || undefined,
				identificador: searchFilters.identificador || undefined,
				regime: selectedRegime,
				mes: Number(searchFilters.mesVigente || 0) || undefined,
				ano: Number(searchFilters.anoVigente || 0) || undefined,
			});

			if (formularioAssistivoAtual?.error) {
				const apiErrorMessage = getErrorMessage(
					formularioAssistivoAtual,
					'Não foi possível preparar o formulário da escala.',
				);
				setPopupSubmitError(apiErrorMessage);
				return;
			}

			const targetScaleIdFromFormulario = Number(
				formularioAssistivoAtual?.escala?.id ||
					formularioAssistivoAtual?.contexto_competencia
						?.escala_mensal_id ||
					formularioAssistivoAtual?.defaults?.contexto_competencia
						?.escala_mensal_id ||
					0,
			);

			if (!targetScaleId && targetScaleIdFromFormulario > 0) {
				targetScaleId = targetScaleIdFromFormulario;
				shouldCreateInBatchForExistingScale = true;
			}

			applyFormularioAssistivo(formularioAssistivoAtual);
		}

		if (!isEditing && !shouldCreateInBatchForExistingScale) {
			const existingScaleResponse = await requestBuscarGradeEscala({
				token,
				estabelecimento: targetEstabelecimento,
				identificador:
					String(searchFilters.identificador || '').trim() ||
					undefined,
				setor:
					!String(searchFilters.identificador || '').trim() &&
					Number.isFinite(Number(searchFilters.setor))
						? Number(searchFilters.setor)
						: undefined,
				regime: selectedRegime,
				mes: Number(searchFilters.mesVigente || 0) || undefined,
				ano: Number(searchFilters.anoVigente || 0) || undefined,
			});

			const scaleExistsForFilters =
				!existingScaleResponse?.error &&
				(Array.isArray(existingScaleResponse?.visualizacao?.setores) ||
					Array.isArray(existingScaleResponse?.grade?.setores));

			if (scaleExistsForFilters) {
				shouldCreateInBatchForExistingScale = true;
				if (!targetScaleId && existingScaleResponse?.escala?.id) {
					targetScaleId = existingScaleResponse.escala.id;
				}
			}
		}

		let forcedRecorrenciaPayload = null;
		let cardsPeriodoProfissional = [];

		if (isEditing) {
			const periodoInicioEdicao =
				popupExtraFields.dataInicio ||
				formularioAssistivoAtual?.defaults?.periodo?.data_inicio ||
				'';
			const periodoFimEdicao =
				popupExtraFields.dataFim ||
				formularioAssistivoAtual?.defaults?.periodo?.data_fim ||
				'';

			const ocorrenciasPeriodo =
				await carregarOcorrenciasPeriodoProfissional({
					dataInicio: periodoInicioEdicao,
					dataFim: periodoFimEdicao,
				});

			if (ocorrenciasPeriodo?.error) {
				setOperationError(ocorrenciasPeriodo.error);
				setPopupSubmitError(ocorrenciasPeriodo.error);
				return;
			}

			if (Array.isArray(ocorrenciasPeriodo?.ocorrencias)) {
				forcedRecorrenciaPayload = {
					ocorrencias: ocorrenciasPeriodo.ocorrencias,
				};
			}

			cardsPeriodoProfissional = Array.isArray(ocorrenciasPeriodo?.cards)
				? ocorrenciasPeriodo.cards
				: [];
		}

		const { mode, payload } = buildItemPayload({
			targetScaleId: isEditing ? gradePayload?.escala?.id : targetScaleId,
			targetEstabelecimento: targetEstabelecimento,
			targetRegime: selectedRegime,
			targetMesReferencia,
			formularioAssistivo: formularioAssistivoAtual,
			forceBatchCreate: shouldCreateInBatchForExistingScale,
			forcedRecorrenciaPayload,
		});
		let response;

		if (isEditing) {
			const shouldPatchSingleProfessionalData =
				shouldUseSingleProfessionalPatch(
					formularioAssistivoAtual,
					payload,
				);

			const cardsParaAtualizar =
				cardsPeriodoProfissional.length > 0
					? cardsPeriodoProfissional
					: editingItemSnapshot
						? [
								{
									item: editingItemSnapshot,
									horario:
										buildOcorrenciaFromEscalaItem(
											editingItemSnapshot,
										),
								},
							]
						: [];

			if (!cardsParaAtualizar.length) {
				setPopupSubmitError(
					'Não foi possível identificar os cards que devem ser atualizados.',
				);
				return;
			}

			const itemIdsSelecionados = [
				...new Set(
					cardsParaAtualizar
						.map((entry) =>
							resolveEscalaItemId(entry?.item || entry),
						)
						.filter(
							(value) =>
								value !== null &&
								value !== undefined &&
								String(value).trim() !== '',
						),
				),
			];

			if (!itemIdsSelecionados.length) {
				setPopupSubmitError(
					'Não foi possível identificar os cards selecionados para atualização.',
				);
				return;
			}

			if (
				shouldPatchSingleProfessionalData &&
				itemIdsSelecionados.length === 1
			) {
				response = await requestEditarItemEscala({
					token,
					id: editingItemId,
					body: buildSingleProfessionalPatchPayload(
						formularioAssistivoAtual,
						payload,
					),
				});
			} else {
				response = await requestEditarLoteItensEscala({
					token,
					body: buildBatchSelectedItemsPatchPayload(
						payload,
						cardsParaAtualizar,
					),
				});
			}
		} else if (mode === 'batch' || mode === 'append-batch') {
			response = await requestCriarLoteItensEscala({
				token,
				body: payload,
			});
		} else {
			response = await requestCriarItemEscala({ token, body: payload });
		}

		if (response?.error) {
			const apiErrorMessage = getErrorMessage(
				response,
				'Não foi possível salvar item da escala.',
			);
			setOperationError(apiErrorMessage);
			setPopupSubmitError(apiErrorMessage);
			const mappedFieldErrors = mapApiErrorToFields(apiErrorMessage);
			if (Object.keys(mappedFieldErrors).length > 0) {
				setPopupFieldErrors((prevState) => ({
					...prevState,
					...mappedFieldErrors,
				}));
			}
			return;
		}

		setEditingItemId(null);
		setEditingItemSnapshot(null);
		setIsPopupEditing(false);
		setPopupFieldErrors({});
		setPopupSubmitError('');
		const filtersAfterSave = resolveFiltersAfterScaleSave(
			response,
			{
				...payload,
				regime: payload?.contexto_competencia?.regime || selectedRegime,
				mes_referencia:
					payload?.contexto_competencia?.mes_referencia ||
					targetMesReferencia,
				estabelecimento: targetEstabelecimento,
			},
			searchFilters,
		);
		setSearchFilters(filtersAfterSave);
		setAppliedFilters(filtersAfterSave);
		fecharModalEscala();
		await refreshGrade(filtersAfterSave);
	};

	const isEditingModal =
		isPopupEditing && editingItemId !== null && editingItemId !== undefined;
	const isViewOnlyModal = true;
	const isTrocaMode = popupStatus === 'troca';
	const trocaDisponivelNoModal =
		editingItemId !== null &&
		editingItemId !== undefined &&
		canSwapScaleItem(editingItemSnapshot);
	const isProfessionalFormLocked = isEditingModal;
	const isScheduleTabLocked =
		popupStatus === 'vacancia' || isEditingModal || isTrocaMode;
	const isCpfFieldLocked = (fieldName) =>
		!isPopupEditing && Boolean(cpfLockedFields[fieldName]);
	const isProfessionalFieldLocked = (fieldName) =>
		isProfessionalFormLocked || isCpfFieldLocked(fieldName);
	const isCargoFieldLocked = vacanciaOn || !isPopupEditing;
	const unidadeObrigatoriaPendente =
		showUnidadeFilter && !appliedFilters.unidade && !gradePayload;

	// Limpa o cache da escala quando o usuário faz logout
	useEffect(() => {
		if (authStatus === 'unauthorized') {
			localStorage.removeItem(ESCALA_FILTERS_CACHE_KEY);
		}
	}, [authStatus]);

	useEffect(() => {
		const cacheableFilters = { ...(appliedFilters || {}) };
		delete cacheableFilters.mesVigente;
		delete cacheableFilters.anoVigente;

		localStorage.setItem(
			ESCALA_FILTERS_CACHE_KEY,
			JSON.stringify(cacheableFilters),
		);
	}, [appliedFilters]);

	useEffect(() => {
		if (!token) return;

		const estabelecimentoDaEscalaAtual = String(
			gradePayload?.contexto?.estabelecimento?.id ||
				appliedFilters?.unidade ||
				'',
		).trim();

		const estabelecimentoCatalogo = String(
			isPopupEditing
				? estabelecimentoDaEscalaAtual ||
						searchFilters.unidade ||
						userCnes
				: showUnidadeFilter
					? searchFilters.unidade ||
						appliedFilters?.unidade ||
						estabelecimentoDaEscalaAtual
					: userCnes || estabelecimentoDaEscalaAtual,
		).trim();

		if (!estabelecimentoCatalogo) {
			if (showUnidadeFilter) {
				setServiceOptions([]);
				setRegimeOptions([]);
				setEspecialidadeOptions([]);
				setCatalogoSetores([]);
				setIdentificadorCatalogOptions([]);
				setSetorOptions([]);
				setSalaOptions([]);
				setSearchFilters((prevState) => {
					if (!prevState.tipoSetor && !prevState.servico)
						return prevState;
					return {
						...prevState,
						tipoSetor: '',
						identificador: '',
						servico: '',
						setor: '',
						especialidade: '',
					};
				});
			}
			return;
		}

		let cancelled = false;
		(async () => {
			const { catalogos, catalogosComItens, error } =
				await fetchCatalogosOnce({
					token,
					estabelecimento: estabelecimentoCatalogo,
				});
			if (cancelled || error || catalogos?.error) return;

			const tiposSetor = catalogos.tiposSecao || catalogos.servicos || [];
			const identificadores = catalogos.identificadores || [];
			const setoresCatalogo = Array.isArray(catalogosComItens?.setores)
				? catalogosComItens.setores
				: catalogos.setores || [];
			setServiceOptions(tiposSetor);
			const regimesCatalogo = catalogos.regimes || [];
			setRegimeOptions(regimesCatalogo);
			const setoresOrdenados = sortSetoresPorAndar(setoresCatalogo);
			const identificadoresOrdenados =
				sortSetoresPorAndar(identificadores);
			setCatalogoSetores(setoresOrdenados);
			setSetorOptions(setoresOrdenados);
			setIdentificadorCatalogOptions(identificadoresOrdenados);
			const especialidadesComItens = Array.isArray(
				catalogosComItens?.especialidades,
			)
				? catalogosComItens.especialidades
				: null;
			setEspecialidadeOptions(
				especialidadesComItens ?? (catalogos.especialidades || []),
			);

			setSearchFilters((prevState) => {
				const regimeAtualExiste = regimeExistsInOptions(
					prevState.regime,
					regimesCatalogo,
				);
				const setorAtualExiste = setoresCatalogo.some(
					(item) => String(item.id) === String(prevState.setor),
				);
				const tipoAtualExiste = tiposSetor.some(
					(item) => String(item.id) === String(prevState.tipoSetor),
				);
				const identificadorAtualExiste = identificadores.some(
					(item) =>
						String(item.value) === String(prevState.identificador),
				);
				if (
					setorAtualExiste &&
					(!prevState.tipoSetor || tipoAtualExiste)
				) {
					return {
						...prevState,
						regime: regimeAtualExiste
							? prevState.regime
							: getRegimeDefaultFromOptions(regimesCatalogo),
						identificador: identificadorAtualExiste
							? prevState.identificador
							: '',
					};
				}

				return {
					...prevState,
					tipoSetor: '',
					servico: '',
					identificador: '',
					regime: regimeAtualExiste
						? prevState.regime
						: getRegimeDefaultFromOptions(regimesCatalogo),
					setor: setorAtualExiste ? prevState.setor : '',
				};
			});
		})();

		return () => {
			cancelled = true;
		};
	}, [
		token,
		showUnidadeFilter,
		searchFilters.unidade,
		userCnes,
		isPopupEditing,
		gradePayload,
		appliedFilters?.unidade,
	]);

	useEffect(() => {
		let userVerification = Cookies.get('user');

		if (userVerification) {
			let user = JSON.parse(Cookies.get('user'));
			const { unidade, nome } = user;
			const value = unidade ? unidade : nome;
			const padiVerify = value.includes('padi') ? 'padi' : '';
			setCabeçalho((prevState) => ({
				...prevState,
				unidades: value,
				regimes: padiVerify,
			}));
		}
		if (JSON.parse(localStorage.getItem('cabeçalho'))) {
			setCabeçalho(JSON.parse(localStorage.getItem('cabeçalho'))[0]);
		}
		const currentPage = 'escala';
		const validate = String(window.location.pathname).includes(currentPage);
		const localData = Cookies.get('user');
		if (!localData) {
			// Usuário não autenticado: limpa cache da escala
			localStorage.removeItem(ESCALA_FILTERS_CACHE_KEY);
			setAuthStatus(validate ? 'unauthorized' : 'authorized');
			return;
		}

		if (localData) {
			const parsedUser = JSON.parse(localData);
			setToken(parsedUser.token || '');

			const cargoNormalizado = String(
				parsedUser.cargo || '',
			).toLowerCase();
			const permissaoUsuario = Array.isArray(parsedUser.permissao)
				? parsedUser.permissao
				: [parsedUser.permissao].filter(Boolean);
			const usuarioNormalizado = String(
				parsedUser.username || parsedUser.nome || '',
			).toLowerCase();
			const unidadeNormalizada = String(parsedUser.unidade || '')
				.trim()
				.toLowerCase();
			const canManageEscalaProfissionais =
				hasNamedPermission(permissaoUsuario, 'EscalaMedicaEdicao') ||
				cargoNormalizado === 'desenvolvedor';

			const buscaGeralCatalogos =
				cargoNormalizado === 'desenvolvedor' ||
				cargoNormalizado === 'subhue' ||
				usuarioNormalizado.includes('subhue') ||
				unidadeNormalizada === 'subhue' ||
				unidadeNormalizada.length === 0;
			setIsPrivilegedEscalaUser(canManageEscalaProfissionais);
			setCanAccessProfessionalManagement(canManageEscalaProfissionais);
			const cnesEstabelecimento = String(
				parsedUser.cnes ||
					parsedUser.cnes_estabelecimento ||
					parsedUser.cnesEstabelecimento ||
					'',
			).trim();
			setUserCnes(cnesEstabelecimento);
			setShowUnidadeFilter(buscaGeralCatalogos);
			setSearchFilters((prevState) => ({
				...prevState,
				unidade: buscaGeralCatalogos
					? prevState.unidade
					: cnesEstabelecimento,
			}));

			(async () => {
				if (!parsedUser.token) return;
				const [vinculos, catalogoUnidadesCompartilhado] = await Promise.all([
					requestVinculosEscala({ token: parsedUser.token }),
					requestCatalogosEscalaSelects({
						token: parsedUser.token,
						scope: 'enfermagem',
					}),
				]);

				if (!vinculos?.error) {
					setVinculoOptions(vinculos || []);
				}

				if (!catalogoUnidadesCompartilhado?.error) {
					setUnidadeOptions(
						normalizeUnits(
							catalogoUnidadesCompartilhado.estabelecimentos,
						),
					);
				}
			})();

			setAuthStatus('authorized');
		}
	}, []);

	useEffect(() => {
		setSetorOptions(sortSetoresPorAndar(catalogoSetores));

		const setorAtual = catalogoSetores.find(
			(item) => String(item.id) === String(currentPro.setor),
		);
		setSalaOptions(setorAtual?.salas || []);
	}, [catalogoSetores, currentPro.setor]);

	useEffect(() => {
		if (!hasInitialCachedFilters) {
			setInitialGradeChecked(true);
			return;
		}

		if (initialGradeChecked) return;
		if (
			!token ||
			(showUnidadeFilter && !appliedFilters?.unidade) ||
			!appliedFilters?.setor ||
			!appliedFilters?.mesVigente ||
			!appliedFilters?.anoVigente
		) {
			return;
		}

		let cancelled = false;
		setInitialGradeChecked(true);

		(async () => {
			await refreshGrade(appliedFilters);
			if (cancelled) return;
		})();

		return () => {
			cancelled = true;
		};
	}, [
		hasInitialCachedFilters,
		initialGradeChecked,
		token,
		appliedFilters,
		userCnes,
		showUnidadeFilter,
		refreshGrade,
	]);

	// Novo: escuta evento global para abrir o popup de edição de escala
	useEffect(() => {
		function handleAbrirPopupEditarEscala(e) {
			const item = e.detail?.item;
			if (!item) return;

			Promise.resolve(handleEditItemRef.current?.(item)).catch(() => {
				setPopupSubmitError(
					'Não foi possível preparar a edição do card selecionado.',
				);
			});
		}
		window.addEventListener(
			'abrirPopupEditarEscala',
			handleAbrirPopupEditarEscala,
		);
		return () => {
			window.removeEventListener(
				'abrirPopupEditarEscala',
				handleAbrirPopupEditarEscala,
			);
		};
	}, []);

	const handleDeleteItem = async (itemId) => {
		if (!token || !itemId) return;
		const response = await requestExcluirItemEscala({ token, id: itemId });
		if (response?.error) {
			setOperationError(
				getErrorMessage(
					response,
					'Não foi possível excluir item da escala.',
				),
			);
			return;
		}
		await refreshGrade({ ...searchFilters, regime: searchFilters.regime });
	};

	const preencherModalComItem = (item, statusForcado) => {
		const dados = item?.dados_edicao || item || {};
		const horarioEdicao =
			item?.horario || item?.edicao?.horario || dados?.horario || {};
		const profissionalEdicao =
			item?.edicao?.profissional_por_cpf ||
			dados?.profissional_por_cpf ||
			{};
		const contextoEdicao =
			item?.edicao?.contexto_competencia ||
			dados?.contexto_competencia ||
			{};
		const itemId = resolveEscalaItemId(item);

		if (itemId === null) {
			setOperationError(
				'Não foi possível identificar o item para edição. Atualize a grade e tente novamente.',
			);
			return;
		}

		const tipoItem = normalizeItemType(
			dados?.tipo_item || item?.tipo_item || '',
		);
		const tipoItemParaEdicao = tipoItem === 'troca' ? 'alocacao' : tipoItem;

		const diaPorNumero = {
			0: 'Domingo',
			1: 'Segunda-feira',
			2: 'Terça-feira',
			3: 'Quarta-feira',
			4: 'Quinta-feira',
			5: 'Sexta-feira',
			6: 'Sábado',
			7: 'Domingo',
		};

		const diaSelecionado =
			horarioEdicao?.nome_dia_semana ||
			dados?.nome_dia_semana ||
			item?.celula?.nome_dia_semana ||
			diaPorNumero[horarioEdicao?.dia_semana] ||
			diaPorNumero[dados?.dia_semana] ||
			diaPorNumero[item?.celula?.dia_semana] ||
			'';

		const horaInicioRaw =
			dados?.hora_inicio ||
			horarioEdicao?.hora_inicio ||
			item?.hora_inicio ||
			'';
		const horaFimRaw =
			dados?.hora_fim || horarioEdicao?.hora_fim || item?.hora_fim || '';
		const horaInicio = String(horaInicioRaw).slice(0, 2);
		const horaFim = String(horaFimRaw).slice(0, 2);

		const diasPreenchidos = diaSelecionado ? [diaSelecionado] : [];
		const turnosPreenchidos = diaSelecionado
			? {
					[diaSelecionado]: {
						inicio: horaInicio || '',
						fim: horaFim || '',
					},
				}
			: {};

		const statusPadrao =
			tipoItem === 'troca'
				? 'troca'
				: tipoItemParaEdicao === 'vacancia' ||
					  tipoItemParaEdicao === 'atestado' ||
					  tipoItemParaEdicao === 'folga' ||
					  tipoItemParaEdicao === 'licenca' ||
					  tipoItemParaEdicao === 'ferias' ||
					  tipoItemParaEdicao === 'alocacao' ||
					  tipoItemParaEdicao === 'falta'
					? tipoItemParaEdicao
					: '';
		const statusSelecionado = statusForcado || statusPadrao;

		setOperationError('');
		setEditingItemId(itemId);
		setEditingItemSnapshot(item || null);
		setEditingProfessionalGradeItems([]);
		setEditingProfessionalGradeDays([]);
		setIsPopupEditing(true);
		setForceViewOnlyModal(Boolean(item?.forceViewOnly));
		setCpfLockedFields({});
		setPopupStatus(statusSelecionado);
		setVacanciaOn(statusSelecionado === 'vacancia');
		setCurrentSearch([]);
		resetTrocaState();
		const isVacanciaItem = statusSelecionado === 'vacancia';
		const observacaoItem = getEscalaItemObservation(item, dados);
		const vinculoNomeItem = getVinculoDisplayName(
			dados,
			item,
			profissionalEdicao,
			item?.resumo,
		);
		const especialidadeItem = resolveEspecialidadeSelectValue(
			dados?.especialidade_id,
			dados?.especialidade,
			dados?.nome_especialidade,
			item?.especialidade?.id,
			item?.especialidade?.nome,
			item?.especialidade_id,
			item?.nome_especialidade,
			item?.edicao?.especialidade_id,
			item?.edicao?.especialidade,
			item?.edicao?.nome_especialidade,
			profissionalEdicao?.especialidade_id,
			profissionalEdicao?.especialidade_nome,
			item?.resumo?.especialidade,
		);
		const turnoItemLabel = resolveEscalaItemShiftLabel(
			dados,
			horarioEdicao,
			item,
			item?.edicao,
			item?.resumo,
		);
		const dataItemSelecionado =
			normalizeIsoDate(
				dados.data || horarioEdicao?.data || item?.celula?.data || '',
			) || '';
		const dataInicioVacancia =
			normalizeIsoDate(
				item?.edicao?.data_inicio_vacancia ||
					dados?.data_inicio_vacancia ||
					'',
			) || '';

		setPopupExtraFields({
			dataInicio: isVacanciaItem ? '' : dataItemSelecionado,
			dataFim: isVacanciaItem ? '' : dataItemSelecionado,
			dataInicioVacancia: dataInicioVacancia,
			observacao: observacaoItem,
		});
		setCanViewProfessionalPhone(
			!isVacanciaItem &&
				(hasTelefoneField(item) ||
					hasTelefoneField(profissionalEdicao)),
		);

		setCpf(
			isVacanciaItem
				? ''
				: dados.cpf ||
						dados.cpf_profissional ||
						item?.profissional?.cpf ||
						profissionalEdicao?.cpf ||
						item?.cpf_profissional ||
						'',
		);

		setCurrentPro({
			nome: isVacanciaItem
				? item?.resumo?.titulo || item?.nome || 'Vacância'
				: dados.nome ||
					dados.nome_profissional ||
					item?.profissional?.nome ||
					profissionalEdicao?.nome ||
					item?.resumo?.titulo ||
					item?.nome_profissional ||
					'',
			cargo: isVacanciaItem
				? ''
				: dados.cargo || profissionalEdicao?.cargo || '',
			admissao: isVacanciaItem
				? ''
				: dados.data_admissao ||
					profissionalEdicao?.data_admissao ||
					'',
			numeroConselho: isVacanciaItem
				? ''
				: getNumeroConselhoFromProfissional(dados) ||
					getNumeroConselhoFromProfissional(profissionalEdicao) ||
					getNumeroConselhoFromProfissional(item?.profissional) ||
					getNumeroConselhoFromProfissional(
						item?.profissional_por_cpf,
					),
			ufConselho: isVacanciaItem
				? ''
				: getUfConselhoFromProfissional(dados) ||
					getUfConselhoFromProfissional(profissionalEdicao),
			regime: String(
				dados?.regime ||
					contextoEdicao?.regime ||
					searchFilters.regime ||
					popupRegime ||
					'rotina',
			).toLowerCase(),
			cargaHorariaSemanal: isVacanciaItem
				? ''
				: String(
						dados.carga_horaria_semanal ||
							profissionalEdicao?.carga_horaria_semanal ||
							'',
					),
			turnos: turnosPreenchidos,
			turnoLabel: turnoItemLabel,
			especialidade: especialidadeItem,
			observacao: '',
			vinculoNome: vinculoNomeItem,
			vinculo: dados.vinculo_tipo
				? String(dados.vinculo_tipo)
				: profissionalEdicao?.vinculo_tipo_id
					? String(profissionalEdicao.vinculo_tipo_id)
					: dados.vinculo
						? String(dados.vinculo)
						: '',
			setor: dados.setor
				? String(dados.setor)
				: contextoEdicao?.setor_id
					? String(contextoEdicao.setor_id)
					: gradePayload?.contexto?.setor?.id
						? String(gradePayload.contexto.setor.id)
						: searchFilters.setor || '',
			sala: isVacanciaItem ? '' : dados.sala ? String(dados.sala) : '',
			telefone: isVacanciaItem
				? ''
				: dados.telefone ||
					dados.celular ||
					profissionalEdicao?.telefone ||
					'',
			dias: diasPreenchidos,
			trocaCom: '',
			trocaComNome: '',
			trocaData: '',
			trocaHora: '',
			trocaItemDestinoId: '',
		});
		setPopupFieldErrors({});
		setPopupSubmitError('');
		setModalViewLoadError('');
		setShowAddScaleModal(true);
		setIsModalLoading(true);

		return itemId;
	};

	const handleEditItem = async (item) => {
		const itemId = preencherModalComItem(item);

		if (itemId === null || itemId === undefined) {
			return;
		}

		let responseFormulario = null;

		try {
			responseFormulario = await runWithModalLoadingWindow(
				requestFormularioItemEscala({
					token,
					id: itemId,
				}),
			);
		} catch (_error) {
			responseFormulario = { error: true };
		}

		if (responseFormulario?.error || responseFormulario?.timeout) {
			setEditingProfessionalGradeItems([]);
			setEditingProfessionalGradeDays([]);
			setModalViewLoadError(MODAL_VIEW_LOAD_ERROR_MESSAGE);
			setPopupSubmitError(MODAL_VIEW_LOAD_ERROR_MESSAGE);
			setIsModalLoading(false);
			return;
		}

		if (responseFormulario?.error) {
			const errorMessage = getErrorMessage(
				responseFormulario,
				'Não foi possível carregar os dados completos para edição.',
			);
			setEditingProfessionalGradeItems([]);
			setEditingProfessionalGradeDays([]);
			setPopupSubmitError(errorMessage);
			setIsModalLoading(false);
			return;
		}

		const itemFormulario = responseFormulario?.item || {};
		const horarioFormulario = itemFormulario?.horario || {};
		const tipoItemAtual =
			getEscalaItemTipo(itemFormulario) || getEscalaItemTipo(item);
		const isVacanciaItem = tipoItemAtual === 'vacancia';

		const targetEstabelecimento = showUnidadeFilter
			? searchFilters.unidade || undefined
			: userCnes || searchFilters.unidade || undefined;
		const targetRegime = String(
			searchFilters.regime ||
				responseFormulario?.contexto_competencia?.regime ||
				popupRegime ||
				'',
		).toUpperCase();
		const targetMesReferencia =
			responseFormulario?.contexto_competencia?.mes_referencia ||
			responseFormulario?.defaults?.contexto_competencia
				?.mes_referencia ||
			getMesReferenciaAtual();

		let todosItensGrade = [];
		if (!isVacanciaItem) {
			const gradeProfissionalResponse =
				await carregarGradeProfissionalParaEdicao({
					formularioAtual: responseFormulario,
					itemSnapshot: item,
					targetEstabelecimento,
					targetRegime,
					targetMesReferencia,
				});

			if (gradeProfissionalResponse?.error) {
				setPopupSubmitError(gradeProfissionalResponse.error);
				setIsModalLoading(false);
				return;
			}

			// Consolida dias/turnos de TODOS os cards do profissional no mês
			todosItensGrade = gradeProfissionalResponse?.itens || [];
		} else {
			setEditingProfessionalGradeItems([]);
			setEditingProfessionalGradeDays([]);
		}
		const turnosConsolidados = {};
		todosItensGrade.forEach((gradeItem) => {
			const ocorrencia = buildOcorrenciaFromEscalaItem(gradeItem);
			if (!ocorrencia) return;
			const diaStr = fromDiaSemanaNumero(ocorrencia.dia_semana);
			if (!diaStr) return;
			if (!turnosConsolidados[diaStr]) {
				turnosConsolidados[diaStr] = {
					inicio: String(ocorrencia.hora_inicio || '').slice(0, 2),
					fim: String(ocorrencia.hora_fim || '').slice(0, 2),
				};
			}
		});
		const diasConsolidados = semana.filter((d) => turnosConsolidados[d]);

		applyFormularioAssistivo(responseFormulario, {
			applyDefaultsToForm: false,
		});
		const recorrenciaFormulario =
			responseFormulario?.defaults?.recorrencia || {};
		const ocorrenciasRecorrentes = Array.isArray(
			recorrenciaFormulario?.ocorrencias,
		)
			? recorrenciaFormulario.ocorrencias.filter(
					(ocorrencia) =>
						ocorrencia?.dia_semana &&
						ocorrencia?.hora_inicio &&
						ocorrencia?.hora_fim,
				)
			: [];
		const profissionalFormulario =
			itemFormulario?.edicao?.profissional_por_cpf ||
			itemFormulario?.profissional_por_cpf ||
			itemFormulario?.profissional ||
			{};
		const profissionalDefaults =
			responseFormulario?.defaults?.profissional_por_cpf ||
			responseFormulario?.defaults?.profissional ||
			{};
		const contextoFormulario =
			responseFormulario?.contexto_competencia ||
			responseFormulario?.defaults?.contexto_competencia ||
			{};
		const formularioHasTelefoneField =
			hasTelefoneField(responseFormulario) ||
			hasTelefoneField(profissionalFormulario) ||
			hasTelefoneField(itemFormulario);
		setCanViewProfessionalPhone(
			!isVacanciaItem && formularioHasTelefoneField,
		);
		const diasRecorrentes = Array.isArray(
			recorrenciaFormulario?.dias_semana,
		)
			? recorrenciaFormulario.dias_semana
					.map((dia) => fromDiaSemanaNumero(dia))
					.filter(Boolean)
			: [];
		const horarioRecorrente = recorrenciaFormulario?.horario || {};
		const diasRecorrentesPorOcorrencia = ocorrenciasRecorrentes
			.map((ocorrencia) => fromDiaSemanaNumero(ocorrencia?.dia_semana))
			.filter(Boolean);
		const turnosRecorrentesPorOcorrencia = buildTurnosFromOcorrencias(
			ocorrenciasRecorrentes,
		);
		const observacaoOficialItem = getEscalaItemObservation(
			itemFormulario,
			item,
			responseFormulario?.defaults,
		);
		const especialidadeFormularioValue = resolveEspecialidadeSelectValue(
			profissionalFormulario?.especialidade_id,
			profissionalFormulario?.especialidade,
			profissionalFormulario?.especialidade_nome,
			itemFormulario?.especialidade_id,
			itemFormulario?.especialidade,
			itemFormulario?.nome_especialidade,
			profissionalDefaults?.especialidade_id,
			profissionalDefaults?.especialidade,
			profissionalDefaults?.especialidade_nome,
			item?.especialidade?.id,
			item?.especialidade?.nome,
			item?.especialidade_id,
			item?.nome_especialidade,
			item?.resumo?.especialidade,
		);
		const turnoFormularioLabel = resolveEscalaItemShiftLabel(
			itemFormulario,
			horarioFormulario,
			responseFormulario?.defaults,
			responseFormulario?.defaults?.horario,
			item,
			item?.dados_edicao,
			item?.resumo,
		);
		const intervaloEspecial = getSpecialContinuousPeriod({
			items: todosItensGrade,
			currentItem:
				Object.keys(itemFormulario || {}).length > 0
					? itemFormulario
					: item,
			fallbackDate:
				horarioFormulario?.data ||
				item?.celula?.data ||
				item?.data ||
				'',
		});
		const dataInicioFormulario =
			intervaloEspecial?.dataInicio ||
			responseFormulario?.defaults?.periodo?.data_inicio ||
			horarioFormulario?.data ||
			responseFormulario?.defaults?.horario?.data ||
			'';
		const dataFimFormulario =
			intervaloEspecial?.dataFim ||
			itemFormulario?.data_fim ||
			itemFormulario?.periodo?.data_fim ||
			responseFormulario?.defaults?.periodo?.data_fim ||
			'';

		setPopupExtraFields((prevState) => ({
			...prevState,
			observacao:
				observacaoOficialItem ||
				responseFormulario?.defaults?.observacao ||
				prevState.observacao ||
				'',
			dataInicio:
				(isVacanciaItem ? '' : dataInicioFormulario) ||
				prevState.dataInicio ||
				'',
			dataFim:
				(isVacanciaItem ? '' : dataFimFormulario) ||
				prevState.dataFim ||
				'',
			dataInicioVacancia:
				prevState.dataInicioVacancia ||
				itemFormulario?.data_inicio_vacancia ||
				responseFormulario?.defaults?.data_inicio_vacancia ||
				'',
		}));

		setCpf((prevState) =>
			isVacanciaItem
				? ''
				: prevState ||
					profissionalFormulario?.cpf ||
					itemFormulario?.cpf ||
					'',
		);

		setCurrentPro((prevState) => ({
			...prevState,
			vinculoNome:
				prevState.vinculoNome ||
				getVinculoDisplayName(
					profissionalFormulario,
					itemFormulario,
					profissionalDefaults,
					item,
					item?.dados_edicao,
					item?.resumo,
				),
			nome: isVacanciaItem
				? prevState.nome || itemFormulario?.nome || 'Vacância'
				: prevState.nome ||
					profissionalFormulario?.nome ||
					profissionalDefaults?.nome ||
					itemFormulario?.nome ||
					'',
			cargo: isVacanciaItem
				? ''
				: resolveCargoValue(
						profissionalFormulario?.cargo,
						itemFormulario?.cargo,
						profissionalDefaults?.cargo,
						prevState.cargo,
					),
			admissao: isVacanciaItem
				? ''
				: prevState.admissao ||
					profissionalFormulario?.data_admissao ||
					itemFormulario?.data_admissao ||
					profissionalDefaults?.data_admissao ||
					'',
			numeroConselho: isVacanciaItem
				? ''
				: getNumeroConselhoFromProfissional(
						itemFormulario?.edicao?.profissional_por_cpf,
					) ||
					getNumeroConselhoFromProfissional(profissionalFormulario) ||
					getNumeroConselhoFromProfissional(profissionalDefaults) ||
					getNumeroConselhoFromProfissional(itemFormulario) ||
					prevState.numeroConselho ||
					'',
			ufConselho: isVacanciaItem
				? ''
				: prevState.ufConselho ||
					getUfConselhoFromProfissional(profissionalFormulario) ||
					getUfConselhoFromProfissional(profissionalDefaults) ||
					getUfConselhoFromProfissional(itemFormulario),
			telefone:
				!isVacanciaItem && formularioHasTelefoneField
					? prevState.telefone ||
						profissionalFormulario?.telefone ||
						profissionalDefaults?.telefone ||
						itemFormulario?.telefone ||
						''
					: '',
			vinculo:
				prevState.vinculo ||
				(profissionalFormulario?.vinculo_tipo_id
					? String(profissionalFormulario.vinculo_tipo_id)
					: itemFormulario?.vinculo_tipo_id
						? String(itemFormulario.vinculo_tipo_id)
						: profissionalDefaults?.vinculo_tipo_id
							? String(profissionalDefaults.vinculo_tipo_id)
							: itemFormulario?.vinculo
								? String(itemFormulario.vinculo)
								: profissionalDefaults?.vinculo
									? String(profissionalDefaults.vinculo)
									: ''),
			especialidade:
				prevState.especialidade || especialidadeFormularioValue,
			regime: String(
				prevState.regime ||
					contextoFormulario?.regime ||
					itemFormulario?.regime ||
					responseFormulario?.defaults?.contexto_competencia
						?.regime ||
					popupRegime ||
					'rotina',
			).toLowerCase(),
			turnoLabel: prevState.turnoLabel || turnoFormularioLabel,
			setor:
				prevState.setor ||
				(contextoFormulario?.setor_id
					? String(contextoFormulario.setor_id)
					: itemFormulario?.setor
						? String(itemFormulario.setor)
						: profissionalFormulario?.setor_id
							? String(profissionalFormulario.setor_id)
							: profissionalDefaults?.setor_id
								? String(profissionalDefaults.setor_id)
								: ''),
			dias:
				diasConsolidados.length > 0
					? diasConsolidados
					: diasRecorrentesPorOcorrencia.length > 0
						? diasRecorrentesPorOcorrencia
						: diasRecorrentes.length > 0
							? diasRecorrentes
							: prevState.dias,
			turnos:
				diasConsolidados.length > 0
					? turnosConsolidados
					: Object.keys(turnosRecorrentesPorOcorrencia).length > 0
						? turnosRecorrentesPorOcorrencia
						: diasRecorrentes.length > 0
							? diasRecorrentes.reduce((acc, dia) => {
									acc[dia] = {
										inicio: String(
											horarioRecorrente?.hora_inicio ||
												'',
										).slice(0, 2),
										fim: String(
											horarioRecorrente?.hora_fim || '',
										).slice(0, 2),
									};
									return acc;
								}, {})
							: prevState.turnos,
		}));
		setIsModalLoading(false);
	};

	handleEditItemRef.current = handleEditItem;

	const normalizeUnitSearch = useCallback(
		(value = '') =>
			String(value)
				.normalize('NFD')
				.replace(/[\u0300-\u036f]/g, '')
				.toLowerCase()
				.trim(),
		[],
	);

	const formatUnidadeOptionLabel = useCallback(
		(unidade) => {
			const nome = String(unidade?.nome || '').trim();
			return normalizeUnitSearch(nome) ===
				normalizeUnitSearch('CER Jacarepaguá')
				? 'Hospital Cardoso Fontes'
				: nome;
		},
		[normalizeUnitSearch],
	);

	const unidadeOptionsFiltradas = unidadeOptions.filter((unidade) => {
		const termo = normalizeUnitSearch(unidadeSearchTerm);
		if (normalizeUnitSearch(unidade?.nome || '') === 'cer andarai') {
			return false;
		}
		if (!termo) return true;

		const nome = normalizeUnitSearch(
			formatUnidadeOptionLabel(unidade),
		);
		const cnes = normalizeUnitSearch(unidade?.cnes || '');
		return nome.includes(termo) || cnes.includes(termo);
	});

	const identificadorOptions = useMemo(() => {
		const tipoSelecionado = String(
			searchFilters.tipoSetor || searchFilters.servico || '',
		);

		return sortSetoresPorAndar(
			identificadorCatalogOptions.filter((identificador) => {
				if (!tipoSelecionado) return true;
				return (
					String(identificador.tipo_secao_id || '') ===
						tipoSelecionado ||
					String(identificador.servico_id || '') === tipoSelecionado
				);
			}),
		);
	}, [
		identificadorCatalogOptions,
		searchFilters.tipoSetor,
		searchFilters.servico,
	]);

	useEffect(() => {
		if (!searchFilters.identificador || !identificadorOptions.length) {
			return;
		}

		const identificadorSelecionado = identificadorOptions.find(
			(item) =>
				String(item.value) === String(searchFilters.identificador),
		);

		if (
			identificadorSelecionado?.setor_id &&
			String(searchFilters.setor || '') !==
				String(identificadorSelecionado.setor_id)
		) {
			setSearchFilters((prevState) => ({
				...prevState,
				setor: String(identificadorSelecionado.setor_id),
			}));
		}
	}, [
		identificadorOptions,
		searchFilters.identificador,
		searchFilters.setor,
	]);

	const unidadeInputInvalida =
		showUnidadeFilter &&
		String(unidadeSearchTerm || '').trim() !== '' &&
		!searchFilters.unidade;

	const handleUnidadeInputChange = (value) => {
		setUnidadeSearchTerm(value);
		setShowUnidadeSuggestions(true);

		const inputNormalizado = normalizeUnitSearch(value);

		const unidadeCorrespondente = unidadeOptions.find((unidade) => {
			const label = normalizeUnitSearch(
				formatUnidadeOptionLabel(unidade),
			);
			const cnes = normalizeUnitSearch(unidade?.cnes || '');
			return label === inputNormalizado || cnes === inputNormalizado;
		});

		setSearchFilters((prevState) => ({
			...prevState,
			unidade: unidadeCorrespondente
				? String(unidadeCorrespondente.cnes)
				: '',
			tipoSetor: unidadeCorrespondente ? prevState.tipoSetor : '',
			servico: unidadeCorrespondente ? prevState.servico : '',
			setor: unidadeCorrespondente ? prevState.setor : '',
		}));
	};

	const handleSelectUnidade = (unidade) => {
		setUnidadeSearchTerm(formatUnidadeOptionLabel(unidade));
		setSearchFilters((prevState) => ({
			...prevState,
			unidade: String(unidade.cnes),
		}));
		setShowUnidadeSuggestions(false);
	};

	useEffect(() => {
		if (!showUnidadeFilter) return;
		if (!searchFilters.unidade) return;

		const unidadeAtual = unidadeOptions.find(
			(unit) => String(unit.cnes) === String(searchFilters.unidade),
		);
		if (!unidadeAtual) return;

		const displayValue = formatUnidadeOptionLabel(unidadeAtual);
		if (
			normalizeUnitSearch(unidadeSearchTerm) !==
			normalizeUnitSearch(displayValue)
		) {
			setUnidadeSearchTerm(displayValue);
		}
	}, [
		showUnidadeFilter,
		searchFilters.unidade,
		unidadeOptions,
		unidadeSearchTerm,
		formatUnidadeOptionLabel,
		normalizeUnitSearch,
	]);

	const semSetores = setorOptions.length === 0;
	const semEspecialidadesModal = modalEspecialidadeOptions.length === 0;

	if (authStatus === 'loading') {
		return (
			<div className='flex justify-center items-center min-h-screen w-full bg-[#F0F4F8]'>
				<div
					className='flex justify-center items-center'
					role='status'>
					<svg
						aria-hidden='true'
						className='w-8 h-8 text-gray-200 animate-spin fill-blue-600'
						viewBox='0 0 100 101'
						fill='none'
						xmlns='http://www.w3.org/2000/svg'>
						<path
							d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
							fill='currentColor'
						/>
						<path
							d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
							fill='currentFill'
						/>
					</svg>
				</div>
			</div>
		);
	}

	return (
		<div className='flex flex-col mx-auto min-h-screen overflow-x-hidden'>
			{authStatus === 'authorized' ? (
				<div className='text-center flex-grow mt-[64px] sm:mt-[80px]'>
					<div className='flex flex-col w-[100%] lg:w-[100%] mx-auto text-wrap text-center flex-grow bg-[#F0F4F8]'>
						<div className='mx-[7%] text-center md:text-center flex justify-center text-[#13335A] font-semibold text-[26px] sm:text-[28px] py-[28px] sm:py-[36px]'>
							Escala Médica
						</div>
					</div>
					{/* Filtros */}
					<div className='flex my-[18px] flex-col items-center w-full mx-auto rounded-md mb-8 px-2 sm:px-0'>
						<div className='w-[90%] sm:w-[95%] flex justify-start gap-2  py-5 items-center max-w-[1600px]'>
							<Link
								to='/escala'
								className='inline-flex items-center  gap-1.5 text-[18px] font-medium text-[#17395f]  hover:text-[#60758f] duration-300 transition-colors mb-4'>
								<MdArrowBackIosNew size={15} />
								Voltar
							</Link>
						</div>
						<div className='w-[90%] sm:w-[95%] flex flex-col gap-2 border border-gray-100 rounded-lg shadow-md p-3 sm:p-5 items-center max-w-[1600px]'>
							<div className='flex flex-row justify-between items-center w-full flex-wrap gap-2 '>
								<p className='text-[#13335A] font-medium text-[24px] text-start'>
									Filtros de pesquisa
								</p>
								<div className='flex w-full sm:w-auto shadow-md rounded-md overflow-hidden'>
									<button
										type='button'
										onClick={() => setViewMode('setor')}
										className={`flex-1 font-inter sm:flex-none px-2 sm:px-3 py-1.5 w-auto sm:w-[230px] min-w-0 text-[12px] sm:text-[14px] leading-tight font-normal border transition-all duration-200 ${
											viewMode === 'setor'
												? 'bg-[#004A80] text-white border-[#004A80] border-[1px]'
												: 'bg-white rounded-l-lg text-[#13335A]  hover:bg-[#F3F6FA] border-[#004A80] border-[1px]'
										}`}>
										<span className='sm:hidden'>
											Por setor
										</span>
										<span className='hidden sm:inline'>
											Visualizar por Setor
										</span>
									</button>
									<button
										type='button'
										onClick={() =>
											setViewMode('especialidade')
										}
										className={`flex-1 font-inter sm:flex-none px-2 sm:px-3 py-1.5 w-auto sm:w-[230px] min-w-0 text-[12px] sm:text-[14px] leading-tight font-normal border transition-all duration-200 ${
											viewMode === 'especialidade'
												? 'bg-[#004A80] text-white border-[#004A80] border-[1px]'
												: 'bg-white text-[#13335A]  hover:bg-[#F3F6FA] rounded-r-lg border-[#004A80] border-[1px]'
										}`}>
										<span className='sm:hidden'>
											Por área de atuação
										</span>
										<span className='hidden sm:inline'>
											Visualizar por Área de atuação
										</span>
									</button>
								</div>
							</div>
							<hr className='w-full border-t border-gray-200 my-2' />
							{showUnidadeFilter && (
								<div className='w-full self-start mb-3 relative'>
									<label
										htmlFor='unidade'
										className='font-medium text-[16px] text-start text-[#13335A] block'>
										Unidade
									</label>
									<input
										id='unidade'
										type='text'
										value={unidadeSearchTerm}
										onChange={(e) =>
											handleUnidadeInputChange(
												e.target.value,
											)
										}
										onFocus={() =>
											setShowUnidadeSuggestions(true)
										}
										onBlur={() => {
											setTimeout(
												() =>
													setShowUnidadeSuggestions(
														false,
													),
												120,
											);
										}}
										placeholder='Digite para buscar e selecionar unidade'
										className={`border rounded-[8px] px-3 py-2 mt-1 w-full font-medium text-[#6b7280] ${
											unidadeInputInvalida
												? 'border-[#dc2626]'
												: 'border-[#E3E3E3]'
										}`}
									/>
									{showUnidadeSuggestions && (
										<div className='absolute left-0 right-0 top-full mt-1 z-30 w-full max-h-[220px] overflow-y-auto rounded-[8px] border border-[#E3E3E3] bg-white shadow-sm'>
											{unidadeOptionsFiltradas.length >
											0 ? (
												unidadeOptionsFiltradas.map(
													(unidade) => (
														<button
															type='button'
															key={unidade.cnes}
															onMouseDown={(
																e,
															) => {
																e.preventDefault();
																handleSelectUnidade(
																	unidade,
																);
															}}
															className='block w-full px-3 py-2 text-left text-[14px] text-[#4F5257] hover:bg-[#F3F4F6]'>
															{formatUnidadeOptionLabel(
																unidade,
															)}
														</button>
													),
												)
											) : (
												<div className='px-3 py-2 text-[13px] text-[#9CA3AF]'>
													Nenhuma unidade encontrada
												</div>
											)}
										</div>
									)}
								</div>
							)}
							<div className='flex flex-col md:flex-row justify-between items-end gap-4 md:gap-[32px] w-full'>
								{viewMode === 'especialidade' ? (
									<div className='flex flex-col w-full min-w-0'>
										<p className='text-[18px] text-[#13335A] font-medium text-start'>
											Área de atuação
										</p>
										<div className='relative'>
											<select
												name='especialidade'
												value={
													searchFilters.especialidade
												}
												onChange={(e) =>
													handleFilterInputChange(
														'especialidade',
														e.target.value,
													)
												}
												className='border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full font-semibold text-[#949494] appearance-none'>
												<option value=''>
													Selecione uma área de atuação
												</option>
												{especialidadeOptions.map(
													(tipo) => (
														<option
															key={tipo.id}
															value={tipo.id}>
															{tipo.nome}
														</option>
													),
												)}
											</select>
											<span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#d1d5db]'>
												<svg
													width='18'
													height='18'
													fill='none'
													viewBox='0 0 24 24'>
													<path
														fill='currentColor'
														d='M7 10l5 5 5-5H7z'
													/>
												</svg>
											</span>
										</div>
									</div>
								) : (
									<div className='flex flex-col w-full min-w-0'>
										<label
											htmlFor='setor'
											className='font-medium text-[18px] text-start text-[#13335A]'>
											Setor
										</label>
											<SearchableSetorSelect
												value={searchFilters.setor}
												options={setorOptions}
												onChange={(value) =>
													handleFilterInputChange('setor', value)
												}
												disabled={semSetores}
												emptyMessage='Nenhum setor cadastrado para esta unidade'
												className='mt-1 rounded-lg border border-gray-300 px-3 py-2 font-semibold text-[#949494] outline-none'
											/>
											{/* 	{!searchFilters.servico && (
												<span className='pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-[#949494] font-semibold'>
													Selecione o serviço
													</span>
												)} */}
									</div>
								)}
								{(viewMode === 'setor' ||
									viewMode === 'especialidade') && (
									<div className='flex flex-col w-full min-w-0'>
										<p className='text-[18px] text-[#13335A] font-medium text-start'>
											Regime
										</p>
										<div className='relative'>
											<select
												name='regime'
												value={searchFilters.regime}
												onChange={(e) =>
													handleFilterInputChange(
														'regime',
														e.target.value,
													)
												}
												className='border border-gray-300 rounded-lg px-3 py-2 mt-1 w-full font-semibold text-[#949494] appearance-none'>
												{(regimeOptions.length
													? regimeOptions
													: [
															{
																value: 'rotina',
																label: 'Rotina',
															},
															{
																value: 'plantonista',
																label: 'Plantonista',
															},
														]
												).map((regime) => (
													<option
														key={regime.value}
														value={regime.value}>
														{regime.label}
													</option>
												))}
											</select>
											<span className='pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[#d1d5db]'>
												<svg
													width='18'
													height='18'
													fill='none'
													viewBox='0 0 24 24'>
													<path
														fill='currentColor'
														d='M7 10l5 5 5-5H7z'
													/>
												</svg>
											</span>
										</div>
									</div>
								)}
								{/* DATA VIGENTE */}
								<div className='bg-white w-full md:w-auto md:min-w-[460px] rounded-lg min-w-0'>
									<p className='text-[16px] text-[#13335A] font-medium mb-2 text-start font-inter'>
										Data vigente
									</p>
									<div className='flex flex-col sm:flex-row gap-2 justify-between'>
										{/* Mês */}
										<div className='flex flex-col items-start w-full'>
											<span className='text-xs text-[#757575] mb-1 ml-1 '>
												Mês
											</span>
											<div className='relative w-full'>
												<span className='absolute right-3 top-1/2 -translate-y-1/2 text-[#a9a9a9] pointer-events-none'>
													<svg
														width='16'
														height='16'
														fill='none'
														viewBox='0 0 24 24'>
														<path
															fill='currentColor'
															d='M7 10l5 5 5-5H7z'
														/>
													</svg>
												</span>
												<select
													value={
														searchFilters.mesVigente
													}
													onChange={(e) =>
														handleFilterInputChange(
															'mesVigente',
															e.target.value,
														)
													}
													className='border border-gray-300 rounded-lg pl-3 pr-10 w-full py-2 appearance-none text-start text-[#a0a0a0]'>
													<option
														value=''
														disabled
														hidden
														className=''>
														Mês
													</option>
													{new Array(12)
														.fill(null)
														.map((_, i) => {
															const month =
																String(
																	i + 1,
																).padStart(
																	2,
																	'0',
																);
															return (
																<option
																	className=''
																	key={month}
																	value={
																		month
																	}>
																	{moment()
																		.month(
																			i,
																		)
																		.format(
																			'MMMM',
																		)}
																</option>
															);
														})}
												</select>
											</div>
										</div>
										{/* Ano */}
										<div className='flex flex-col items-start w-full'>
											<span className='text-xs text-[#757575] mb-1 ml-1'>
												Ano
											</span>
											<div className='relative w-full'>
												<span className='absolute right-3 top-1/2 -translate-y-1/2 text-[#a9a9a9] pointer-events-none'>
													<svg
														width='16'
														height='16'
														fill='none'
														viewBox='0 0 24 24'>
														<path
															fill='currentColor'
															d='M7 10l5 5 5-5H7z'
														/>
													</svg>
												</span>
												<select
													value={
														searchFilters.anoVigente
													}
													onChange={(e) =>
														handleFilterInputChange(
															'anoVigente',
															e.target.value,
														)
													}
													className='border border-gray-300 rounded-lg pl-3 pr-10 py-2 w-full appearance-none text-start text-[#9b9b9b]'>
													<option
														value=''
														disabled
														hidden>
														Ano
													</option>
													{Array.from(
														{ length: 11 },
														(_, i) => {
															const year =
																new Date().getFullYear() -
																i;
															return (
																<option
																	key={year}
																	value={
																		year
																	}>
																	{year}
																</option>
															);
														},
													)}
												</select>
											</div>
										</div>
									</div>
								</div>
							</div>
							{/* Botão Enviar */}
							<div className='flex justify-center w-full'>
								<button
									onClick={handleFilterSearch}
									className='bg-[#FFFFFF] text-[#13335A]  font-inter border border-[#ebebeb] text-[16px] mt-5 px-4 py-2 rounded-md hover:bg-[#13335A] hover:text-white
                  hover:border-[#13335A] transition-all duration-300 shadow-md'
									style={{
										width: '100%',
										maxWidth: '200px',
									}}>
									<IoIosSearch className='inline-block mr-2 w-[20px] h-auto' />
									Buscar Escala
								</button>
							</div>
						</div>
					</div>

					<div className='grid grid-cols-1 sm:grid-cols-3 max-w-[1600px] w-[90%] sm:w-[95%] mt-4 mb-6 sm:mb-8 items-stretch sm:items-center gap-3 sm:gap-2 mx-auto'>
						<div className='hidden sm:block' />
						<div className='flex font-inter justify-center w-full gap-3 flex-wrap'>
							{/* <button
								onClick={abrirModalAdicionarEscala}
								className='flex items-center justify-between gap-2 bg-[#004A80] text-white px-5 py-2 hover:bg-[#0d466c] transition w-full max-w-[220px] rounded-[7px] shadow-md'>
								<span className='text-2xl leading-none mb-1 flex items-center'>
									+
								</span>
								<span className='inline-block w-[2px] h-6 bg-[#003D69] rounded-full'></span>
								<span className='flex items-center h-full'>
									Adicionar à Escala
								</span>
							</button> */}
							{canAccessProfessionalManagement && (
								<button
									onClick={() =>
										navigate('/escala-profissionais', {
											state: { escalaOrigem: 'medica' },
										})
									}
									className='flex w-[250px] items-center justify-center gap-2 sm:gap-3 bg-[#004A80] text-[#FFFFFF] px-3 py-1.5 text-[13px] sm:px-4 sm:py-2 sm:text-base font-light hover:bg-[#0d466c] transition rounded-[7px] shadow-md'>
									<IoSettingsOutline className='w-[16px] h-[16px] sm:w-[20px] sm:h-[20px]' />

									<span className='inline-block w-[2px] h-5 sm:h-6 bg-[#003D69] rounded-full'></span>
									<span className='flex items-center h-full'>
										Gerenciar Profissionais
									</span>
								</button>
							)}
						</div>
						{MOSTRAR_SOLICITACAO_TROCA_UI &&
							!canAccessProfessionalManagement && (
							<div className='flex justify-center sm:justify-end font-inter'>
								<button
									type='button'
									onClick={abrirModalSolicitacaoTroca}
									className='flex w-[220px] items-center justify-center border border-[#004A80] text-[#004A80] bg-white px-3 py-1.5 text-[13px] sm:px-4 sm:py-2 sm:text-base transition rounded-[7px] shadow-sm hover:bg-[#004A80] hover:text-white font-medium font-inter'>
									Solicitar troca
								</button>
							</div>
						)}
					</div>

					{(hasInitialCachedFilters ||
						gradePayload !== null ||
						gradeEspecialidadePayloads.length > 0 ||
						carregando ||
						gradeMessage !== '' ||
						gradeEspecialidadeMessage !== '') && (
						<>
							{carregando ? (
								<div
									className='flex justify-center h-[300px] content-center w-full my-20'
									role='status'>
									<svg
										aria-hidden='true'
										className='w-8 h-8 text-gray-200 animate-spin  fill-blue-600'
										viewBox='0 0 100 101'
										fill='none'
										xmlns='http://www.w3.org/2000/svg'>
										<path
											d='M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z'
											fill='currentColor'
										/>
										<path
											d='M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z'
											fill='currentFill'
										/>
									</svg>
								</div>
							) : unidadeObrigatoriaPendente ? (
								<div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>
									Selecione uma unidade para visualizar e
									gerenciar a escala.
								</div>
							) : viewMode === 'especialidade' ? (
								gradeEspecialidadePayloads.length > 0 ? (
									<div className='flex flex-col gap-10'>
										{/* 	aaaa */}
										{gradeEspecialidadePayloads.map(
											(grade, idx) => (
												<ApiScaleGrid
													key={idx}
													payload={grade}
													tituloEscala={
														grade?.contexto?.setor
															?.nome_exibicao ||
														grade?.contexto?.setor
															?.nome ||
														`Setor ${idx + 1}`
													}
													onEditItem={handleEditItem}
													onDeleteItem={
														handleDeleteItem
													}
													canManageItems={
														!unidadeObrigatoriaPendente
													}
													canModifyItems={
														isPrivilegedEscalaUser
													}
													regime={
														appliedFilters?.regime ||
														searchFilters.regime
													}
												/>
											),
										)}
									</div>
								) : (
									<div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>
										{gradeEspecialidadeMessage}
									</div>
								)
							) : gradePayload ? (
								<ApiScaleGrid
									payload={gradePayload}
									agruparPorEspecialidade
									onEditItem={handleEditItem}
									onDeleteItem={handleDeleteItem}
									canManageItems={!unidadeObrigatoriaPendente}
									canModifyItems={isPrivilegedEscalaUser}
									regime={
										appliedFilters?.regime ||
										searchFilters.regime
									}
								/>
							) : (
								<div className='w-full text-center py-14 text-[#757575] text-[18px] font-medium'>
									{gradeMessage}
								</div>
							)}
						</>
					)}
					{/* <VanillaTableScale
						onResize={handleTableScaleResize}
						inputsCabeçalho={cabeçalho}
						onEdit={handleEditUser} 
					/> */}
				</div>
			) : (
				<section className='bg-[#13335A] rounded-md mx-2 sm:w-[50%] sm:mx-auto my-auto'>
					<div className='py-8 px-4 mx-auto max-w-screen-xl lg:py-16 lg:px-6'>
						<div className='mx-auto max-w-screen-sm text-center'>
							<h1 className='mb-4 text-white text-6xl tracking-tight font-extrabold lg:text-7xl text-primary-600 '>
								401
							</h1>
							<p className='mb-10 text-3xl tracking-tight font-bold text-slate-100 md:text-4xl '>
								Acesso Negado
							</p>
							<p className='mb-7 text-lg font-light text-gray-300 '>
								Você não possui permissão para acessar este
								campo. Entre em contato com o administrador(a)
								da sua unidade para solicitação de acesso
							</p>
							<a
								href='/login'
								className='inline-flex hover:scale-105 transition-transform text-gray-300 bg-primary-600 hover:bg-primary-800 focus:ring-4 focus:outline-none focus:ring-primary-300 font-medium rounded-lg text-sm px-5 py-2.5 text-center  my-4'>
								Volte para a Tela de Login
							</a>
						</div>
					</div>
				</section>
			)}

					{MOSTRAR_SOLICITACAO_TROCA_UI &&
						showSolicitacaoTrocaModal && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4 overflow-y-auto overflow-x-hidden'>
					<div className='bg-white rounded-lg shadow-2xl max-w-[720px] w-full sm:w-[92%] relative border border-[#E3E3E3] overflow-hidden max-h-[95vh] sm:max-h-[90vh] mx-auto'>
						<div className='w-full bg-[#F1F1F1] h-8 flex items-center justify-end px-3'>
							<button
								type='button'
								onClick={fecharModalSolicitacaoTroca}
								aria-label='Fechar modal de solicitação de troca'
								className='text-[#BDBDBD] transition-all duration-300 hover:text-[#888888] text-2xl font-bold mb-1'>
								&times;
							</button>
						</div>

						<div className='px-3 py-4 sm:px-5 sm:py-5 overflow-y-auto overflow-x-hidden max-h-[calc(95vh-2rem)] sm:max-h-[calc(90vh-2rem)]'>
							<div className='w-full'>
								<h2 className='bg-[#967771] px-4 py-3 text-center font-inter text-[18px] sm:text-[20px] font-semibold text-white rounded-lg'>
									Solicitar troca
								</h2>

								{solicitacaoTrocaEnviada ? (
									<div className='flex min-h-[260px] flex-col items-center justify-center gap-4 bg-white py-8 font-inter'>
										<div className='flex h-16 w-16 items-center justify-center rounded-full border-2 border-[#7EAF89] text-[#4F8A5B]'>
											<MdCheck className='h-10 w-10' />
										</div>
										<p className='text-center text-[17px] font-medium text-[#777777]'>
											Solicitação enviada com sucesso
										</p>
									</div>
								) : (
									<div className='flex flex-col gap-4 bg-white py-4 sm:py-5 font-inter'>
										<div>
											<label
												htmlFor='solicitacao-troca-origem'
												className='mb-1 block text-[14px] font-medium text-[#0d466c] text-start'>
												Dia de origem
											</label>
											<select
												id='solicitacao-troca-origem'
												value={
													solicitacaoTrocaForm.origemId
												}
												onChange={(event) =>
													handleSolicitacaoTrocaOrigemChange(
														event.target.value,
													)
												}
												disabled={
													solicitacaoTrocaLoading
												}
												className='w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-[14px] text-[#555555] outline-none transition focus:border-[#3F7BA7] disabled:cursor-not-allowed disabled:bg-[#E9E9E9]'>
												<option value=''>
													{solicitacaoTrocaLoading
														? 'Buscando suas escalas...'
														: 'Selecione o dia de origem'}
												</option>
												{solicitacaoTrocaOrigens.map(
													(option) => (
														<option
															key={option.value}
															value={
																option.value
															}>
															{option.label}
														</option>
													),
												)}
											</select>
										</div>

										<div className='grid grid-cols-1 gap-4 sm:grid-cols-3'>
											<div>
												<label
													htmlFor='solicitacao-troca-profissional'
													className='mb-1 block text-[14px] font-medium text-[#0d466c] text-start'>
													Trocar com:
												</label>
												<select
													id='solicitacao-troca-profissional'
													value={
														solicitacaoTrocaForm.trocaCom
													}
													onChange={(event) =>
														handleSolicitacaoTrocaFieldChange(
															'trocaCom',
															event.target.value,
														)
													}
													disabled={
														!solicitacaoTrocaForm.origemId ||
														solicitacaoTrocaLoading
													}
													className='w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-[14px] text-[#555555] outline-none transition focus:border-[#3F7BA7] disabled:cursor-not-allowed disabled:bg-[#E9E9E9]'>
													<option value=''>
														{solicitacaoTrocaLoading
															? 'Carregando...'
															: 'Selecione'}
													</option>
													{solicitacaoTrocaProfissionais.map(
														(option) => (
															<option
																key={
																	option.value
																}
																value={
																	option.value
																}>
																{option.label}
															</option>
														),
													)}
												</select>
											</div>

											<div>
												<label
													htmlFor='solicitacao-troca-data'
													className='mb-1 block text-[14px] font-medium text-[#0d466c] text-start'>
													Dia
												</label>
												<select
													id='solicitacao-troca-data'
													value={
														solicitacaoTrocaForm.data
													}
													onChange={(event) =>
														handleSolicitacaoTrocaFieldChange(
															'data',
															event.target.value,
														)
													}
													disabled={
														!solicitacaoTrocaForm.trocaCom ||
														solicitacaoTrocaLoading
													}
													className='w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-[14px] text-[#555555] outline-none transition focus:border-[#3F7BA7] disabled:cursor-not-allowed disabled:bg-[#E9E9E9]'>
													<option value=''>
														Selecione
													</option>
													{solicitacaoTrocaDatas.map(
														(option) => (
															<option
																key={
																	option.value
																}
																value={
																	option.value
																}>
																{option.label}
															</option>
														),
													)}
												</select>
											</div>

											<div>
												<label
													htmlFor='solicitacao-troca-turno'
													className='mb-1 block text-[14px] font-medium text-[#0d466c] text-start'>
													Turno
												</label>
												<select
													id='solicitacao-troca-turno'
													value={
														solicitacaoTrocaForm.turnoId
													}
													onChange={(event) =>
														handleSolicitacaoTrocaFieldChange(
															'turnoId',
															event.target.value,
														)
													}
													disabled={
														!solicitacaoTrocaForm.data ||
														solicitacaoTrocaLoading
													}
													className='w-full rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-[14px] text-[#555555] outline-none transition focus:border-[#3F7BA7] disabled:cursor-not-allowed disabled:bg-[#E9E9E9]'>
													<option value=''>
														Selecione
													</option>
													{solicitacaoTrocaTurnos.map(
														(option) => (
															<option
																key={
																	option.value
																}
																value={
																	option.value
																}>
																{option.label}
															</option>
														),
													)}
												</select>
											</div>
										</div>

										<div>
											<label
												htmlFor='solicitacao-troca-justificativa'
												className='mb-1 block text-[14px] font-medium text-[#0d466c] text-start'>
												Justificativa
											</label>
											<textarea
												id='solicitacao-troca-justificativa'
												rows={4}
												maxLength={1000}
												value={
													solicitacaoTrocaForm.justificativa
												}
												onChange={(event) =>
													handleSolicitacaoTrocaFieldChange(
														'justificativa',
														event.target.value,
													)
												}
												placeholder='Informe a justificativa'
												className='w-full resize-none rounded-md border border-[#D1D5DB] bg-white px-3 py-2 text-[14px] text-[#555555] outline-none transition placeholder:text-[#A3A3A3] focus:border-[#3F7BA7]'
											/>
										</div>

										{solicitacaoTrocaFeedback.message && (
											<div
												role='status'
												className={`rounded-md border px-3 py-2 text-center text-[14px] font-medium ${
													solicitacaoTrocaFeedback.type ===
													'success'
														? 'border-[#A7D8B2] bg-[#EFFAF1] text-[#27763A]'
														: 'border-[#F0C7C7] bg-[#FFF4F4] text-[#A04141]'
												}`}>
												{
													solicitacaoTrocaFeedback.message
												}
											</div>
										)}

										<div className='flex justify-center pt-1'>
											<button
												type='button'
												onClick={
													handleEnviarSolicitacaoTroca
												}
												disabled={
													solicitacaoTrocaLoading ||
													!solicitacaoTrocaOrigens.length
												}
												className='w-full max-w-[250px] rounded-[7px] bg-[#004A80] px-4 py-2 text-[14px] font-medium text-white shadow-md transition hover:bg-[#0d466c] disabled:cursor-not-allowed disabled:bg-[#A8B6C0]'>
												Enviar
											</button>
										</div>
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
			)}

			{/* formulário em um modal */}
			{showAddScaleModal && (
				<div className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 p-2 sm:p-4 overflow-y-auto overflow-x-hidden'>
					<div className='bg-white rounded-lg shadow-2xl max-w-[800px] w-full sm:w-[92%] relative border border-[#E3E3E3] overflow-hidden max-h-[95vh] sm:max-h-[90vh] mx-auto'>
						<div className='w-full bg-[#F1F1F1] h-8 flex items-center justify-end px-3'>
							{/* {isEditingModal && (
								<span className='mr-auto text-[12px] font-medium text-[#0d466c]'>
									Atualizando card #{editingItemId}
								</span>
							)} */}
							<button
								onClick={fecharModalEscala}
								className='text-[#BDBDBD] transition-all duration-300 hover:text-[#888888] text-2xl font-bold mb-1'>
								&times;
							</button>
						</div>
						<div className='py-4 pb-6 px-3 sm:px-4 flex flex-col gap-5 relative overflow-y-auto overflow-x-hidden max-h-[calc(95vh-2rem)] sm:max-h-[calc(90vh-2rem)]'>
							{isModalLoading ? (
								<div className='w-full min-h-[280px] flex flex-col items-center justify-center gap-4'>
									<div className='animate-spin h-12 w-12 rounded-full border-4 border-[#E5E7EB] border-t-[#3F7BA7]' />
									<div className='space-y-2 w-full max-w-[520px]'>
										<div className='h-4 rounded-full bg-[#E5E7EB]' />
										<div className='h-4 rounded-full bg-[#E5E7EB] w-[90%]' />
										<div className='h-4 rounded-full bg-[#E5E7EB] w-[75%]' />
									</div>
								</div>
							) : modalViewLoadError ? (
								<div className='w-full min-h-[220px] flex items-center justify-center px-4'>
									<div className='w-full max-w-[520px] rounded-lg border border-[#F5C2C7] bg-[#FDECEE] px-4 py-3 text-center text-[14px] font-medium text-[#A61B29]'>
										{modalViewLoadError}
									</div>
								</div>
							) : isViewOnlyModal ? (
								(() => {
									const tipo = (
										popupStatus || 'alocacao'
									).toLowerCase();
									const colorByTipo = {
										vacancia: '#BD504E',
										ferias: '#6ACEF8',
										atestado: '#FB965C',
										folga: '#8BBD8B',
										licenca: '#A476C7',
										troca: '#7A4B3A',
										falta: '#545454',
										alocacao: '#4D6C92',
									};
									const labelByTipo = {
										vacancia: 'Vacância',
										ferias: 'Férias',
										atestado: 'Atestado',
										folga: 'Folga',
										licenca: 'Licença',
										troca: 'Troca',
										falta: 'Falta',
										alocacao: 'Alocação',
									};
									const cor = colorByTipo[tipo] || '#6ACEF8';
									const label = labelByTipo[tipo] || tipo;
									const fmtDate = (iso) => {
										if (!iso) return '';
										const parts = String(iso).split('-');
										return `${parts[2]}/${parts[1]}/${parts[0]}`;
									};
									let badgeText = label;
									if (tipo === 'vacancia') {
										const dt =
											popupExtraFields.dataInicioVacancia ||
											popupExtraFields.dataInicio;
										badgeText = `Vacância a partir de ${fmtDate(dt) || '--/--/----'}`;
									} else if (tipo === 'ferias') {
										badgeText = `Férias até ${fmtDate(popupExtraFields.dataFim) || '--/--/----'}`;
									} else if (
										tipo === 'atestado' ||
										tipo === 'folga' ||
										tipo === 'licenca'
									) {
										badgeText = `${label} até ${fmtDate(popupExtraFields.dataFim) || '--/--/----'}`;
									} else if (tipo === 'troca') {
										badgeText = 'Troca com:';
									} else if (tipo === 'falta') {
										badgeText = 'FALTA';
									} else {
										badgeText = `Alocação ${fmtDate(popupExtraFields.dataInicio) || '--/--/----'}`;
									}
									const nomePro =
										currentSearch.length > 0
											? currentSearch[0].nome
											: currentPro.nome;
									const telefonePro =
										currentSearch.length > 0
											? currentSearch[0].telefone
											: currentPro.telefone;
									const isVacanciaCard = tipo === 'vacancia';
									const numeroConselhoPro = isVacanciaCard
										? ''
										: getNumeroConselhoFromProfissional(
												currentSearch[0],
											) ||
											getNumeroConselhoFromProfissional(
												popupFormularioAssistivo?.item
													?.edicao
													?.profissional_por_cpf,
											) ||
											currentPro.numeroConselho ||
											getNumeroConselhoFromProfissional(
												popupFormularioAssistivo?.item
													?.profissional_por_cpf,
											) ||
											getNumeroConselhoFromProfissional(
												popupFormularioAssistivo?.item
													?.profissional,
											) ||
											getNumeroConselhoFromProfissional(
												popupFormularioAssistivo
													?.defaults
													?.profissional_por_cpf,
											) ||
											getNumeroConselhoFromProfissional(
												popupFormularioAssistivo
													?.defaults?.profissional,
											) ||
											'';
									const shouldShowTelefonePro =
										canAccessProfessionalManagement &&
										canViewProfessionalPhone &&
										Boolean(
											String(telefonePro || '').trim(),
										);
									const vinculoTipoIdModal =
										getVinculoTipoIdFromProfissionalPorCpf(
											popupFormularioAssistivo?.item
												?.profissional_por_cpf,
											popupFormularioAssistivo?.defaults
												?.profissional_por_cpf,
										) || currentPro.vinculo;
									const vinculoLabelModal =
										vinculoTipoIdModal == null ||
										String(vinculoTipoIdModal).trim() === ''
											? 'não informado'
											: getVinculoOptionLabel(
													vinculoOptions,
													vinculoTipoIdModal,
												) || 'não informado';
									const vinculoLabel =
										currentPro.vinculoNome || vinculoLabelModal;
									const admissaoLabel =
										fmtDate(currentPro.admissao) ||
										'não informado';
									const trocaComNome =
										getEscalaTrocaPartnerName(
											editingItemSnapshot,
											popupFormularioAssistivo?.item,
											popupFormularioAssistivo?.defaults,
											currentPro,
										);
									const isFaltaCard = tipo === 'falta';
									const badgeFieldValue =
										tipo === 'troca'
											? trocaComNome || ''
											: popupExtraFields.observacao;
									const especialidadeValue =
										resolveEspecialidadeSelectValue(
											currentPro.especialidade,
											popupFormularioAssistivo?.item
												?.especialidade_id,
											popupFormularioAssistivo?.item
												?.especialidade,
											popupFormularioAssistivo?.defaults
												?.especialidade_id,
											popupFormularioAssistivo?.defaults
												?.especialidade,
											editingItemSnapshot?.especialidade_id,
											editingItemSnapshot?.especialidade,
											editingItemSnapshot?.nome_especialidade,
											editingItemSnapshot?.dados_edicao
												?.especialidade_id,
											editingItemSnapshot?.dados_edicao
												?.especialidade,
											editingItemSnapshot?.dados_edicao
												?.nome_especialidade,
											editingItemSnapshot?.resumo
												?.especialidade,
										) || '';
									const especialidadeLabel =
										modalEspecialidadeOptions.find(
											(o) =>
												String(o.id) ===
												String(especialidadeValue),
										)?.nome ||
										String(especialidadeValue || '');
									const setorLabel =
										modalSetorOptions.find(
											(o) =>
												String(o.id) ===
												String(currentPro.setor),
										)?.nome_exibicao ||
										modalSetorOptions.find(
											(o) =>
												String(o.id) ===
												String(currentPro.setor),
										)?.nome ||
										String(currentPro.setor || '');
									const fieldCls =
										'border border-[#E3E3E3] rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 text-[12px] sm:text-[14px] leading-snug text-[#6B7280] bg-white min-h-[34px] sm:min-h-[40px] flex items-center break-words whitespace-normal';
									const labelCls =
										'text-[13px] sm:text-[16px] text-start text-[#13335A] mb-1';
									const regimeLabel =
										regimeOptions.find(
											(option) =>
												String(
													option.value || '',
												).toLowerCase() ===
												String(
													currentPro.regime ||
														popupRegime ||
														'',
												).toLowerCase(),
										)?.label ||
										(String(
											currentPro.regime ||
												popupRegime ||
												'',
										).toLowerCase() === 'plantonista'
											? 'Plantonista'
											: String(
														currentPro.regime ||
															popupRegime ||
															'',
												  )
												? 'Rotina'
												: '');
									const vacanciaTurnoLabel =
										currentPro.turnoLabel ||
										Object.entries(currentPro.turnos || {})
											.filter(
												([, turno]) =>
													turno?.inicio || turno?.fim,
											)
											.map(([dia, turno]) => {
												const inicio = String(
													turno?.inicio || '',
												).trim();
												const fim = String(
													turno?.fim || '',
												).trim();
												const faixa = [inicio, fim]
													.filter(Boolean)
													.map(
														(hora) =>
															`${hora.padStart(2, '0')}:00`,
													)
													.join(' às ');

												return dia && faixa
													? `${dia} ${faixa}`
													: faixa || dia;
											})
											.filter(Boolean)
											.join(' • ');
									return (
										<div className='flex flex-col gap-3 w-full font-inter'>
											{!isVacanciaCard && (
												<p className='font-semibold text-[16px] sm:text-[20px] leading-tight break-words text-[#13335A] flex justify-start'>
													{nomePro || ''}
												</p>
											)}

											{!isVacanciaCard &&
												!badgeText.includes(
													'Alocação',
												) && (
													<div>
														<div className='flex items-center gap-3'>
															<div
																style={{
																	backgroundColor:
																		cor,
																}}
																className={`rounded-lg px-2 py-1.5 sm:px-3 sm:py-2 flex w-full flex-col sm:flex-row items-stretch sm:items-center content-center gap-2 sm:gap-3 text-white text-[12px] sm:text-[14px] whitespace-normal sm:whitespace-nowrap shrink-0 ${isFaltaCard ? 'justify-center' : ''}`}>
																<span
																	className={
																		isFaltaCard
																			? 'w-full text-center text-[15px]'
																			: ''
																	}>
																	{badgeText}
																</span>
																{!isFaltaCard && (
																	<div
																		className={`${fieldCls} flex-1 font-semibold`}
																		style={{
																			borderColor:
																				cor,
																		}}>
																		{
																			badgeFieldValue
																		}
																	</div>
																)}
															</div>
														</div>
													</div>
												)}
											{!isVacanciaCard && <hr></hr>}
											{!isVacanciaCard ? (
												<>
													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Admissão
															</p>
															<div
																className={
																	fieldCls
																}>
																{admissaoLabel}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Vínculo
															</p>
															<div
																className={
																	fieldCls
																}>
																{vinculoLabel}
															</div>
														</div>
													</div>

													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Cargo
															</p>
															<div
																className={
																	fieldCls
																}>
																{currentPro.cargo ||
																	''}
															</div>
														</div>
														<div>
															<div className='flex gap-2'>
																<p
																	className={`${labelCls} flex-1`}>
																	Número do
																	conselho
																</p>
																<p
																	className={`${labelCls} w-[80px]`}>
																	UF
																</p>
															</div>

															<div className='flex gap-2'>
																<div
																	className={`${fieldCls} flex-1`}>
																	{numeroConselhoPro ||
																		currentPro.numeroConselho ||
																		''}
																</div>
																<div className='w-[80px]'>
																	<div
																		className={
																			fieldCls
																		}>
																		{currentPro.ufConselho ||
																			''}
																	</div>
																</div>
															</div>
														</div>
													</div>

													<div className='grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
												Área de atuação
															</p>
															<div
																className={
																	fieldCls
																}>
																{
																	especialidadeLabel
																}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Setor
															</p>
															<div
																className={
																	fieldCls
																}>
																{setorLabel}
															</div>
														</div>
													</div>

													{shouldShowTelefonePro && (
														<div>
															<p
																className={
																	labelCls
																}>
																Telefone
															</p>
															<div
																className={
																	fieldCls
																}>
																{telefonePro ||
																	''}
															</div>
														</div>
													)}
												</>
											) : (
												<div className='w-full rounded-lg bg-white p-3 sm:p-3'>
													<div
														style={{
															backgroundColor:
																cor,
														}}
														className='w-full rounded-lg px-4 py-3 text-center text-[20px] font-semibold uppercase tracking-wide text-white'>
														Vacância
													</div>
													<div className='mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4'>
														<div>
															<p
																className={
																	labelCls
																}>
																Início da
																vacância
															</p>
															<div
																className={
																	fieldCls
																}>
																{fmtDate(
																	popupExtraFields.dataInicioVacancia ||
																		popupExtraFields.dataInicio,
																) ||
																	'--/--/----'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
												Área de atuação
															</p>
															<div
																className={
																	fieldCls
																}>
																{especialidadeLabel ||
																	'--'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Vínculo
															</p>
															<div
																className={
																	fieldCls
																}>
																{vinculoLabel}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Regime
															</p>
															<div
																className={
																	fieldCls
																}>
																{regimeLabel ||
																	'--'}
															</div>
														</div>
														<div>
															<p
																className={
																	labelCls
																}>
																Turno
															</p>
															<div
																className={`${fieldCls} items-start py-3`}>
																<span className='whitespace-pre-wrap break-words'>
																	{vacanciaTurnoLabel ||
																		'--'}
																</span>
															</div>
														</div>
													</div>
													<div className='mt-4'>
														<p className={labelCls}>
															Observação
														</p>
														<div
															className={`${fieldCls} min-h-[120px] items-start py-3`}>
															<span className='whitespace-pre-wrap break-words'>
																{popupExtraFields.observacao ||
																	'--'}
															</span>
														</div>
													</div>
												</div>
											)}
										</div>
									);
								})()
							) : (
								<fieldset
									className={`flex flex-col gap-5 min-w-0`}>
									{/* <div className='w-full flex justify-center items-center'>
									<div className='flex flex-col gap-1 w-full sm:w-auto min-w-0'>
										<div className='flex flex-col sm:flex-row justify-center font-inter gap-2 sm:gap-3 items-stretch sm:items-center w-full sm:w-auto'>
											<p className='font-inter font-medium text-[#13335A]'>
												CPF
											</p>
											<input
												type='text'
												value={cpf}
												onChange={handleCpf}
												onInput={(e) => {
													e.target.value =
														e.target.value.slice(
															0,
															11,
														);
												}}
												placeholder='123456789-00'
												disabled={
													vacanciaOn ||
													isTrocaMode ||
													isProfessionalFormLocked
												}
												className={`border ${getFieldBorderClass('cpf')} rounded-lg py-1.5 outline-none px-3 text-[#666] w-full sm:w-[190px] ${vacanciaOn || isTrocaMode || isProfessionalFormLocked ? 'bg-[#e0e0e0] cursor-not-allowed' : 'bg-white'}`}
											/>
											<button
												onClick={() => handleSearch()}
												disabled={
													vacanciaOn ||
													isTrocaMode ||
													isProfessionalFormLocked
												}
												className={`px-[8px] py-[6px] sm:py-[3px] rounded-md border border-[#E3E3E3] transition-all shadow-md duration-200 w-full sm:w-auto ${vacanciaOn || isTrocaMode || isProfessionalFormLocked ? 'text-[#a0a0a0] cursor-not-allowed bg-[#f3f3f3]' : 'text-[#4F5257]  hover:bg-[#e9e9e9]'}`}>
												Pesquisar
											</button>
										</div>
										{cpfSearchWarning && !vacanciaOn && (
											<span className='text-[12px] leading-tight text-[#dc2626] sm:max-w-[360px]'>
												{cpfSearchWarning}
											</span>
										)}
									</div>
								</div> */}

									{/* <div className='w-full flex justify-between gap-2 flex-wrap sm:flex-nowrap'>
									{[
										{
											key: 'ferias',
											label: 'Férias',
											color: 'bg-[#42B9EB]',
											border: 'border border-[#2A95C2]',
										},
										{
											key: 'atestado',
											label: 'Atestado',
											color: 'bg-[#EB6D21]',
											border: 'border border-[#B14A16]',
										},
										{
											key: 'folga',
											label: 'Folga',
											color: 'bg-[#0B6B3A]',
											border: 'border border-[#06441F]',
											disabled: false,
										},
										{
											key: 'licenca',
											label: 'Licença',
											color: 'bg-[#8D148F]',
											border: 'border border-[#620E63]',
											disabled: false,
										},
										{
											key: 'troca',
											label: 'Troca',
											color: 'bg-[#7A4B3A]',
											border: 'border border-[#5A372B]',
											disabled: !trocaDisponivelNoModal,
										},
										{
											key: 'vacancia',
											label: 'Vacância',
											color: 'bg-[#B81A18]',
											border: 'border border-[#79120F]',
											disabled: false,
										},
									].map((status) => {
										const ativo =
											popupStatus === status.key;
										return (
											<button
												key={status.key}
												type='button'
												disabled={status.disabled}
												onClick={() =>
													handlePopupStatusSelect(
														status.key,
													)
												}
												className={`${status.color} ${status.border} text-white rounded-md h-[32px] w-full sm:w-[138px] gap-2 px-2 sm:px-3 py-1 flex items-center justify-center text-[14px] sm:text-[15px] font-normal ${status.disabled ? 'opacity-80 cursor-not-allowed' : ''}`}>
												<span className='text-[16px]'>
													{status.label}
												</span>
												<span className='relative flex items-center justify-center'>
													<span
														className={`block ${ativo ? 'bg-[#13335A]' : 'bg-[#cfcfcf]'}  h-[18px] w-[36px] rounded-full`}
													/>
													<span
														className={`absolute h-[14px] w-[14px] rounded-full bg-white/90  transition-all ${ativo ? 'translate-x-[9px]' : '-translate-x-[9px]'}`}
													/>
												</span>
											</button>
										);
									})}
								</div> */}
									<div className='w-full flex justify-start text-[20px] text-[#13335A] font-semibold'>
										{currentSearch.length > 0
											? currentSearch[0].nome
											: currentPro.nome}
									</div>
									{popupStatus === 'ferias' && (
										<div className='p-2 border rounded-lg w-full grid grid-cols-1 sm:grid-cols-2 gap-3'>
											<div className='w-full  '>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Data início
												</p>
												<input
													type='date'
													name='dataInicio'
													value={
														popupExtraFields.dataInicio
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getPopupDateFieldClass(
														'dataInicio',
													)}
													style={{ height: 40 }}
												/>
											</div>
											<div className='w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Data fim
												</p>
												<input
													type='date'
													name='dataFim'
													value={
														popupExtraFields.dataFim
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className={getPopupDateFieldClass(
														'dataFim',
													)}
													style={{ height: 40 }}
												/>
											</div>
										</div>
									)}

									{(popupStatus === 'atestado' ||
										popupStatus === 'folga' ||
										popupStatus === 'licenca') && (
										<div className='w-full grid grid-cols-1 sm:grid-cols-3 gap-3 border rounded-lg p-2'>
											<div className='col-span-1 font-inter grid grid-cols-1 sm:grid-cols-2 gap-3'>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data início
													</p>
													<input
														type='date'
														name='dataInicio'
														value={
															popupExtraFields.dataInicio
														}
														onChange={
															handlePopupExtraFieldChange
														}
														className={getPopupDateFieldClass(
															'dataInicio',
														)}
														style={{ height: 37 }}
													/>
												</div>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data fim
													</p>

													<input
														type='date'
														name='dataFim'
														value={
															popupExtraFields.dataFim
														}
														onChange={
															handlePopupExtraFieldChange
														}
														className={getPopupDateFieldClass(
															'dataFim',
														)}
														style={{ height: 37 }}
													/>
												</div>
											</div>

											<div className='col-span-1 sm:col-span-2 w-full font-inter'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Observação
												</p>
												<input
													type='text'
													name='observacao'
													placeholder='Observação'
													value={
														popupExtraFields.observacao
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className='border-[1px] w-full outline-none border-[#E3E3E3] rounded-md text-[14px] px-2 py-1 text-start text-[#8d8d8d]'
													style={{ height: 37 }}
												/>
											</div>
										</div>
									)}

									{popupStatus === 'troca' && (
										<div className='w-full grid grid-cols-1 sm:grid-cols-3 gap-3 bg-[#fcfcfc] p-2 rounded-lg border border-[#e0e0e0]'>
											<div className='col-span-1 sm:col-span-2 w-full'>
												<p className='text-[16px] font-medium text-start text-[#4F5257]'>
													Trocar com
												</p>
												<select
													value={currentPro.trocaCom}
													onChange={(e) =>
														handleTrocaInputChange(
															'trocaCom',
															e.target.value,
														)
													}
													disabled={carregandoTroca}
													className={`border-[1px] w-full outline-none ${getFieldBorderClass('trocaCom')} rounded-md text-[14px] px-2 py-1 text-start text-[#8d8d8d] ${carregandoTroca ? 'bg-[#E3E3E3] cursor-not-allowed' : ''}`}
													style={{ height: 37 }}>
													<option value=''>
														{carregandoTroca
															? 'Carregando candidatos...'
															: 'Selecione o profissional'}
													</option>
													{!carregandoTroca &&
														!profissionaisTrocaDisponiveis.length && (
															<option
																value=''
																disabled>
																{getEmptySelectMessage(
																	'troca',
																)}
															</option>
														)}
													{profissionaisTrocaDisponiveis.map(
														(profissional) => (
															<option
																key={
																	profissional.value
																}
																value={
																	profissional.value
																}>
																{
																	profissional.label
																}
															</option>
														),
													)}
												</select>
												{/* {trocaItemOrigem && (
											<p className='text-[12px] text-[#6B7280] mt-2 text-start'>
												Origem:{' '}
												{trocaItemOrigem?.nome_profissional ||
													currentPro.nome ||
													'Profissional'}{' '}
												•{' '}
												{formatarDataCurta(
													trocaItemOrigem?.data ||
														trocaItemOrigem
															?.dados_edicao
															?.data ||
														'',
												)}{' '}
												•{' '}
												{formatarFaixaTroca(
													trocaItemOrigem,
												)}
											</p>
										)} */}
											</div>
											<div className='col-span-1 font-inter grid grid-cols-1 sm:grid-cols-2 gap-3'>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Data
													</p>
													<select
														value={
															currentPro.trocaData
														}
														onChange={(e) =>
															handleTrocaInputChange(
																'trocaData',
																e.target.value,
															)
														}
														disabled={
															!currentPro.trocaCom ||
															carregandoTroca
														}
														className={`border-[1px] w-full outline-none ${getFieldBorderClass('trocaData')} rounded-md text-[14px] px-2 py-1 text-start text-[#8d8d8d] ${!currentPro.trocaCom || carregandoTroca ? 'bg-[#E3E3E3] cursor-not-allowed' : ''}`}
														style={{ height: 37 }}>
														<option value=''>
															00/00/0000
														</option>
														{datasTrocaDisponiveis.map(
															(item) => (
																<option
																	key={
																		item.value
																	}
																	value={
																		item.value
																	}>
																	{item.label}
																</option>
															),
														)}
													</select>
												</div>
												<div className='w-full'>
													<p className='text-[16px] font-medium text-start text-[#4F5257]'>
														Hora
													</p>
													<select
														value={
															currentPro.trocaHora
														}
														onChange={(e) =>
															handleTrocaInputChange(
																'trocaHora',
																e.target.value,
															)
														}
														disabled={
															!currentPro.trocaData ||
															carregandoTroca
														}
														className={`border-[1px] w-full outline-none ${getFieldBorderClass('trocaHora')} rounded-md text-[14px] px-2 py-1 text-start text-[#8d8d8d] ${!currentPro.trocaData || carregandoTroca ? 'bg-[#E3E3E3] cursor-not-allowed' : ''}`}
														style={{ height: 37 }}>
														<option value=''>
															00 às 00
														</option>
														{horasTrocaDisponiveis.map(
															(item) => (
																<option
																	key={
																		item.value
																	}
																	value={
																		item.itemId
																	}>
																	{item.label}
																</option>
															),
														)}
													</select>
												</div>
											</div>
										</div>
									)}

									<div className='w-full h-[1px] bg-[#E3E3E3]' />

									<div className='flex flex-col lg:flex-row gap-[12px] min-w-0'>
										{/* <div className='w-full lg:w-[300px] lg:min-w-[250px] lg:max-w-[300px] border border-[#E3E3E3] bg-white p-2 rounded-lg flex flex-col justify-start gap-[8px] font-inter text-[#757575] text-medium min-w-0'>
									
										<div>
											<div
												className={`border border-[#E3E3E3] bg-white items-start rounded-lg px-2 py-1 ${
													isScheduleTabLocked
														? 'opacity-80 pointer-events-none'
														: ''
												}`}>
												<p className='text-start font-16px text-[#13335A]'>
													Regime
												</p>
												<div className='flex flex-wrap justify-start gap-3 sm:gap-4 mt-1'>
													<label className='flex gap-1'>
														<input
															type='radio'
															name='regime'
															value='rotina'
															disabled={
																isScheduleTabLocked
															}
															checked={
																popupRegime ===
																'rotina'
															}
															onChange={(e) =>
																setPopupRegime(
																	e.target
																		.value,
																)
															}></input>
														Rotina
													</label>
													<label className='flex gap-1'>
														<input
															type='radio'
															name='regime'
															value='plantonista'
															disabled={
																isScheduleTabLocked
															}
															checked={
																popupRegime ===
																'plantonista'
															}
															onChange={(e) =>
																setPopupRegime(
																	e.target
																		.value,
																)
															}></input>
														Plantonista
													</label>
												</div>
											</div>
										</div>
										{semana.map((dia, index) => {
											const isChecked =
												currentPro.dias.includes(dia);
											return (
												<label
													htmlFor={
														isScheduleTabLocked
															? undefined
															: dia
													}
													className={`flex flex-col sm:flex-row sm:justify-between text-[13px] sm:text-[14px] gap-2 sm:gap-2 min-w-0 ${
														isScheduleTabLocked
															? 'pointer-events-none select-none opacity-80'
															: ''
													}`}
													key={index}>
													<div className='flex items-center gap-1'>
														<input
															className='cursor-pointer'
															id={dia}
															type='checkbox'
															checked={isChecked}
															disabled={
																isScheduleTabLocked
															}
															onChange={handleDia}
														/>
														{dia}
													</div>
													<div
														className={`flex w-full sm:w-auto justify-center sm:justify-start gap-[1px] px-1 py-[4px] border text-[16px] rounded-md border-[#e7e7e7] ${isChecked ? 'bg-white' : 'bg-[#e9e9e9] opacity-80 pointer-events-none cursor-not-allowed'}`}>
														<input
															type='number'
															className={`outline-none w-[34px] sm:w-[30px] text-center ${isChecked ? 'bg-white' : 'bg-[#e9e9e9]'}`}
															placeholder='00'
															min={0}
															max={23}
															id={`${dia}/inicio`}
															readOnly={
																isScheduleTabLocked
															}
															tabIndex={
																isScheduleTabLocked
																	? -1
																	: 0
															}
															disabled={
																isScheduleTabLocked ||
																!isChecked
															}
															value={
																currentPro
																	.turnos?.[
																	dia
																]?.inicio || ''
															}
															onChange={
																isScheduleTabLocked
																	? undefined
																	: handleTurnos
															}
															onInput={(e) => {
																let value =
																	e.target.value
																		.replace(
																			/\D/g,
																			'',
																		)
																		.slice(
																			0,
																			2,
																		);
																if (
																	parseInt(
																		value,
																	) > 23
																)
																	value =
																		'23';
																e.target.value =
																	value;
															}}
															onBlur={
																isScheduleTabLocked
																	? undefined
																	: () =>
																			handleTurnoBlur(
																				dia,
																				'inicio',
																			)
															}
														/>
														<span className='text-xs sm:text-[13px] items-center content-center'>
															às
														</span>
														<input
															type='number'
															className={`outline-none w-[34px] sm:w-[30px] text-center ${isChecked ? 'bg-white' : 'bg-[#e9e9e9]'}`}
															placeholder='00'
															id={`${dia}/fim`}
															readOnly={
																isScheduleTabLocked
															}
															tabIndex={
																isScheduleTabLocked
																	? -1
																	: 0
															}
															disabled={
																isScheduleTabLocked ||
																!isChecked
															}
															value={
																currentPro
																	.turnos?.[
																	dia
																]?.fim || ''
															}
															onChange={
																isScheduleTabLocked
																	? undefined
																	: handleTurnos
															}
															min={0}
															max={23}
															onInput={(e) => {
																let value =
																	e.target.value
																		.replace(
																			/\D/g,
																			'',
																		)
																		.slice(
																			0,
																			2,
																		);
																if (
																	parseInt(
																		value,
																	) > 23
																)
																	value =
																		'23';
																e.target.value =
																	value;
															}}
															onBlur={
																isScheduleTabLocked
																	? undefined
																	: () =>
																			handleTurnoBlur(
																				dia,
																				'fim',
																			)
															}
														/>
														<BsClock className='w-[16px] h-auto' />
													</div>
												</label>
											);
										})}
									</div> */}

										{/* <div className='w-[98%] h-[2px] bg-[#dfdfdf] mx-auto'></div> */}
										{popupStatus === 'vacancia' ? (
											<div className='w-full rounded-lg border border-[#E3E3E3] bg-white p-3 font-inter'>
												<div className='w-full grid grid-cols-1 gap-3 mb-3'>
													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Data de início da
															vacância
														</p>
														<input
															type='date'
															name='dataInicioVacancia'
															value={
																popupExtraFields.dataInicioVacancia ||
																''
															}
															onChange={
																handlePopupExtraFieldChange
															}
															className='border-[1px] w-full outline-none border-[#E3E3E3] rounded-md text-[14px] font-medium px-2 py-2 text-start text-[#8d8d8d]'
															style={{
																height: 40,
															}}
														/>
													</div>
												</div>

												<p className='text-[16px] font-medium text-start text-[#13335A] mb-2'>
													Observação
												</p>
												<textarea
													name='observacao'
													placeholder='Observação'
													value={
														popupExtraFields.observacao
													}
													onChange={
														handlePopupExtraFieldChange
													}
													className='border-[1px] w-full outline-none border-[#E3E3E3] rounded-md text-[14px] font-medium p-2 text-start text-[#8d8d8d] resize-none min-h-[280px]'
												/>
											</div>
										) : (
											<div
												className={`relative w-full flex flex-col justify-between mx-auto font-inter min-w-0 ${isTrocaMode ? 'opacity-80 pointer-events-none select-none' : ''}`}>
												{isProfessionalFormLocked && (
													<div className='absolute inset-0 z-10 rounded-lg bg-white/35 cursor-not-allowed' />
												)}
												<div className='w-full grid grid-cols-1 gap-4'>
													{/* <div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Nome
														</p>
														<input
															placeholder='Nome completo'
															name='nome'
															value={
																currentSearch.length >
																0
																	? currentSearch[0]
																			.nome
																	: currentPro.nome
															}
															onChange={
																handleProInputs
															}
															disabled={
																vacanciaOn ||
																isProfessionalFieldLocked(
																	'nome',
																)
															}
															className={getProfessionalModalFieldClass(
																'nome',
																currentSearch.length >
																	0
																	? currentSearch[0]
																			.nome
																	: currentPro.nome,
																{
																	locked:
																		vacanciaOn ||
																		isProfessionalFieldLocked(
																			'nome',
																		),
																},
															)}
															style={{
																height: 40,
															}}
														/>
													</div> */}
												</div>

												<div className='w-full grid grid-cols-1 sm:grid-cols-2 gap-4'>
													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Admissão
														</p>
														<input
															type='date'
															name='admissao'
															value={
																currentPro.admissao ||
																''
															}
															onChange={
																handleProInputs
															}
															disabled={
																vacanciaOn ||
																isProfessionalFieldLocked(
																	'admissao',
																)
															}
															className={getProfessionalModalDateFieldClass(
																'admissao',
																currentPro.admissao ||
																	'',
																{
																	locked:
																		vacanciaOn ||
																		isProfessionalFieldLocked(
																			'admissao',
																		),
																},
															)}
															style={{
																height: 40,
															}}
														/>
													</div>
													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Cargo
														</p>
														<select
															name='cargo'
															value={
																currentPro.cargo ||
																DEFAULT_MEDICO_CARGO
															}
															onChange={
																handleProInputs
															}
															disabled={
																isCargoFieldLocked
															}
															className={getProfessionalModalFieldClass(
																'cargo',
																currentPro.cargo ||
																	DEFAULT_MEDICO_CARGO,
																{
																	locked: isCargoFieldLocked,
																},
															)}
															style={{
																height: 40,
															}}>
															{cargoSelectOptions.map(
																(option) => (
																	<option
																		key={
																			option.value
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.value
																		}>
																		{
																			option.label
																		}
																	</option>
																),
															)}
														</select>
													</div>
												</div>

												<div className='w-full grid grid-cols-1 sm:grid-cols-2 gap-4'>
													<div className='w-full flex gap-4 justify-between'>
														<div className='w-full'>
															<p className='text-[16px] from-medium text-start text-[#13335A]'>
																Número do
																conselho
															</p>

															<input
																type='text'
																inputMode='numeric'
																pattern='[0-9]*'
																placeholder='Número do conselho'
																name='numeroConselho'
																value={
																	currentPro.numeroConselho ||
																	''
																}
																onChange={
																	handleProInputs
																}
																disabled={
																	vacanciaOn ||
																	isProfessionalFieldLocked(
																		'numeroConselho',
																	)
																}
																maxLength={20}
																className={getProfessionalModalFieldClass(
																	'numeroConselho',
																	currentPro.numeroConselho ||
																		'',
																	{
																		locked:
																			vacanciaOn ||
																			isProfessionalFieldLocked(
																				'numeroConselho',
																			),
																	},
																)}
																style={{
																	height: 40,
																}}
															/>
														</div>
														<div className='w-[110px] shrink-0'>
															<p className='text-[16px] from-medium text-start text-[#13335A]'>
																UF
															</p>
															<select
																name='ufConselho'
																value={
																	currentPro.ufConselho ||
																	''
																}
																onChange={
																	handleProInputs
																}
																disabled={
																	vacanciaOn ||
																	isProfessionalFieldLocked(
																		'ufConselho',
																	)
																}
																className={getProfessionalModalFieldClass(
																	'ufConselho',
																	currentPro.ufConselho ||
																		'',
																	{
																		locked:
																			vacanciaOn ||
																			isProfessionalFieldLocked(
																				'ufConselho',
																			),
																	},
																)}
																style={{
																	height: 40,
																}}>
																<option
																	value=''
																	{...SELECT_OPTION_PROPS}>
																	UF
																</option>
																{UF_OPTIONS.map(
																	(uf) => (
																		<option
																			key={
																				uf
																			}
																			{...SELECT_OPTION_PROPS}
																			value={
																				uf
																			}>
																			{uf}
																		</option>
																	),
																)}
															</select>
														</div>
													</div>
													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
													Área de atuação
														</p>
														<select
															name='especialidade'
															value={
																currentPro.especialidade ||
																''
															}
															onChange={
																handleProInputs
															}
															disabled={
																vacanciaOn ||
																isProfessionalFieldLocked(
																	'especialidade',
																)
															}
															className={getProfessionalModalFieldClass(
																'especialidade',
																currentPro.especialidade ||
																	'',
																{
																	locked:
																		vacanciaOn ||
																		isProfessionalFieldLocked(
																			'especialidade',
																		),
																},
															)}
															style={{
																height: 40,
															}}>
													<option
														value=''
														{...SELECT_OPTION_PROPS}>
														Área de atuação
													</option>
															{semEspecialidadesModal && (
																<option
																	value=''
																	{...SELECT_OPTION_PROPS}
																	disabled>
																	{getEmptySelectMessage(
																		'especialidade',
																	)}
																</option>
															)}
															{modalEspecialidadeOptions.map(
																(option) => (
																	<option
																		key={
																			option.id
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.id
																		}>
																		{
																			option.nome
																		}
																	</option>
																),
															)}
														</select>
													</div>
												</div>

												<div className='w-full grid grid-cols-1 sm:grid-cols-2 gap-4'>
													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Vínculo
														</p>
														<select
															name='vinculo'
															value={
																currentSearch.length >
																0
																	? currentSearch[0]
																			.vinculo
																	: currentPro.vinculo
															}
															onChange={
																handleProInputs
															}
															disabled={
																vacanciaOn ||
																isProfessionalFieldLocked(
																	'vinculo',
																)
															}
															className={getProfessionalModalFieldClass(
																'vinculo',
																currentSearch.length >
																	0
																	? currentSearch[0]
																			.vinculo
																	: currentPro.vinculo,
																{
																	locked:
																		vacanciaOn ||
																		isProfessionalFieldLocked(
																			'vinculo',
																		),
																},
															)}
															style={{
																height: 40,
															}}>
															<option
																value=''
																{...SELECT_OPTION_PROPS}>
																Vínculo
															</option>
															{vinculoOptions.map(
																(option) => (
																	<option
																		key={
																			option.id
																		}
																		{...SELECT_OPTION_PROPS}
																		value={
																			option.id
																		}>
																		{option.nome ||
																			option.descricao ||
																			option.id}
																	</option>
																),
															)}
														</select>
													</div>

													<div className='w-full'>
														<p className='text-[16px] from-medium text-start text-[#13335A]'>
															Setor
														</p>
														<SearchableSetorSelect
															value={currentPro.setor || ''}
															options={modalSetorOptions}
															onChange={(value) =>
																handleProInputs({ target: { name: 'setor', value } })
															}
															disabled={
																vacanciaOn || isProfessionalFieldLocked('setor')
															}
															emptyMessage={getEmptySelectMessage('setor')}
															className={getProfessionalModalFieldClass(
																'setor',
																currentPro.setor || '',
																{
																	locked:
																		vacanciaOn || isProfessionalFieldLocked('setor'),
																},
															)}
															style={{ height: 40 }}
														/>
													</div>
													<div className='w-full'>
														<p className='text-[16px] flex from-medium text-start text-[#13335A]'>
															Carga horária
															semanal
														</p>
														<div
															className={`relative border rounded-lg px-2.5 py-1.5 flex justify-start border-[#E3E3E3] items-center ${vacanciaOn || isProfessionalFieldLocked('cargaHorariaSemanal') ? 'bg-[#E5E7EB] border-[#D1D5DB] cursor-not-allowed' : 'bg-white'} ${getFieldToneClass(currentPro.cargaHorariaSemanal || '')}`}>
															<input
																type='number'
																name='cargaHorariaSemanal'
																placeholder='00'
																value={
																	currentPro.cargaHorariaSemanal ||
																	''
																}
																readOnly
																onChange={(
																	e,
																) => {
																	let numericValue =
																		e.target.value
																			.replace(
																				/\D/g,
																				'',
																			)
																			.slice(
																				0,
																				3,
																			);
																	if (
																		numericValue !==
																			'' &&
																		parseInt(
																			numericValue,
																			10,
																		) > 100
																	) {
																		numericValue =
																			'100';
																	}
																	handleProInputs(
																		{
																			...e,
																			target: {
																				...e.target,
																				name: 'cargaHorariaSemanal',
																				value: numericValue,
																			},
																		},
																	);
																}}
																min={0}
																max={100}
																inputMode='numeric'
																disabled={
																	vacanciaOn ||
																	isProfessionalFieldLocked(
																		'cargaHorariaSemanal',
																	)
																}
																className={`${vacanciaOn || isProfessionalFieldLocked('cargaHorariaSemanal') ? 'bg-[#E5E7EB] text-[#7C8796]' : 'bg-white'} outline-none rounded-lg text-[14px] font-medium text-start ${getFieldToneClass(currentPro.cargaHorariaSemanal || '')} ${vacanciaOn || isProfessionalFieldLocked('cargaHorariaSemanal') ? 'cursor-not-allowed' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
															/>
															<span className='pointer-events-none absolute left-10 top-1/2 -translate-y-1/2 text-[14px] text-[#8d8d8d]'>
																Horas
															</span>
														</div>
													</div>

													{canAccessProfessionalManagement &&
														canViewProfessionalPhone && (
															<div className='w-full'>
																<p className='text-[16px] from-medium text-start text-[#13335A]'>
																	Telefone
																</p>
																<input
																	placeholder='(00) 0 0000-0000'
																	name='telefone'
																	value={
																		currentSearch.length >
																		0
																			? currentSearch[0]
																					.telefone
																			: currentPro.telefone
																	}
																	onChange={(
																		e,
																	) => {
																		const formatted =
																			formatPhoneNumber(
																				e
																					.target
																					.value,
																			);
																		handleProInputs(
																			{
																				...e,
																				target: {
																					...e.target,
																					value: formatted,
																					name: 'telefone',
																				},
																			},
																		);
																	}}
																	disabled={
																		vacanciaOn ||
																		isProfessionalFieldLocked(
																			'telefone',
																		)
																	}
																	className={getProfessionalModalFieldClass(
																		'telefone',
																		currentSearch.length >
																			0
																			? currentSearch[0]
																					.telefone
																			: currentPro.telefone,
																		{
																			locked:
																				vacanciaOn ||
																				isProfessionalFieldLocked(
																					'telefone',
																				),
																		},
																	)}
																	style={{
																		height: 40,
																	}}
																/>
															</div>
														)}
												</div>
											</div>
										)}
									</div>
									{popupSubmitError && (
										<div className='w-full text-center text-[#dc2626] text-[14px] font-medium mt-2 whitespace-pre-line'>
											{popupSubmitError}
										</div>
									)}
								</fieldset>
							)}
							{/* <div className='w-full flex justify-center mt-2'>
								<button
									onClick={fecharModalEscala}
									className='bg-white text-[#727272] border border-[#cfcfcf] font-inter rounded-md w-full sm:w-auto px-6 sm:px-16 py-1.5 text-[16px] shadow-md hover:bg-[#b8b8b8] hover:text-white transition-all'>
									Fechar
								</button>
								adsadsadasdsdadsadad
								Dadasdas
								DASdasdasD
								Asddsad
							</div> */}
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default FormScale;
